/**
 * The DOC BRIDGE — the script injected into every resting doc-slide iframe. Three jobs:
 *
 * 1. FREEZE (canvas-performance Rule 1): `{__bfz: bool}` pauses/resumes ONLY the animations whose
 *    iteration count is Infinity — ambient drift, pulses — so finite entrances always play once and can
 *    never be stranded half-faded. The parent may stop sending it once the baked tier is live; the
 *    handler stays because a live iframe is still the fallback whenever a bake fails.
 *
 * 2. BAKE (the resting tier): `{__bds_bake: {seq}}` serializes the document's CURRENT settled state to a
 *    static HTML string and posts it up as `{__bds_baked, seq, ok, html, fontHrefs, err}`. The parent
 *    renders that string in a shadow root at rest — no iframe mounted at all — and feeds the same string
 *    to thumbnail rasterization. This replaces the webp-snapshot tier: bitmaps fixed the zoom raster
 *    churn (visible live 1920x1080 browsing contexts re-rastering was the measured 120→60-80fps zoom
 *    drop) but pinned one capture scale; settled HTML is resolution-free AND drops the iframe entirely,
 *    so a zoom gesture re-rasters plain DOM and stays crisp at any zoom.
 *
 *    Why capture INSIDE the iframe: the parent only holds the raw docHtml, which shows entrance
 *    animations at their UN-animated state (opacity 0, fill-mode both) and count-up scripts at zero.
 *    The live DOM after settling is the truth. Mechanics: prove the document ever rendered (two-rAF
 *    gate — Chrome skips rendering for offscreen/display-locked iframes, and in a never-rendered
 *    document CSS animations are never even CREATED, so entrance-animated elements still compute
 *    opacity:0), finish() every finite animation to its fill-both end, clone the DOM, bake each
 *    ANIMATED element's computed values inline on the clone (plus generated rules for animated
 *    ::before/::after), strip script/link/style/bridge nodes, re-collect the document's own <style>
 *    text into one leading <style>, and wrap the body's children in a 1920x1080 root div carrying
 *    body's computed visuals inline — html/body selectors cannot match inside a shadow root, so the
 *    root div IS the body. Fonts are NOT inlined: the reply lists the document's stylesheet hrefs and
 *    the parent registers those faces once on document.fonts (docFonts.ts) — shadow trees share the
 *    host document's FontFaceSet, so one registration serves every baked slide instead of a base64
 *    payload per capture. Any thrown failure posts ok:false and the parent keeps the live iframe —
 *    graceful. No reply at all is also legal: a throttled iframe never fires the rAF gate, no message
 *    is posted, and the parent's timeout keeps the live iframe until a later attempt.
 *
 *    QUIESCENCE GATE + ACK + STAMP (v2 — the wrong-numbers fix): CSS settling alone still baked wrong
 *    numbers, because documents drive count-ups as rAF loops writing TEXT — no Animation object
 *    exists, so Web Animations settling cannot see them. Measured corpus: starts 380–920ms after
 *    load, ~1100ms durations, overlapping the capture point (a bake recorded "40.9M" where 48.4M is
 *    the end value). The flagrant path is render entry: display-lock suspends rAF, so a slide
 *    scrolling into view wakes its suspended count-ups and the whenRendered gate on the SAME frame —
 *    the clone lands 1–2 frames into the count ("7%" baked on a ride to 95). Fix: after whenRendered
 *    the bridge immediately acks `{__bds_baking: 1, seq}` (the queue re-arms its no-answer timeout on
 *    the ack so the gate's wait is not culled as a dead iframe; silence still is), then captures only
 *    once a whole-document MutationObserver goes quiet: no mutation for 500ms of arming = static
 *    document, go now; each mutation re-arms a 300ms quiet timer; hard cap 3000ms from arming so
 *    ambient tickers cannot stall bakes (mid-phase capture at the cap is accepted — the semantics
 *    infinite CSS animations already get). CSS settling runs at gate EXIT so finite animations
 *    re-triggered during the wait still land on their end state. Every gate-era bake stamps its root
 *    div with data-bds-v="2"; the parent treats persisted bakes without the stamp as invalid, which
 *    forces the one-time re-bake that heals decks saved with pre-gate mid-count numbers.
 *
 * 3. PICK MODE + LIVE STYLE PREVIEW (the in-app inspector): `{__bds_pick:{on}}` turns the document into a
 *    target — hovering an element that directly holds text outlines it, clicking it selects it and posts
 *    `{__bds_picked, path, tag, text, fontSize, rect, styles}` — and `{__bds_style:{path,prop,value}}` writes that
 *    one property INLINE on the element at `path` so the user sees the change at full fidelity before
 *    committing (`value:null` removes the declaration, restoring the stylesheet's own value). Nothing
 *    here is persistence: the parent replays the same `path` against the SOURCE HTML to make an edit
 *    real, so the iframe stays a preview surface and the document string stays the only truth. Every
 *    apply answers `{__bds_overflow, bottom}` because the slide is a hard 1080px box with no scrollbar —
 *    text pushed past the fold is not scrolled past, it is GONE — and the user has to be told the moment
 *    a bigger font starts costing them a paragraph. All of it is inert until the first `on:true`: no
 *    listener attached, no style node created, nothing an ordinary resting card or bake ever pays for.
 *
 *    Two more inbound messages address the SAME element by the SAME path, for the things a cursor cannot
 *    express. `{__bds_select:{path}}` selects without a click, which is how the breadcrumb walks up from a
 *    headline to the card containing it — so unlike a click it must NOT require the element to own text,
 *    or containers, the only reason to walk up at all, would be unselectable. `{__bds_remove:{path}}` takes
 *    a node out of the live document and answers `{__bds_removed, ok}` plus a fresh overflow reading, since
 *    a document reflows the moment a node leaves it.
 *
 * 4. INLINE TEXT EDIT (the same path contract, one step further): `{__bds_edit:{path,on:true}}` makes THAT
 *    ONE element contentEditable so the user types into the real document — real fonts, real line-breaking,
 *    real reflow, no textarea pretending to be a slide — and `on:false` (or a blur) commits
 *    `{__bds_text, path, html, text}`. `html` is the element's innerHTML AFTER the edit rather than its
 *    text, because the author's own inline markup (a `<br>`, a styled `<span>`) has to survive an edit the
 *    parent applies by replacing that element's CONTENTS in the source string. Escape commits too, as a
 *    click away does (Beka, 2026-09-27: it used to cancel and throw the typing away); a caret closed with
 *    nothing changed posts the same unchanged words, which the parent writes as nothing.
 *    In text mode the caret itself is the parent's to draw: the engine's is hidden, and
 *    `{__bds_caret, rect, color}` says where it stands (`DocCaretReply`).
 *
 * 5. **THREE ADDITIONS FOR /deck's PREVIEW** (2026-09-05), all additive and all cold until asked:
 *
 *    - `{__bds_pick:{on, mode}}` gains a MODE. `select` is everything above, unchanged. `text` is the
 *      preview's no-mode text editing: the same hover outline, but over anything that OWNS TEXT a first
 *      click selects the element and a second opens the caret in it, and the cursor there is an I-beam.
 *      A click on something that owns no text — a chart, an image, empty ground — falls through to the
 *      document. Note what that net catches: a `<button>` with a label and an `<a>` with words own text,
 *      so while this mode is armed neither runs. That is deliberate for the link and a stated cost for
 *      the button — see the note in `onClick`. The two marks the inspector paints (its selection
 *      outline, and the engine's focus ring on the open editor) are both suppressed in this mode: the
 *      parent draws the ring out there, and two would be two.
 *    - `{__bds_hit:{x,y,seq}}` → `{__bds_hitres, seq, path, rect, chain}` answers "what is under this
 *      point" without arming anything. Annotate mode draws its layer OUTSIDE this iframe, so its pointer
 *      never crosses the boundary and it cannot hit-test the DOM itself; it sends the point instead. The
 *      reply is a three-deep descriptor chain rather than a name, because naming is heuristics and those
 *      belong in TypeScript, not in this string.
 *    - `{__bds_fonts:1}` → `{__bds_fontres, families:[{name, weights}]}` reports what this document can
 *      actually SET, read off `document.fonts`. It is what stops the toolbar offering six weights on a
 *      family that ships two. Answered twice — once now, once when `document.fonts.ready` settles —
 *      because a Google Fonts stylesheet is a request behind the document that names it.
 *    - `{__bds_fontlink:{href}}` adds a stylesheet to this head, once per href. The other side of the
 *      same problem: the toolbar offers the whole catalogue, and a family the document never loaded
 *      cannot be set from out there at all. Deliberately NOT bridge-marked — the link becomes the
 *      document's own and rides into the source on the same write.
 *
 *    `⌘B`/`⌘I` also stop being refused below — see the note there; `⌘U` still is.
 *
 * 6. **LAYOUT EDITING, the live mirror of hand edits** (2026-09-24, spike). `__bds_style` takes
 *    `priority:'important'` (and then leaves runs alone) and `quiet:1` (drag frames skip the overflow
 *    reading). `__bds_mark` stamps `data-eid`/`data-hand` and answers a computed confirm read;
 *    `__bds_move` reorders among siblings exactly like `moveHand`; `__bds_settle` holds the page at its
 *    end state; `__bds_picked` carries `box`; and while a box is selected with no caret open, arrows, Esc,
 *    Delete/Backspace and ⌘Z are forwarded as `__bds_key`, and in text mode the insert dock's plain T, R and
 *    C with or without a selection (2026-09-27). All cold until armed.
 *
 *    Everything a contenteditable does on its own is cancelled and re-done by hand — Enter inserts one
 *    `<br>` (the engines' own defaults wrap lines in `<div>` or `<p>` and hand back a restructured
 *    element), paste takes the text/plain flavour only (the html flavour is a stranger's DOM tree landing
 *    in a document we store and re-render), and ⌘/Ctrl+B/I/U are refused outright (they inject `<b>`/`<i>`/
 *    `<u>` that belong to no part of the artifact's style system). This is a DOCUMENT being edited in
 *    place, not a rich text editor. While an element is open the inspector is suppressed inside it, or the
 *    very click that places the caret would re-pick it and fight the edit. One consequence is shared with
 *    removal: an edit rewrites the element's children, so any path pointing INSIDE the edited element is
 *    stale the moment it commits — paths to everything else are untouched.
 *
 * 7. **TEXT MODE SELECTS FROM THE OUTSIDE IN, like Figma** (2026-09-25). A click takes the outermost box
 *    under the pointer, the next click inside takes the next box in, a sibling click takes the sibling;
 *    ⌘/Ctrl-click takes the innermost, Enter goes in, Shift+Enter and `{__bds_select:{up:1}}` go out, and
 *    out from the top clears the selection. The cursor is the arrow everywhere but the open caret, and the
 *    hover is posted as `__bds_hover` for the parent to draw. `__bds_slide` answers the slide's frame (or
 *    body) for its Background, and `box.text` says whether the Text controls apply. See drillTarget.
 *    Nothing inside the selection is hovered, `parent` on `__bds_picked` / `__bds_overflow` is the level
 *    around the selection (drawn dashed), and `{__bds_click:{x,y,deep}}` is a click the host passes in:
 *    its handles cover the selected box so all of it drags, and a press there that never moved is a click.
 *    Text mode has no double-click: the caret is a click on the selected element's own words, and while
 *    one is open a click on other words goes straight into them. An element that is not selected can be
 *    pressed and dragged in one go: `{__bds_dragok:{on}}` arms it, and the drag is streamed out as
 *    `{__bds_drag, phase, path?, x, y, alt}` for the host's handles to run.
 *
 * 8. **PICTURES ADDED BY HAND** (Beka, 2026-09-25): dropped, pasted or sent from the Directory, placed on top
 *    and free to move like a Figma layer. `{__bds_insert:{seq, items:[{id, src, alt, box}]}}` builds each
 *    `<img data-deck-image data-hand-add>` in the insert parent, absolutely placed at its box and one z-index
 *    above the highest in there, and answers `{__bds_inserted, seq, ok, parentPath, paths, decls}`: the
 *    declarations it set, for the host to write into the source. The insert parent mirrors the source's: body,
 *    then down while exactly one content child is left (pictures already added passed over) and it holds
 *    elements, takes hand edits, and is not an svg or a deck-* host; on body the pictures go in front of the
 *    bridge's own scripts. It is not the Background's slideTarget, whose fill test would take a full-bleed svg
 *    backdrop or photo wrapper. The pictures' own finite animations are finished before their box is measured,
 *    settle mode or not. It neither marks nor picks; the host does both once the source holds the pictures, and
 *    `box.added` on `__bds_picked` says a picked element is one. Such a picture stays a level at any size,
 *    full-bleed included. `__bds_remove` echoes `seq`, takes only an element carrying `data-hand-add` when it
 *    says `added:1`, and posts the empty `__bds_picked` when the selection went with it. While text mode is armed and no caret is open, a paste carrying image files is posted up as
 *    `{__bds_paste, files}`, and a drag carrying files as `{__bds_filedrag}` so the host can raise its drop
 *    overlay; the frame itself never takes the drop. `{__bds_imgsrc:{path, id, src, alt, seq}}` changes a deck
 *    picture's picture (the Properties panel's Image control) and answers `{__bds_imgset, seq, ok}`.
 *
 * 9. **REORDER, MOVE BETWEEN CONTAINERS, DELETE** (Beka, 2026-09-25): an element that sits in a container is
 *    dragged to a SLOT, never to a free offset, and anything selected can be deleted; the slide's own children
 *    are the exception, placed freely (`box.top`, `__bds_freeze`). The bridge measures; the host composes the
 *    source (moveHandTo / moveHand / removeHand) and mirrors it here.
 *    - THE SLIDE IS WHAT THE BRIDGE MEASURES (Beka, 2026-09-25): slideTarget, the frame `__bds_slide` answers
 *      (body when nothing fills the slide). The host passes its path to moveHandTo / removeHand, which then take
 *      the same rule; the source's own single-child chain took a lone 622px column beside a picture the user
 *      added for the slide itself, and it could not be dragged, deleted or detached.
 *    - `box.frame` on `__bds_picked`: the element is that slide or holds it (frameOf). A press there is a click
 *      only, and a drop, a move or a hand delete refuses it. `box.top`: its parent is the slide, the level that
 *      works like a Figma frame without auto layout: its children are placed freely, containers inside reorder.
 *    - The streamed drag's `start` carries `flow` (position is neither absolute nor fixed) and `top` (box.top's
 *      rule), so the host picks reorder, freeze or free mode at the press without waiting for a pick it has not
 *      rendered yet.
 *    - `{__bds_freeze:{seq, path, dx, dy}}` → `{__bds_frozen, seq, ok, items}`: the first free drag of a flow child
 *      of the slide pins every flow child of it absolutely where it stands, that one dx,dy on, and answers the
 *      declarations set, container first (see doFreeze). Nothing marked, nothing picked; an overflow reading follows.
 *    - `{__bds_lift:{path, on}}` dims the dragged element in place (`data-bds-lifted`, never in a bake); off
 *      takes the dim off everything. No reply.
 *    - `{__bds_dropat:{seq, path, x, y}}` → `{__bds_dropslot, seq, ok, parentPath, beforePath, line, target,
 *      same, noop}`: the container and slot a drop at that slide point would take (see dropSlot), in the
 *      current document's paths. It keeps to what `moveHandTo` takes with the slide's path: never out of the
 *      frame into body, a table part only among its siblings, and no move that would change which element is
 *      the slide (P4, measured).
 *    - `{__bds_spacing:{seq, parentPath, path}}` → `{__bds_spacingis, seq, fix, gap, axis, convert, paths}`: what
 *      making that stack space its gaps like auto layout would take, before a move in or into it (doSpacing).
 *    - `{__bds_moveto:{seq, path, parentPath, beforePath, eid, hand, clear}}` moves the live element there,
 *      takes the lift off, removes the `clear` props from its inline style, marks it like `__bds_mark`, and
 *      answers `{__bds_movedto, seq, ok, path, rect, css}`; then it is picked, and an overflow reading follows.
 *    - `__bds_move` also takes `{path, step: ±1, flow: 1, seq}`: one flow sibling back or on, in what the eye
 *      sees (a reversed flex counts backwards), answered with the `toIndex` it used, for `moveHand`; refused
 *      in an append-only container, where the DOM order is not the order on screen.
 *    - `__bds_remove` with `hand:1` takes any element the source's removeHand would, and refuses the slide and
 *      what holds it (frameOf), what is not a level, and anything a hand edit cannot reach.
 *
 * 10. **THE STAGE'S EDIT KEYS, CLIPBOARD, MENU AND LAYER ORDER** (Beka, 2026-09-27: undo/redo keys, copy and
 *    paste, text from outside, a right-click menu, forward and backward). Text mode only, never in a caret.
 *    - Keys, selection or not: ⌘Z / ⇧⌘Z / ⌘Y, ⌘D and ⌘] / ⌘[ (with ⌥ or ⇧) go out as `__bds_key` (`key` z, y, d, ]
 *      or [). ⌘C, ⌘X and ⌘V are left to the browser, whose copy, cut and paste events carry the clipboard.
 *    - Copy and cut with an element selected (not the slide): the clipboard gets its words and a span with a
 *      token; `{__bds_copy, token, path, cut}` tells the host. Paste: `{__bds_paste, files, text, clip, at}`.
 *    - A right-click selects as a click would (keeping the selection when inside it) and posts
 *      `{__bds_menu, x, y, on}`; in an open caret the browser's own menu stays.
 *    - `{__bds_point:{seq}}` → `{__bds_pointis, seq, at}`: where the pointer last was.
 *    - `{__bds_graft:{seq, html, parentPath, beforePath}}` → `{__bds_grafted, seq, ok, path, rect, z}`: the copy the
 *      host composed goes in at that place, scrubbed, unmarked and unpicked (see doGraft).
 *    - `{__bds_stack:{seq, path}}` → `{__bds_stackis, seq, ok, free, front, forward, backward, back, isolate}`: the
 *      z-index each layer command would set on a free element (see doStack).
 *    - An insert always sets a z-index now (one above the highest, 1 when nothing stacks), and a text item with
 *      `paste:1` carries words up to PASTED_TEXT_MAX, a line each.
 */

import { DECK_RUNTIME_SNIPPET } from './deckRuntime'
import { activeBrandFaceCssForDoc } from '../utils/brandFonts'
import { primeBrandFonts } from './brandKitDefault'
import { BOX_CSS_PROPS, type BoxCssProp } from './boxCss'
import { ADDED_ROUND, ADDED_SHAPE_FILL, ADDED_TEXT_MAX, ADDED_TEXT_STYLE, PASTED_TEXT_MAX } from '../birtvyOpen/addedLook'

/** Marker attribute for bridge-owned nodes so the bake serializer skips them. */
const BRIDGE_MARK = 'data-bds-x'

/**
 * PINNED CONTRACT: the exact stamp every current-era bake carries on its root div, interpolated into the
 * snippet below so emission and the parent-side check can never drift. A persisted bake WITHOUT this
 * stamp predates a correctness fix and must be treated as invalid so it re-bakes once (that is what
 * heals already-saved poisoned bakes). History: v2 = the quiescence gate (mid-count numbers); v3 = the
 * `:root` → `[data-bds-root]` rewrite (Contract v2 tokens live on `:root`, which matches NOTHING inside
 * a shadow root — same family as the dead html/body selectors — so every var() fell back and baked
 * artifacts rendered in the wrong fonts and colors at rest); v5 = body's LAYOUT box (padding, display, flex/grid alignment) rides inline, so a document framed on body no longer bakes flat against the corner; v4 = canvas pixels captured into the bake
 * (a cloned <canvas> serializes EMPTY — its bitmap is not DOM — so a script-drawn scene rested as a
 * blank region while the text around it survived; measured live on the 100-card wormhole slide, whose
 * whole visual is one rAF-painted canvas).
 */
export const DOC_BAKE_STAMP = 'data-bds-v="5"'

const DOC_BRIDGE_SNIPPET =
  `<script ${BRIDGE_MARK}>(function(){
'use strict';
var frozen=false;
/* STILL = the layout editor's settle mode (__bds_settle). Loops hold while it is on, like a freeze. */
var still=false;
function applyFreeze(){
  try{
    var as=document.getAnimations();
    for(var i=0;i<as.length;i++){
      var a=as[i];
      var t=a.effect&&a.effect.getTiming?a.effect.getTiming():null;
      if(t&&t.iterations===Infinity){if(frozen||still)a.pause();else a.play()}
    }
  }catch(e){}
}
var n=0;var iv=setInterval(function(){if(frozen)applyFreeze();if(++n>20)clearInterval(iv)},500);

/* ---- bake ---- */
/* PART 1 of the blank-bake fix: never capture from an unrendered document. Chrome skips rendering
   for offscreen / display-locked iframes; in a never-rendered document CSS animations are never even
   CREATED, so an entrance-animated element still computes opacity:0 and the bake would hold background
   only. Two rAF ticks only ever fire in a document that is actually rendering — they are the
   proof-of-render gate. In a throttled iframe the callback simply never runs, no reply is posted, and
   the parent keeps the live iframe (correct pixels, bake deferred). */
function whenRendered(cb){
  requestAnimationFrame(function(){requestAnimationFrame(cb)});
}
/* Then force every FINITE animation to its end state before reading styles: finish() jumps an idle or
   mid-flight entrance to its fill-both end, exactly what the resting slide shows. Infinite ambience is
   left alone — it is frozen mid-phase by the freeze bridge and that phase is what the bake should hold. */
function settleAnimations(){
  try{
    var as=document.getAnimations();
    for(var i=0;i<as.length;i++){
      var a=as[i];
      var t=a.effect&&a.effect.getTiming?a.effect.getTiming():null;
      if(t&&t.iterations!==Infinity){try{a.finish()}catch(e){}}
    }
  }catch(e){}
}
/* PART 2 of the wrong-numbers fix: settling covers CSS, but documents drive count-ups as rAF loops
   writing TEXT — no Animation object exists, so settleAnimations cannot see them. Measured corpus:
   starts 380-920ms after load, ~1100ms durations, overlapping the capture point (a bake recorded
   "40.9M" where 48.4M is the end value). Worst at render entry: display-lock suspends rAF, so the
   suspended count-ups and the whenRendered gate wake on the SAME frame and the clone lands 1-2
   frames into the count. So bake only when the DOM goes quiet: no mutation within 500ms of arming =
   static document, proceed now; each mutation re-arms a 300ms quiet timer; hard cap 3000ms from
   arming so ambient tickers cannot stall bakes forever (mid-phase capture at the cap is accepted —
   the semantics infinite CSS animations already get). Single-fire: the observer is disconnected and
   every timer cleared on proceed, and a failed observer construction degrades to proceeding
   immediately, which is exactly the pre-gate behavior. */
function whenQuiet(cb){
  var done=false;var quiet=null;var cap=null;var obs=null;
  function go(){
    if(done)return;
    done=true;
    try{if(obs)obs.disconnect()}catch(e){}
    if(quiet!==null)clearTimeout(quiet);
    if(cap!==null)clearTimeout(cap);
    cb();
  }
  function arm(ms){
    if(done)return;
    if(quiet!==null)clearTimeout(quiet);
    quiet=setTimeout(go,ms);
  }
  try{
    obs=new MutationObserver(function(){arm(300)});
    obs.observe(document.documentElement,{subtree:true,childList:true,characterData:true,attributes:true});
  }catch(e){go();return}
  cap=setTimeout(go,3000);
  arm(500);
}
var ANIM_PROPS=['opacity','transform','visibility','width','height','left','top','background-position','background-color','color','filter','clip-path','border-radius','box-shadow'];
/* Inline each ANIMATED element's settled computed values on its clone, with !important so they beat the
   element's own rules once the global animation kill-switch applies. Pairing is positional
   (querySelectorAll('*') on both trees), which is why this MUST run before any node is stripped from
   the clone — removals desynchronize the two lists. Animated pseudo-elements cannot take inline styles,
   so they get generated classes + rules instead. */
function bakeState(liveRoot,cloneRoot){
  var live=liveRoot.querySelectorAll('*');
  var clone=cloneRoot.querySelectorAll('*');
  var pseudoRules=[];var pn=0;
  for(var i=0;i<live.length&&i<clone.length;i++){
    var el=live[i],cl=clone[i];
    var cs=getComputedStyle(el);
    if(cs.animationName&&cs.animationName!=='none'){
      for(var p=0;p<ANIM_PROPS.length;p++){
        try{cl.style.setProperty(ANIM_PROPS[p],cs.getPropertyValue(ANIM_PROPS[p]),'important')}catch(e){}
      }
    }
    var pseudos=['::before','::after'];
    for(var q=0;q<pseudos.length;q++){
      var ps=getComputedStyle(el,pseudos[q]);
      if(ps.content!=='none'&&ps.animationName&&ps.animationName!=='none'){
        var cls='bds-p'+(pn++);
        cl.setAttribute('class',(cl.getAttribute('class')||'')+' '+cls);
        var decl='';
        for(var r=0;r<ANIM_PROPS.length;r++)decl+=ANIM_PROPS[r]+':'+ps.getPropertyValue(ANIM_PROPS[r])+' !important;';
        pseudoRules.push('.'+cls+pseudos[q]+'{'+decl+'}');
      }
    }
  }
  return pseudoRules.join('\\n');
}
function collectStyles(){
  var out=[];
  var styles=document.querySelectorAll('style:not([${BRIDGE_MARK}])');
  for(var i=0;i<styles.length;i++)out.push(styles[i].textContent||'');
  return out.join('\\n');
}
/* Attribute-value escape for the hand-assembled root div. The body-children markup comes out of
   XMLSerializer already escaped; only the two attributes we build by string concat need this. */
function escAttr(s){
  return String(s).replace(/&/g,'&amp;').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
}
/* The root div stands in for <body>: html/body selectors are dead inside a shadow root, so the visuals
   those rules produced must ride inline. Background composites the way the browser canvas does — body's
   unless fully transparent, else html's, else white. */
function rootStyle(){
  var bs=getComputedStyle(document.body||document.documentElement);
  var hs=getComputedStyle(document.documentElement);
  var bg=bs.backgroundColor;
  if(bg==='rgba(0, 0, 0, 0)'||bg==='transparent')bg=hs.backgroundColor;
  if(bg==='rgba(0, 0, 0, 0)'||bg==='transparent')bg='#fff';
  var bgi=bs.backgroundImage;
  if(!bgi||bgi==='none')bgi=hs.backgroundImage;
  /* isolation: the root stands in for the page, whose background is painted UNDER everything, negative z
     included. Without its own stacking context a z-index below 0 (an element sent behind the text) would sink
     under this div's background and vanish from every bake while the live frame shows it. */
  var s='position:relative;isolation:isolate;width:1920px;height:1080px;overflow:hidden;';
  s+='background-color:'+bg+';';
  if(bgi&&bgi!=='none')s+='background-image:'+bgi+';';
  s+='color:'+bs.color+';';
  s+='font-family:'+bs.fontFamily+';';
  s+='font-size:'+bs.fontSize+';';
  s+='line-height:'+bs.lineHeight+';';
  s+='letter-spacing:'+bs.letterSpacing+';';
  s+='font-weight:'+bs.fontWeight+';';
  s+='text-align:'+bs.textAlign+';';
  /* THE BODY BOX — everything above is paint or inheritance, these are LAYOUT. Dropping them is why a
     document that puts its frame on body (padding, or body AS the flex/grid container) baked with its
     content flat against the top-left corner while the live iframe looked right: the selector that
     would carry them, the body selector, matches nothing inside a shadow root — the same trap as :root, which is
     already rewritten below. Measured on slide 4's own document with its padding moved to body: live
     content started at 320px from the left, the bake at 0px. */
  s+='box-sizing:'+bs.boxSizing+';';
  s+='padding:'+bs.paddingTop+' '+bs.paddingRight+' '+bs.paddingBottom+' '+bs.paddingLeft+';';
  var disp=bs.display;
  s+='display:'+disp+';';
  if(disp.indexOf('flex')>=0||disp.indexOf('grid')>=0){
    s+='flex-direction:'+bs.flexDirection+';';
    s+='flex-wrap:'+bs.flexWrap+';';
    s+='align-items:'+bs.alignItems+';';
    s+='justify-content:'+bs.justifyContent+';';
    s+='row-gap:'+bs.rowGap+';column-gap:'+bs.columnGap+';';
    s+='grid-template-columns:'+bs.gridTemplateColumns+';';
    s+='grid-template-rows:'+bs.gridTemplateRows+';';
  }
  return s;
}
function doBake(seq){
  var finish=function(ok,html,fontHrefs,err){
    try{window.parent.postMessage({__bds_baked:1,seq:seq,ok:ok,html:html||null,fontHrefs:fontHrefs||[],err:err||null},'*')}catch(e){}
  };
  whenRendered(function(){
    /* Ack BEFORE the gate arms (pinned contract): the parent's no-answer timeout exists to cull
       render-throttled iframes that will never reply, and the gate may legally hold this bake for
       3000ms — the ack says rendering-and-gating, answer coming, so the queue extends its timeout
       instead of killing a legitimate slow settle. Silence still means dead. */
    try{window.parent.postMessage({__bds_baking:1,seq:seq},'*')}catch(e){}
    whenQuiet(function(){
      var fontHrefs=[];
      try{
        /* Settle at gate EXIT, not entry: finite CSS animations re-triggered while the gate waited
           still get jumped to their fill-both end state. */
        settleAnimations();
        var links=document.querySelectorAll('link[rel="stylesheet"]');
        var seen={};
        for(var l=0;l<links.length;l++){
          var h=links[l].href;
          if(h&&!seen[h]){seen[h]=1;fontHrefs.push(h)}
        }
        /* A LIFTED element (a reorder drag in flight, __bds_lift) is dimmed by a bridge rule, and bakeState
           would read that opacity off an animated one and inline it for good. The dim comes off for the two
           reads and goes back on in the same task, so no frame ever paints without it. */
        var lifted=document.querySelectorAll('[data-bds-lifted]');
        for(var lf=0;lf<lifted.length;lf++)lifted[lf].removeAttribute('data-bds-lifted');
        var clone,pseudoCss;
        try{
          clone=document.documentElement.cloneNode(true);
          pseudoCss=bakeState(document.documentElement,clone);
        }finally{
          for(var lg=0;lg<lifted.length;lg++)lifted[lg].setAttribute('data-bds-lifted','');
        }
        /* PICTURE BOXES. Every placed picture (img[data-deck-image]) gets its measured box stamped on
           the clone as data-bds-box="x y w h", frame pixels, so the server's look_at_artifact can say
           "inside the frame" or "clipped by N px" with a number instead of "not measured yet". Pairing
           is positional, like the canvases below; a picture the live document cannot measure is
           simply left unstamped. */
        var liveImgs=document.querySelectorAll('img[data-deck-image]');
        var cloneImgs=clone.querySelectorAll('img[data-deck-image]');
        for(var ii=0;ii<liveImgs.length&&ii<cloneImgs.length;ii++){
          try{
            var rb=liveImgs[ii].getBoundingClientRect();
            cloneImgs[ii].setAttribute('data-bds-box',Math.round(rb.left)+' '+Math.round(rb.top)+' '+Math.round(rb.width)+' '+Math.round(rb.height));
          }catch(e){}
        }
        /* TEXT THAT LOSES WORDS. The slide is a hard 1920x1080 box that hides its overflow, so text laid out
           past it is cropped, and text taller or wider than a box that hides ITS overflow is cut inside the
           frame. Each such block is stamped on the clone with data-bds-overflow="x y w h" (its box, frame
           pixels) and, when its own box cuts it, data-bds-cut="px" (how much the box hides), so the
           server's look_at_artifact can name the text and the pixels instead of calling the artifact fine.
           Only elements that hold text themselves are read, so a wrapper is never blamed for its child.
           Pairing is positional like the pictures above; capped so a runaway document cannot bloat the bake. */
        var liveText=document.body?document.body.querySelectorAll('*'):[];
        var cloneBody=clone.querySelector('body');
        var cloneText=cloneBody?cloneBody.querySelectorAll('*'):[];
        var textStamped=0;
        for(var ti=0;ti<liveText.length&&ti<cloneText.length&&textStamped<12;ti++){
          try{
            var te=liveText[ti];
            var ownText=false;
            for(var tn=te.firstChild;tn;tn=tn.nextSibling){
              if(tn.nodeType===3&&tn.nodeValue.replace(/\\s+/g,'')){ownText=true;break}
            }
            if(!ownText)continue;
            var tr=te.getBoundingClientRect();
            if(!tr.width||!tr.height)continue;
            var tcs=getComputedStyle(te);
            if(tcs.visibility==='hidden')continue;
            var pastFrame=tr.left<-1||tr.top<-1||tr.right>1921||tr.bottom>1081;
            var hidesOwn=tcs.overflowX!=='visible'||tcs.overflowY!=='visible';
            var cutPx=hidesOwn?Math.max(te.scrollHeight-te.clientHeight,te.scrollWidth-te.clientWidth):0;
            if(!pastFrame&&cutPx<=2)continue;
            cloneText[ti].setAttribute('data-bds-overflow',Math.round(tr.left)+' '+Math.round(tr.top)+' '+Math.round(tr.width)+' '+Math.round(tr.height));
            if(cutPx>2)cloneText[ti].setAttribute('data-bds-cut',String(Math.round(cutPx)));
            textStamped++;
          }catch(e){}
        }
        /* CANVAS PIXELS ARE NOT DOM (the Graphy lesson, arriving for model-authored documents): a
           cloned <canvas> serializes EMPTY, so a script-drawn scene baked as a blank region while the
           text around it survived. Swap each clone canvas for an <img> of the live canvas's CURRENT
           pixels — the frame the quiescence gate happened to land on, the same mid-phase semantics
           infinite CSS animations already get. Pairing is positional and runs BEFORE the junk strip
           for the same reason bakeState does. The copied attributes then pass through the inline-
           handler / javascript:-URL security sweep below like any other element's. A tainted canvas
           (cross-origin
           image drawn in) throws on toDataURL — that one keeps today's behavior (blank), per-canvas,
           without failing the bake. Zero-sized canvases are skipped outright. */
        var liveCv=document.querySelectorAll('canvas');
        var cloneCv=clone.querySelectorAll('canvas');
        for(var cvi=0;cvi<liveCv.length&&cvi<cloneCv.length;cvi++){
          try{
            var lcv=liveCv[cvi];
            if(!lcv.width||!lcv.height)continue;
            /* Capture at the LAYOUT size, not the bitmap size: model documents draw at devicePixelRatio
               (the wormhole's canvas is 3840x2160 for a 1920x1080 box), and a device-resolution PNG baked
               2.4MB — measured — for pixels the resting tier can never show. Downscaling to the CSS box
               quarters the pixels; WebP (every runtime we ship into is Chromium) cuts the rest, with the
               PNG fallback detectable because toDataURL answers a PNG when it does not speak the asked
               type. Drawing through the temp canvas also surfaces taint as a throw, same handling. */
            var cvW=lcv.clientWidth||lcv.width;
            var cvH=lcv.clientHeight||lcv.height;
            var cvTmp=document.createElement('canvas');
            cvTmp.width=cvW;cvTmp.height=cvH;
            cvTmp.getContext('2d').drawImage(lcv,0,0,cvW,cvH);
            var cvUrl=cvTmp.toDataURL('image/webp',0.92);
            if(cvUrl.indexOf('data:image/webp')!==0)cvUrl=cvTmp.toDataURL('image/png');
            var cvImg=document.createElement('img');
            var cvAt=cloneCv[cvi].attributes;
            for(var cva=0;cva<cvAt.length;cva++){
              var cvNm=cvAt[cva].name;
              if(cvNm!=='width'&&cvNm!=='height')cvImg.setAttribute(cvNm,cvAt[cva].value);
            }
            cvImg.setAttribute('src',cvUrl);
            /* The canvas's LAYOUT box, not its bitmap size: width/height ATTRS are device pixels and
               often exceed the CSS box, and an <img> with no other sizing falls back to them. */
            var cvSt=cvImg.getAttribute('style')||'';
            cvImg.setAttribute('style',cvSt+(cvSt&&!/[;\\s]$/.test(cvSt)?';':'')+'width:'+lcv.clientWidth+'px;height:'+lcv.clientHeight+'px');
            cloneCv[cvi].parentNode&&cloneCv[cvi].parentNode.replaceChild(cvImg,cloneCv[cvi]);
          }catch(e){}
        }
        /* strip AFTER baking (pairing above is positional): scripts, links, styles and bridge nodes —
           none of them belong in the static string. Styles are re-collected from the live document into
           the single leading <style> below. */
        var junk=clone.querySelectorAll('script,link,style,[${BRIDGE_MARK}]');
        for(var j=junk.length-1;j>=0;j--)junk[j].parentNode&&junk[j].parentNode.removeChild(junk[j]);
        var seeded=clone.querySelectorAll('[data-bds-seed]');
        for(var d=0;d<seeded.length;d++)seeded[d].removeAttribute('data-bds-seed');
        /* Same idea for inspector chrome: the highlight <style> is bridge-marked so the strip above
           already took it, but the hover/selected attributes ride on the DOCUMENT'S OWN elements. A bake
           taken while pick mode was open would carry them into persisted HTML forever — rules gone,
           attributes immortal — so clear them here, and a reorder drag's dim (data-bds-lifted) with them.
           data-bds-pick sits on <html>, which the clone root is, and querySelectorAll never matches its own
           root. */
        var chr=clone.querySelectorAll('[data-bds-hi],[data-bds-sel],[data-bds-lifted]');
        for(var c=0;c<chr.length;c++){chr[c].removeAttribute('data-bds-hi');chr[c].removeAttribute('data-bds-sel');chr[c].removeAttribute('data-bds-lifted')}
        try{clone.removeAttribute('data-bds-pick')}catch(e){}
        /* SETTLE MODE'S ROOT MARK, same reasoning. data-eid / data-hand are NOT chrome: they are the
           user's hand edits, written into the source, and must ride every bake. */
        try{clone.removeAttribute('data-bds-still')}catch(e){}
        /* contenteditable is bridge state too, and the only piece of it that would still DO something at
           rest: the baked string renders in the PARENT document, where an editable attribute makes a
           resting card that has no editor attached to it take a caret and swallow keystrokes. A bake can
           legally be taken mid-edit — the queue does not ask the inspector for permission — so strip it
           from the clone. The LIVE element keeps its own until the edit ends; only the snapshot is
           cleaned. */
        var ce=clone.querySelectorAll('[contenteditable]');
        for(var ci=0;ci<ce.length;ci++)ce[ci].removeAttribute('contenteditable');
        /* SECURITY: the baked string renders in the PARENT document — a shadow root scopes styles, NOT
           scripts. Inline handlers (onclick=…) and javascript: URLs that were inert behind the opaque
           sandbox would execute in app context, so strip them at the source: the static tier must stay
           exactly as inert as the iframe it replaces. */
        var els=clone.querySelectorAll('*');
        for(var x=0;x<els.length;x++){
          var attrs=els[x].attributes;
          for(var y=attrs.length-1;y>=0;y--){
            var an=attrs[y].name;
            if(an.slice(0,2).toLowerCase()==='on'){els[x].removeAttribute(an);continue}
            if((an==='href'||an==='src'||an==='xlink:href')&&/^\\s*javascript:/i.test(attrs[y].value))els[x].removeAttribute(an);
          }
        }
        /* Serialize each body child with XMLSerializer (not innerHTML) so the output is well-formed
           enough to survive a foreignObject re-parse on the thumbnail path; childNodes, not children,
           because model-authored docs sometimes leave bare text directly under <body>. */
        var body=clone.querySelector('body');
        var parts=[];
        var ser=new XMLSerializer();
        if(body){
          var kids=body.childNodes;
          for(var k=0;k<kids.length;k++){
            try{parts.push(ser.serializeToString(kids[k]))}catch(e){}
          }
        }
        var styleText=collectStyles()+'\\n'+pseudoCss+'\\n*{animation:none !important;transition:none !important}';
        /* Contract v2 tokens live on :root — a selector that matches NOTHING inside a shadow root (the
           same trap as html/body). Rewrite it onto the root div, which already exists and wraps every
           baked node, so custom properties (and any other :root rule) keep cascading at rest exactly
           as they do live. */
        styleText=styleText.replace(/:root\\b/g,'[data-bds-root]');
        /* ${DOC_BAKE_STAMP} marks a bake that went through the quiescence gate; the parent treats
           persisted bakes WITHOUT it as invalid, forcing the one-time re-bake that heals decks saved
           with pre-gate mid-count numbers. */
        var html='<style>'+styleText+'</style>'
          +'<div data-bds-root="" ${DOC_BAKE_STAMP} data-bds-fonts="'+escAttr(JSON.stringify(fontHrefs))+'" style="'+escAttr(rootStyle())+'">'
          +parts.join('')
          +'</div>';
        finish(true,html,fontHrefs,null);
      }catch(e){finish(false,null,fontHrefs,'bake: '+(e&&e.message||'failed'))}
    });
  });
}

/* ---- pick mode + live style preview ---- */
/* Everything under here stays COLD until the parent sends the first {__bds_pick:{on:true}}: no listener
   is registered, no style node is created, no element is touched. A resting card that never opens the
   inspector — which is every card on the canvas — pays for a few function objects and nothing else. */
var pickOn=false;var pickMode='select';var pickStyle=null;var hoverEl=null;var selEl=null;
var PICK_SKIP={HTML:1,BODY:1,HEAD:1,SCRIPT:1,STYLE:1,LINK:1,META:1,TITLE:1};

/* THE PATH CONTRACT: an element's address as ELEMENT-child indices from document.body down, so [0,2,1]
   means body.children[0].children[2].children[1]. Element children ONLY — the parent replays this path
   against the RAW SOURCE HTML with DOMParser to write the edit back, and the source's whitespace text
   nodes are not the ones this live document ended up with (the parser drops some, scripts add others).
   Any index that counted text or comment nodes would resolve to a different element over there and the
   user's font change would silently land on the wrong line. */
function pathOf(el){
  var out=[];var n=el;var body=document.body;
  while(n&&n!==body&&n.parentNode){
    var p=n.parentNode;var kids=p.children||[];var idx=-1;
    for(var i=0;i<kids.length;i++){if(kids[i]===n){idx=i;break}}
    if(idx<0)return null;
    out.unshift(idx);n=p;
  }
  return n===body?out:null;
}
/* The same contract in reverse, for __bds_style. A path that no longer matches — the parent is holding
   one from before a regeneration, or the document rewrote itself — resolves to null and the apply is a
   no-op. Never a throw: a stale inspector must not be able to break the slide it is pointed at. */
function nodeAt(path){
  try{
    var n=document.body;
    if(!n||!path||!path.length)return null;
    for(var i=0;i<path.length;i++){
      var kids=n.children;var k=path[i];
      if(typeof k!=='number'||k<0||!kids||k>=kids.length)return null;
      n=kids[k];
      if(!n)return null;
    }
    return n;
  }catch(e){return null}
}
/*
 * AN INLINE RUN IS TEXT, NOT A BOX — the one distinction the two functions below are built on.
 *
 * An element at display:inline cannot be sized, positioned or given a box of its own; it is part of its
 * parent's text flow and nothing else. So it is never the thing a click selects, and its words still
 * belong to the block around it. That is true of the spans the toolbar writes when you style a selected
 * word, and it was equally true before them of any span the generator wrote.
 *
 * The tag test comes FIRST and is what keeps this cheap: pickTarget runs on every mousemove, and a
 * getComputedStyle per ancestor would be a style resolution per pointer event on a document of a few
 * hundred elements. Only the handful of tags that are inline BY DEFAULT ever pay for the real reading —
 * which is also the reading that matters, because a span set to display:block IS a box and must stay
 * selectable.
 */
var INLINE_TAGS={SPAN:1,A:1,B:1,I:1,EM:1,STRONG:1,U:1,S:1,STRIKE:1,SMALL:1,SUP:1,SUB:1,MARK:1,CODE:1,ABBR:1,CITE:1,Q:1,VAR:1,KBD:1,SAMP:1,TIME:1,FONT:1,BDI:1,BDO:1,INS:1,DEL:1};
function inlineRun(el){
  if(!el||el.nodeType!==1)return false;
  if(!INLINE_TAGS[String(el.tagName||'').toUpperCase()])return false;
  try{return String(getComputedStyle(el).display||'')==='inline'}catch(e){return true}
}
/* Pickable = the element whose words these are. Clicking a headline has to select the headline, not the
   section wrapper it happens to sit in, and only the element that literally holds the text can take a
   font-size change without dragging every sibling in that wrapper with it.
   ITS OWN TEXT NODES, OR AN INLINE CHILD'S. Direct-only was the rule until styling a selected run
   existed: style the whole of a headline and its text moves inside a span, the headline stops owning any
   text at all, and the span becomes the only selectable thing in it — a word that turned into an object
   because it was given a colour (Beka, 2026-09-10). Text inside an inline child is still this element's
   text, because an inline child has no box to hold it in.
   THE RECURSION STOPS AT THE FIRST BLOCK, and that is the whole safety of it. If it climbed into block
   children, a section wrapper would own its headline's words and clicking the headline would select the
   section — the exact failure the direct-only rule was written to prevent. */
function ownsText(el){
  var kids=el.childNodes;
  for(var i=0;i<kids.length;i++){
    var k=kids[i];
    if(k.nodeType===3&&k.nodeValue&&k.nodeValue.trim()!=='')return true;
    if(k.nodeType===1&&inlineRun(k)&&ownsText(k))return true;
  }
  return false;
}
function pickable(el){
  if(!el||el.nodeType!==1)return false;
  if(PICK_SKIP[String(el.tagName||'').toUpperCase()])return false;
  return ownsText(el);
}
/* A click lands on whatever is under the cursor, which is often a wrapper or an inline decoration. Walk
   UP to the nearest element that owns text; if the chain reaches body without finding one, the click was
   on empty background and is simply ignored.
   DECK COMPONENTS ARE SEALED: anything inside a deck-* host picks the HOST. The runtime expands these
   elements at render time, so their internals exist only in the live DOM — a path into one would replay
   against a source that has no such node. The host itself is authored markup, so its path is real. */
function pickTarget(el){
  var n=el;
  while(n&&n.nodeType===1){
    if(/^DECK-/.test(String(n.tagName||'')))return n;
    n=n.parentNode;
  }
  /* Walk PAST inline runs to the block that owns them. A styled word, a bold phrase, a link inside a
     sentence: all text, none of them objects, and stopping on one hands the user a selection they
     cannot meaningfully resize, align or move.
     The first one found is remembered rather than discarded, because a document whose text lives only
     in inline elements — everything inside one span under body — would otherwise have nothing
     selectable at all. Better the run than nothing; it is only ever reached when there is no block
     above it that owns the words. */
  n=el;
  var run=null;
  while(n&&n.nodeType===1){
    if(pickable(n)){
      if(inlineRun(n)){if(!run)run=n}
      else return n;
    }
    n=n.parentNode;
  }
  return run;
}
/*
 * TEXT MODE SELECTS LIKE FIGMA (Beka, 2026-09-25): from the OUTSIDE IN. The hover shows what a click
 * would take; the first click takes the outermost box under the pointer, a click inside the selection
 * takes the next box in, a click on a sibling takes the sibling. pickTarget above walks the other way
 * (up to the nearest words) and still answers for double-click, the caret, and select mode.
 *
 * A LEVEL is a box a click can land on: any element on the way down from body to the hit, except
 *   - structure and the bridge;
 *   - anything that FILLS THE FRAME. The slide's wrapper and a full-bleed background layer are the slide
 *     itself; selecting one would put a ring round the whole page and move nothing a user can see. A
 *     picture the user added (data-hand-add) is the exception: at any size it is their layer;
 *   - inline runs (text, not boxes; see inlineRun) and display:contents (no box at all);
 *   - an only child with its parent level's exact box: a click that changes nothing on screen. The outer
 *     one stays, unless only the inner owns the words, and then the words win, because the Text controls
 *     have to act on the element whose font the stylesheet actually sets.
 * A deck-* host and an svg are one level each, sealed like everywhere else in this bridge.
 *
 * NON_CONTENT is the source's own set (artifactHand.ts), tag for tag: insertParent walks the same chain the
 * source does, so both have to pass over the same children. (HEAD is never a child of body; PICK_SKIP keeps
 * it out of the levels.)
 */
var NON_CONTENT={SCRIPT:1,STYLE:1,LINK:1,META:1,TITLE:1,TEMPLATE:1,NOSCRIPT:1,BASE:1};
function contentKids(el){
  var out=[];var ks=(el&&el.children)||[];
  for(var i=0;i<ks.length;i++){
    var k=ks[i];
    if(NON_CONTENT[String(k.tagName||'').toUpperCase()]||k.hasAttribute('${BRIDGE_MARK}'))continue;
    out.push(k);
  }
  return out;
}
function fillsFrame(el){
  try{
    var W=window.innerWidth||0,H=window.innerHeight||0;
    if(!(W>0&&H>0))return false;
    var r=el.getBoundingClientRect();
    return r.width>=W-1&&r.height>=H-1;
  }catch(e){return false}
}
function sameBox(a,b){
  try{
    var p=a.getBoundingClientRect(),q=b.getBoundingClientRect();
    if(!(p.width>0&&p.height>0))return false;
    return Math.abs(p.left-q.left)<=1&&Math.abs(p.top-q.top)<=1&&Math.abs(p.width-q.width)<=1&&Math.abs(p.height-q.height)<=1;
  }catch(e){return false}
}
function levelable(el){
  try{
    if(!el||el.nodeType!==1)return false;
    var tn=String(el.tagName||'').toUpperCase();
    if(PICK_SKIP[tn]||NON_CONTENT[tn]||el.hasAttribute('${BRIDGE_MARK}'))return false;
    if(inlineRun(el))return false;
    var d=String(getComputedStyle(el).display||'');
    if(d==='contents'||d==='none')return false;
    /* A PICTURE THE USER ADDED, resized to the whole slide, is still the picture (Beka, 2026-09-25). Refused
       here it could never be hovered, clicked or dragged again, and so never made smaller. */
    return el.hasAttribute('data-hand-add')||!fillsFrame(el);
  }catch(e){return false}
}
/* The levels over the hit, outermost first. Empty = the pointer is on the slide's own ground. */
function levelsOf(hit){
  var raw=[];var n=hit;var body=document.body;
  while(n&&n.nodeType===1&&n!==body){raw.unshift(n);n=n.parentNode}
  if(!body||n!==body)return [];
  for(var i=0;i<raw.length;i++){
    var tn=String(raw[i].tagName||'').toUpperCase();
    if(tn.indexOf('DECK-')===0||tn==='SVG'){raw=raw.slice(0,i+1);break}
  }
  var out=[];
  for(var j=0;j<raw.length;j++){
    var el=raw[j];
    if(!levelable(el))continue;
    var up=out.length?out[out.length-1]:null;
    if(up&&contentKids(el.parentNode).length===1&&sameBox(up,el)){
      if(pickable(el)&&!pickable(up))out[out.length-1]=el;
      continue;
    }
    out.push(el);
  }
  return out;
}
function indexIn(list,el){
  for(var i=0;i<list.length;i++){if(list[i]===el)return i}
  return -1;
}
/* WHAT A CLICK AT hit SELECTS. Walk up from the selection: the first of it or its ancestors that is a level
   over the hit is where the two meet, and the click takes the next level in from there, or that level
   itself when there is nothing further in. Inside the selection that is its child; beside it, the
   sibling; in another branch, that branch where it leaves the shared one. deep (⌘/Ctrl) is Figma's deep
   select: the innermost level, whatever is selected. */
function drillTarget(hit,deep){
  var ch=levelsOf(hit);
  if(!ch.length)return null;
  if(deep)return ch[ch.length-1];
  for(var a=selEl;a&&a.nodeType===1;a=a.parentNode){
    var i=indexIn(ch,a);
    if(i>=0)return ch[i+1<ch.length?i+1:i];
  }
  return ch[0];
}
/* The level a selection sits in: its parent level, or null at the top, where going up means the slide. An
   element that is not a level itself (the inner of a same-box pair, opened by the caret) answers the
   level that stands for it. */
function levelAbove(el){
  var ch=levelsOf(el);
  var i=indexIn(ch,el);
  if(i>0)return ch[i-1];
  if(i<0&&ch.length)return ch[ch.length-1];
  return null;
}
/* Enter's target: the first level, in document order, whose parent level is el. Answered as a click would
   answer it: a same-box pair stands for its words when only the inner owns them, so the walk goes down the
   pair before asking levelsOf which of the two is the level. */
function firstLevelIn(el){
  try{
    var all=el.getElementsByTagName('*');
    for(var i=0;i<all.length;i++){
      var c=all[i];
      var ch=levelsOf(c);
      if(!(ch.length>1&&ch[ch.length-1]===c&&ch[ch.length-2]===el))continue;
      var h=c,ks;
      while((ks=contentKids(h)).length===1&&sameBox(c,ks[0]))h=ks[0];
      if(h===c)return c;
      var hc=levelsOf(h);
      return hc.length?hc[hc.length-1]:c;
    }
  }catch(e){}
  return null;
}
/* ...and when there is none, the first element inside it that owns words, for the caret. SEALED like
   every other walk here: a deck-* host's words live in its runtime expansion, which has no element the
   source could take an edit back into, so a component never opens a caret (pickTarget answers the host for
   anything inside one, which is the test). */
function firstTextIn(el){
  var tn=String(el.tagName||'').toUpperCase();
  if(tn.indexOf('DECK-')===0||tn==='SVG')return null;
  if(pickable(el)&&!inlineRun(el))return el;
  try{
    var all=el.getElementsByTagName('*');
    for(var i=0;i<all.length;i++){
      var c=all[i];
      if(pickable(c)&&!inlineRun(c)&&pickTarget(c)===c)return c;
    }
  }catch(e){}
  return null;
}
/* NOTHING INSIDE THE SELECTION IS HOVERED (Beka, 2026-09-25): a selected card's own children get no line
   while the pointer crosses them; its siblings and everything outside it still do. */
function hoverFor(t){
  return !t||t===selEl||(selEl&&selEl.contains(t))?null:t;
}
/* WHILE TYPING, THE HOVER IS THE NEXT WORDS (Beka, 2026-09-25). A click on other words opens the caret in
   them straight away (onClick), so the line marks those words, not the level a click outside the caret
   would take. Anything that is not words, and a component's words, which the caret cannot open in, show
   the usual line. */
function editHover(target){
  var tw=pickTarget(target);
  if(tw&&tw!==editEl&&String(tw.tagName||'').toUpperCase().indexOf('DECK-')!==0)return tw;
  return hoverFor(drillTarget(target,false));
}
/* THE HOVER FOLLOWS THE SELECTION. A mouseover only comes when the pointer moves, so when the selection moves
   under a still pointer (Enter, Shift+Enter, Escape, the parent's select) the line would stay on what the
   last click would have taken. The last hit is remembered for this, and forgotten when the pointer leaves. */
var lastHit=null;var lastDeep=false;
function rehover(){
  if(pickMode!=='text')return;
  if(!lastHit||!document.contains(lastHit)||inEdit(lastHit)){setHover(null);return}
  setHover(hoverFor(drillTarget(lastHit,lastDeep)));
}
/* One level in (Enter, and {__bds_select:{down:1}} from the host's handles, which hold the focus after a
   press on the selected box), or into the caret when there is no level left. */
function selectDown(){
  if(!selEl)return;
  var kid=firstLevelIn(selEl);
  if(kid){setSel(kid);postPicked(kid);rehover();return}
  var tx=firstTextIn(selEl);
  if(tx)startEdit({path:pathOf(tx),on:true});
}
function selectUp(){
  if(!selEl)return;
  var up=levelAbove(selEl);
  if(editEl)endEdit(true);
  if(up){setSel(up);postPicked(up)}
  else{setSel(null);postPicked(null)}
  rehover();
}
/*
 * THE SLIDE, for the panel's Background when nothing is selected: the frame, meaning the element that
 * fills the whole slide and holds everything (body's only content child, and so on down while that stays
 * true), or body when there is no such wrapper. Its background is stored as a hand edit like any other.
 * bg is what the reader SEES: a transparent frame shows the colour painted behind it, so the reading walks
 * up until something paints.
 */
function slideTarget(){
  var n=document.body;
  if(!n)return null;
  for(;;){
    /* The one child that fills the slide AND holds content. A childless full-bleed layer beside it (grain,
       a glow) is decoration, not a frame, and must not push the target back to body: under a frame that
       paints its own ground, a body background would be a change nobody sees. Nothing the user added
       (data-hand-add) is ever the slide: a text box holding a <br> has children, and stretched to the full
       slide it would otherwise count. */
    var ks=contentKids(n),hit=null,count=0;
    for(var i=0;i<ks.length;i++){
      if(fillsFrame(ks[i])&&handable(ks[i])&&ks[i].children.length>0&&!ks[i].hasAttribute('data-hand-add')){hit=ks[i];count++}
    }
    if(count!==1)break;
    n=hit;
  }
  return n;
}
/* THE SLIDE IS WHAT THE BRIDGE MEASURES (Beka, 2026-09-25): st (slideTarget's answer) and everything holding it are
   the slide, never moved or deleted; nothing else is, however alone it stands. The source's single-child chain
   took a lone 622px column beside a picture the user added for the slide itself, so it could not be dragged,
   deleted or detached. The host passes st's path to moveHandTo / removeHand, which then take the same rule. */
function frameOf(el,st){
  try{return !!st&&!!el&&(el===st||el.contains(st))}catch(e){return false}
}
/* WHAT slideTarget WOULD ANSWER once drag has moved into c, read as if the move were made: its own walk, with drag
   counted in c and not in its old parent. Layout is taken as it stands (a move rarely makes a box fill the slide). */
function slideAfter(drag,c){
  var from=drag.parentNode;
  function kidsOf(n){
    var out=[];var ks=contentKids(n);
    for(var i=0;i<ks.length;i++){if(!(n===from&&ks[i]===drag))out.push(ks[i])}
    if(n===c)out.push(drag);
    return out;
  }
  function held(k){return k.children.length-(k===from?1:0)+(k===c?1:0)>0}
  var n=document.body;
  if(!n)return null;
  for(;;){
    var ks=kidsOf(n),hit=null,count=0;
    for(var i=0;i<ks.length;i++){
      if(fillsFrame(ks[i])&&handable(ks[i])&&held(ks[i])&&!ks[i].hasAttribute('data-hand-add')){hit=ks[i];count++}
    }
    if(count!==1)break;
    n=hit;
  }
  return n;
}
function clearColor(c){
  var s=String(c||'').replace(/\\s+/g,'');
  return s===''||s==='transparent'||/^rgba\\([0-9.]+,[0-9.]+,[0-9.]+,0(\\.0+)?\\)$/.test(s)||/\\/0(\\.0+)?\\)$/.test(s);
}
function effectiveBg(el){
  for(var n=el;n&&n.nodeType===1;n=n.parentNode){
    try{var c=String(getComputedStyle(n).backgroundColor||'');if(!clearColor(c))return c}catch(e){}
  }
  return 'rgb(255, 255, 255)';
}
/* THE LEVEL AROUND A SELECTION, drawn dashed by the host (Beka, 2026-09-25): where the selection sits, and
   where Escape goes. Text mode only; null at the top, where the level around it is the slide. */
function parentRect(el){
  try{
    if(pickMode!=='text')return null;
    var up=levelAbove(el);
    if(!up)return null;
    var q=up.getBoundingClientRect();
    return {x:q.left,y:q.top,w:q.width,h:q.height};
  }catch(e){return null}
}
function doSlide(){
  try{
    var el=slideTarget();
    if(!el){window.parent.postMessage({__bds_slidebox:1,path:null},'*');return}
    var p=el===document.body?[]:pathOf(el);
    if(!p)return;
    window.parent.postMessage({__bds_slidebox:1,path:p,eid:el.getAttribute('data-eid'),hand:el.getAttribute('data-hand'),bg:effectiveBg(el)},'*');
  }catch(e){}
}
/* OUTLINE, never border: a border participates in layout and would shove the document a pixel on every
   hover, which on a 1920x1080 slide reads as the page twitching. Bridge-marked so the bake serializer
   strips this node with the rest of the bridge, and so collectStyles() never folds it into a bake. */
function ensurePickStyle(){
  if(pickStyle)return;
  try{
    var s=document.createElement('style');
    s.setAttribute('${BRIDGE_MARK}','');
    /* data-bds-text is TEXT mode's root mark. ITS CURSOR IS THE ARROW, everywhere (Beka, 2026-09-25):
       every box in there is something a click selects, so a link's hand or an I-beam over a headline
       promised an action the click no longer takes. The I-beam comes back only inside the open caret,
       the one place typing actually lands. The hover outline is select mode's alone: text mode's
       hover is drawn out in the parent (__bds_hover), in the ring's blue and at screen scale, because a
       1px line in here shrinks with the frame and a dark translucent one vanishes on a dark slide. */
    s.textContent='[data-bds-pick] *{cursor:pointer !important}'
      +'[data-bds-pick] [data-bds-hi]{outline:1px solid rgba(0,0,0,0.25) !important;outline-offset:0 !important}'
      +'[data-bds-text],[data-bds-text] *{cursor:default !important}'
      +'[data-bds-text] [contenteditable],[data-bds-text] [contenteditable] *{cursor:text !important}'
      /* A press-and-drag moving an element (onPress): no words get selected on the way. */
      +'[data-bds-dragging],[data-bds-dragging] *{user-select:none !important;-webkit-user-select:none !important}'
      /* THE SECOND OUTLINE. An engine draws its own focus ring on a focused contenteditable, and in
         text mode the parent is already drawing TextEditRing around that same element from outside the
         frame — so an edit showed two boxes, one of them the browser's default, drawn a little apart
         from the parent's. The parent's is the one the design draws, so this one goes.
         Scoped to text mode, not blanket. data-bds-text is the mode's mark on the root element, so it can
         only ever apply while the preview's caret is what put the element in edit — the inspector in
         BirtvyArtifactEditor opens the same editor without a parent ring, and there the focus outline
         is the only thing marking the box. */
      +'[data-bds-text] [contenteditable]{outline:none !important}'
      /* AND THE ENGINE'S CARET, in the same mode: the parent draws that one too (__bds_caret). It is 1px
         in here and the frame is scaled to fit, so at Fit it painted under half a screen pixel and could
         not be seen (Beka, 2026-09-27: "the blinking writing indicator isn't visible"). Left on, it would
         blink out of step under the parent's. */
      +'[data-bds-text] [contenteditable]{caret-color:transparent !important}'
      /* AND THE SELECTION OUTLINE, for the same reason and in the same mode. The inspector paints a
         2px blue box on the element it has selected, which is right when the inspector is the only
         thing on screen — and in text mode the parent is drawing TextEditRing around that very
         element, so it would be two blue boxes a few px apart. Higher specificity than the
         [data-bds-hi] rule above, so it also covers hovering the element you have selected. */
      +'[data-bds-text] [data-bds-sel]{outline:none !important}'
      +'[data-bds-sel]{outline:2px solid #2563eb !important;outline-offset:0 !important}'
      /* A REORDER DRAG: the element dims where it stands while the host's ring travels as the ghost
         (__bds_lift), and a hit test without elementsFromPoint looks through it (dropHit). */
      +'[data-bds-lifted]{opacity:.35 !important}'
      +'[data-bds-nohit],[data-bds-nohit] *{pointer-events:none !important}'
      /* SETTLE MODE. A transition beats even !important, so a confirm read mid-transition lies. */
      +'[data-bds-still] *{transition:none !important}';
    (document.head||document.documentElement).appendChild(s);
    pickStyle=s;
  }catch(e){}
}
function mark(el,attr,on){
  try{if(on)el.setAttribute(attr,'');else el.removeAttribute(attr)}catch(e){}
}
function setHover(el){
  if(hoverEl===el)return;
  if(hoverEl)mark(hoverEl,'data-bds-hi',false);
  hoverEl=el;
  if(hoverEl)mark(hoverEl,'data-bds-hi',true);
  if(pickMode==='text')postHover();
}
/* TEXT MODE'S HOVER IS THE PARENT'S TO DRAW: the rect of what a click would select, in slide pixels like
   every other rect this bridge reports, or null. */
function postHover(){
  try{
    var r=null;
    if(hoverEl){var q=hoverEl.getBoundingClientRect();r={x:q.left,y:q.top,w:q.width,h:q.height}}
    window.parent.postMessage({__bds_hover:1,rect:r},'*');
  }catch(e){}
}
function setSel(el){
  if(dragging&&press&&el!==press.t)cancelDrag();
  if(selEl&&selEl!==el)mark(selEl,'data-bds-sel',false);
  selEl=el;
  if(selEl)mark(selEl,'data-bds-sel',true);
}
/* The slide is a hard 1080px box with overflow hidden — content past the fold is not scrolled past, it
   is cropped and gone. Report the furthest bottom in the document so the parent can warn the moment a
   size bump starts eating content, instead of the user discovering it in the exported PDF. */
function postOverflow(){
  try{
    var els=document.body?document.body.querySelectorAll('*'):[];
    var max=0;
    for(var i=0;i<els.length;i++){
      var b=els[i].getBoundingClientRect().bottom;
      if(b>max)max=b;
    }
    /* The selection's CURRENT rect rides along. The parent draws its own outline outside the iframe (it
       cannot reach in), so without fresh geometry after every apply that outline would sit at the size the
       text used to be — the one thing guaranteed to look broken while resizing text. This message is
       already posted after every style change, so the geometry is free. Null when nothing is selected. */
    var sr=null;
    /* selEl OR editEl. The inspector marks a selection and tracks it; TEXT mode never calls setSel at
       all (its whole point is that a single click belongs to the document), so the element being typed
       into was invisible here and the parent's ring never followed a resize — a headline stepped up
       two sizes kept its outline at the old one. The edited element is the selection in every sense
       that matters to geometry; it simply does not wear the inspector's mark. */
    var re=selEl||editEl;
    if(re){var q=re.getBoundingClientRect();sr={x:q.left,y:q.top,w:q.width,h:q.height}}
    window.parent.postMessage({__bds_overflow:1,bottom:max,rect:sr,parent:re?parentRect(re):null},'*');
  }catch(e){}
}
/* THE PICKED ELEMENT'S CURRENT VALUES, read off ONE computed-style handle. The panel lives outside the
   iframe and cannot reach in, so anything it does not receive here it has to guess — and a weight
   dropdown that opens on "400" for text that is really 700 makes the user's first click a change they
   never asked for. COMPUTED, not inline: the honest answer is what the element RENDERS as, whatever mix
   of stylesheet, cascade and an earlier preview produced it. This reports only; the write direction is
   __bds_style, which already takes any CSS property, so nothing here needs to grow when a control does.
   Every field degrades rather than throws: the whole read sits in one try/catch over an object that is
   already filled with sane defaults, because a picked element the panel can only half-describe is still
   picked, and losing the selection over an unparseable colour would be the worse bug. */
function textStyles(cs){
  /* fontFamily and fontStyle joined the reply for the preview's text toolbar, which draws a family chip
     and an italic toggle and cannot guess either. Additive: the panel that reads this in the artifact
     editor names the fields it wants and ignores the rest. */
  var out={fontSize:0,fontWeight:400,fontStyle:'normal',fontFamily:'',lineHeight:null,letterSpacing:0,textAlign:'',color:'',opacity:1};
  try{
    out.fontSize=parseFloat(cs.fontSize)||0;
    /* Computed font-weight is a number string in every engine we ship to, but the keywords are legal CSS
       and cheap to honour. Anything unreadable lands on 400, not 0: 400 IS normal, while a 0 handed to a
       weight control is a value the user would unknowingly apply straight back into the document. */
    var fw=parseFloat(cs.fontWeight);
    if(!(fw>0))fw=String(cs.fontWeight||'').toLowerCase()==='bold'?700:400;
    out.fontWeight=fw;
    /* line-height as a UNITLESS RATIO, because that is the only form that survives a font-size change: a
       panel holding 64px would keep the leading pinned while the text grew. Ratio only from a px reading
       — the computed form everywhere we ship. 'normal' has no ratio at all (it is the font's own metric,
       different per family) and a unit-less reading would divide into nonsense, so both stay null and the
       control shows an indeterminate state instead of inventing 1.2 and writing that guess into the
       document the first time the user touches anything else. */
    var lhs=String(cs.lineHeight||'');
    if(lhs.slice(-2)==='px'){
      var lh=parseFloat(lhs);
      if(lh>0&&out.fontSize>0)out.lineHeight=Math.round(lh/out.fontSize*100)/100;
    }
    /* 'normal' tracking IS zero, so parseFloat's NaN degrading to 0 is not a fallback, it is the answer. */
    out.letterSpacing=parseFloat(cs.letterSpacing)||0;
    out.textAlign=String(cs.textAlign||'');
    /* Whatever the cascade resolved, quoted names and fallbacks included — the toolbar shows the LEAD
       family and the host owns trimming it, because only the host knows which names it offers. */
    out.fontFamily=String(cs.fontFamily||'');
    /* normal | italic | oblique. Anything but normal lights the toolbar's italic control. */
    out.fontStyle=String(cs.fontStyle||'normal');
    /* Colour stays in whatever form getComputedStyle hands back (rgb/rgba). Parsing it to hex here would
       have to drop or re-encode alpha; the panel can round-trip the browser's own string untouched. */
    out.color=String(cs.color||'');
    /* NaN fails both comparisons, so an unreadable opacity reports fully opaque — the value that makes
       the panel agree with what the user is looking at. */
    var op=parseFloat(cs.opacity);
    out.opacity=op>=0&&op<=1?op:1;
  }catch(e){}
  return out;
}
/* NULL IS AN ANSWER HERE, and the parent needs it. Clicking off a headline onto the artifact's own
   ground deselects, and without a message saying so the ring would stay drawn around the last thing
   the user touched — chrome for a selection that no longer exists. The reply keeps the same name so
   there is one message about what is selected rather than two that can disagree; path null is the
   empty one. */
function postPicked(el){
  try{
    if(!el){window.parent.postMessage({__bds_picked:1,path:null},'*');return}
    var p=pathOf(el);
    if(!p)return;
    var r=el.getBoundingClientRect();
    var t=String(el.textContent||'').trim();
    if(t.length>80)t=t.slice(0,80);
    /* One getComputedStyle handle feeds the whole reply. It is a live object, but every property read off
       it can force a style resolution, and the panel wants seven of them — one handle keeps that to a
       single flush. The top-level fontSize is taken from the same object it reports, so the legacy field
       and styles.fontSize can never disagree. */
    var cs=getComputedStyle(el);
    var st=textStyles(cs);
    /* ...unless a run of words inside it is selected, in which case the toolbar is about to act on THAT
       and must be showing its values, not the element's. Only for the element being edited: a selection
       elsewhere in the document is not inside this one. See rangeStyles for what mixed means. */
    var rng=false;var mx={};
    if(el===editEl){
      var lr=liveRange();
      if(lr){var rs=rangeStyles(lr);st=rs.styles;mx=rs.mixed;rng=true}
    }
    /* rect rides in the DOCUMENT'S OWN coordinates: the iframe viewport is exactly 1920x1080 and the
       slide never scrolls, so client coordinates ARE slide coordinates and the parent only has to
       multiply by the card's scale to draw its selection outline on top. */
    window.parent.postMessage({__bds_picked:1,path:p,tag:String(el.tagName||'').toLowerCase(),text:t,fontSize:st.fontSize,rect:{x:r.left,y:r.top,w:r.width,h:r.height},styles:st,range:rng,mixed:mx,box:boxOf(el,cs,p),parent:parentRect(el)},'*');
  }catch(e){}
}
/* BOX FACTS for the layout editor, off the same computed handle. The rect above INCLUDES translate, so
   the host subtracts it to find the layout slot. siblings skips bridge nodes: it counts what the source
   has, and the bridge scripts only ever sit last in body, so index is unaffected. */
function boxOf(el,cs,p){
  var b={display:'',position:'',translate:'none',eid:null,hand:null,parentDisplay:'',parentPosition:'',parentAnchor:false,index:p&&p.length?p[p.length-1]:0,siblings:0,css:{},kids:0,handable:false,text:false,added:false,frame:false,top:false};
  try{
    /* ADDED = a picture the user added by hand: its placement is the picture, so the host's Reset keeps it. */
    b.added=el.hasAttribute('data-hand-add');
    /* FRAME = the slide as measured (slideTarget) or what holds it: never moved or deleted. TOP = a direct child
       of it, the level placed freely like a Figma frame without auto layout (Beka, 2026-09-25). See frameOf. */
    var st=slideTarget();
    b.frame=frameOf(el,st);
    b.top=!!st&&el.parentNode===st;
    /* TEXT = the Text controls apply: the element owns words, or it is a component whose words live in
       its expansion (a deck-stat's number), which the host styles as a whole. A card does not. */
    b.text=pickable(el)||(/^DECK-/.test(String(el.tagName||'').toUpperCase())&&String(el.textContent||'').trim()!=='');
    b.css=boxCss(cs);
    b.kids=kidCount(el);
    b.handable=handable(el);
    b.display=String(cs.display||'');
    b.position=String(cs.position||'');
    b.translate=String(cs.getPropertyValue('translate')||'').trim()||'none';
    b.eid=el.getAttribute('data-eid');
    b.hand=el.getAttribute('data-hand');
    var par=el.parentElement;
    if(par){
      var pcs=getComputedStyle(par);
      b.parentDisplay=String(pcs.display||'');
      b.parentPosition=String(pcs.position||'static');
      b.parentAnchor=anchorable(el,par);
      var ks=par.children;var c=0;
      for(var i=0;i<ks.length;i++){if(!ks[i].hasAttribute('${BRIDGE_MARK}'))c++}
      b.siblings=c;
    }
  }catch(e){}
  return b;
}
/* BOX CSS = the computed subset the properties panel reads (BOX_CSS_PROPS), off the handle the caller holds.
   kidCount = element children the source has, bridge nodes excluded: Layout controls need 2+ of them. */
var BOX_CSS=${JSON.stringify(BOX_CSS_PROPS)};
function boxCss(cs){
  var o={};
  try{for(var i=0;i<BOX_CSS.length;i++){o[BOX_CSS[i]]=String(cs.getPropertyValue(BOX_CSS[i])||'').trim()}}catch(e){}
  return o;
}
function kidCount(el){
  var ks=el.children,c=0;
  for(var i=0;i<ks.length;i++){if(!ks[i].hasAttribute('${BRIDGE_MARK}'))c++}
  return c;
}
/* ANCHOR = may a Detach pin el to its parent (position:relative on the parent) instead of the slide, so
   the element travels with its card when the layout reflows. No when the parent is structure or the whole
   slide (the slide already is the anchor); no when the parent clips (overflow other than visible), since
   as the containing block it would crop an element dropped past its edge; and no when the parent holds
   another absolutely positioned element that is anchored ABOVE it today: making the parent relative would
   move that one too. */
function anchorable(el,par){
  try{
    if(!handable(par))return false;
    var pcs=getComputedStyle(par);
    var ox=pcs.overflowX||pcs.overflow||'visible',oy=pcs.overflowY||pcs.overflow||'visible';
    if(ox!=='visible'||oy!=='visible')return false;
    var r=par.getBoundingClientRect();
    if(r.width>=window.innerWidth-1&&r.height>=window.innerHeight-1)return false;
    var all=par.getElementsByTagName('*');
    for(var i=0;i<all.length;i++){
      var d=all[i];
      if(d===el||el.contains(d)||d.hasAttribute('${BRIDGE_MARK}'))continue;
      if(getComputedStyle(d).position!=='absolute')continue;
      var inside=false;
      for(var up=d.parentElement;up&&up!==par;up=up.parentElement){
        var ups=getComputedStyle(up).position;
        if(ups&&ups!=='static'){inside=true;break}
      }
      if(!inside)return false;
    }
    return true;
  }catch(e){return false}
}
/* ---- selection-specific styling ---- */
/*
 * A RANGE, WHEN THERE IS ONE WORTH STYLING.
 *
 * Non-collapsed, and wholly inside the element being edited. Both halves are load-bearing: a collapsed
 * caret means the user selected nothing and the toolbar acts on the element (settled 2026-09-10 — you
 * can always select-all to say "the element" explicitly), and a range reaching outside editEl would
 * have this bridge style markup it is not the one committing.
 */
function liveRange(){
  try{
    if(!editEl)return null;
    var s=document.getSelection();
    if(!s||s.rangeCount===0)return null;
    var r=s.getRangeAt(0);
    if(r.collapsed)return null;
    if(!editEl.contains(r.startContainer)||!editEl.contains(r.endContainer))return null;
    return r;
  }catch(e){return null}
}
/*
 * The text nodes with CHARACTERS inside the range, which is not the same as the nodes it touches.
 * intersectsNode answers true for a node that merely meets a boundary with nothing of it selected, and
 * a zero-width overlap is not styled text — it would report the paragraph after the selection as part
 * of it and turn every reading mixed. The two boundary comparisons are the overlap test proper:
 * range.start before node.end, and range.end after node.start.
 */
function rangeTextNodes(r){
  var out=[];
  try{
    var w=document.createTreeWalker(editEl,NodeFilter.SHOW_TEXT,null);
    var n;
    while((n=w.nextNode())){
      if(!n.nodeValue||!n.nodeValue.length)continue;
      var nr=document.createRange();
      nr.selectNodeContents(n);
      if(r.compareBoundaryPoints(Range.END_TO_START,nr)<0&&r.compareBoundaryPoints(Range.START_TO_END,nr)>0)out.push(n);
    }
  }catch(e){}
  return out;
}
/*
 * WHAT THE SELECTION IS SET IN — and where it disagrees with itself.
 *
 * One reading per text node's parent, folded together. A property every node agrees on is reported as
 * that value; one they do not is named in mixed, and the control draws an indeterminate state rather
 * than picking a winner. That distinction is the whole reason this is not just textStyles(editEl): a
 * size box showing 128 over a selection that is half 128 and half 40 makes the user's next press a
 * change they never asked for, which is the same failure the weight dropdown had.
 *
 * Alignment is deliberately the BLOCK's. A run of words cannot be centred while the sentence around it
 * is left, so taking it from the element is not a shortcut, it is the only true answer.
 */
function rangeStyles(r){
  var base=textStyles(getComputedStyle(editEl));
  var nodes=rangeTextNodes(r);
  if(nodes.length===0)return {styles:base,mixed:{}};
  var keys=['fontSize','fontWeight','fontStyle','fontFamily','color','letterSpacing','lineHeight','opacity'];
  var out=null;var mixed={};
  for(var i=0;i<nodes.length;i++){
    var pe=nodes[i].parentElement;
    if(!pe)continue;
    var st=textStyles(getComputedStyle(pe));
    if(!out){out=st;continue}
    for(var k=0;k<keys.length;k++){
      var key=keys[k];
      if(out[key]!==st[key])mixed[key]=true;
    }
  }
  if(!out)return {styles:base,mixed:{}};
  out.textAlign=base.textAlign;
  return {styles:out,mixed:mixed};
}
/*
 * Drop one property from every inline run inside an element, and lift out any wrapper left holding
 * nothing.
 *
 * Runs only — the tag list, and never an element that has made itself a box with its own display. A
 * nested BLOCK is a thing the user can still select and restyle on its own, so its declarations are
 * its business; a run is not, and its declarations are the block's to overwrite.
 */
function clearRunProp(el,prop){
  try{
    var kids=el.children;
    for(var i=0;i<kids.length;i++){
      var k=kids[i];
      if(!INLINE_TAGS[String(k.tagName||'').toUpperCase()])continue;
      if(!k.style)continue;
      /* It has made itself a box, so it is not a run and its declarations are its own. */
      if(k.style.getPropertyValue('display'))continue;
      if(k.style.getPropertyValue(prop)){
        k.style.removeProperty(prop);
        var a=k.getAttribute('style');
        if(a!==null&&String(a).trim()==='')k.removeAttribute('style');
      }
      /* A run may wrap another run. Downward through runs only — a nested BLOCK is selectable in its
         own right and is left entirely alone, subtree included. */
      clearRunProp(k,prop);
    }
  }catch(e){}
}
/* Lift a wrapper out of the tree, keeping its children exactly where they were. */
function unwrapEl(el){
  try{
    var p=el.parentNode;if(!p)return;
    while(el.firstChild)p.insertBefore(el.firstChild,el);
    p.removeChild(el);
  }catch(e){}
}
/*
 * Fold neighbouring wrappers that say the same thing, and glue the text back together.
 *
 * Without it, styling a word and then its neighbour leaves two spans where one would do, and doing it
 * a dozen times leaves a headline made of shrapnel — markup the author never wrote, in a document that
 * gets re-read, re-themed and baked. Adjacent-and-identical only: a general span flattener would have
 * to reason about the author's own spans, and this one is only allowed to tidy after itself.
 */
function mergeSpans(root){
  try{
    var spans=root.querySelectorAll('span[style]');
    for(var i=0;i<spans.length;i++){
      var el=spans[i];
      if(!el.parentNode)continue;
      var prev=el.previousSibling;
      if(prev&&prev.nodeType===1&&String(prev.tagName||'').toUpperCase()==='SPAN'&&prev.getAttribute('style')===el.getAttribute('style')&&prev.className===el.className){
        while(el.firstChild)prev.appendChild(el.firstChild);
        el.parentNode.removeChild(el);
      }
    }
    root.normalize();
  }catch(e){}
}
/*
 * STYLE THE SELECTED RUN by wrapping it.
 *
 * extractContents and re-insert, NOT surroundContents: the latter refuses any range that partially
 * selects an element, and partial is the ordinary case — selecting across the end of a bold word, or
 * from the middle of one line into the next. Extract splits those boundaries itself.
 *
 * Descendants that set the SAME property are stripped, because an inner span saying 40px inside a new
 * outer saying 128px still renders at 40 and the press would look ignored. One left carrying no
 * declarations at all is lifted out rather than kept as an empty wrapper.
 *
 * The words stay selected afterwards. A selection that collapsed on every apply would make the second
 * press of a run — size, then colour — silently style the whole element instead.
 */
function doStyleRange(prop,value){
  var r=liveRange();
  if(!r||!editEl)return false;
  if(value===null||value===undefined)return false;
  try{
    var span=document.createElement('span');
    span.style.setProperty(prop,String(value));
    if(span.style.length===0)return false;
    var frag=r.extractContents();
    /* THE RUN IS ALREADY WRAPPED: set the property on that wrapper instead of building a second one
       around it. Selecting a word and then pressing colour, size and weight in turn is the ordinary
       way this control is used, and without this each press nests another span — three wrappers for
       one word, in a document that gets re-read, re-themed and baked.
       Only one of OURS: no class, and nothing but the span in the fragment. An author's span may be
       carrying a class this bridge knows nothing about, and quietly writing into it would be the
       toolbar editing markup the user did not select. */
    var only=frag.childNodes.length===1?frag.firstChild:null;
    if(only&&only.nodeType===1&&String(only.tagName||'').toUpperCase()==='SPAN'&&!only.className){
      try{
        only.style.setProperty(prop,String(value));
        r.insertNode(only);
        var s0=document.getSelection();
        if(s0){var r0=document.createRange();r0.selectNode(only);s0.removeAllRanges();s0.addRange(r0)}
        mergeSpans(editEl);
        return true;
      }catch(e){}
    }
    span.appendChild(frag);
    var inner=span.querySelectorAll('span[style]');
    for(var i=0;i<inner.length;i++){
      try{
        inner[i].style.removeProperty(prop);
        if(inner[i].style.length===0)unwrapEl(inner[i]);
      }catch(e){}
    }
    r.insertNode(span);
    /* THE WRAPPER, not its contents — and that one word is the difference between one span and three.
       Leaving the selection on the contents means the next apply extracts the TEXT out of this span and
       wraps that instead, nesting inward on every press: colour, then size, then weight came back as
       three spans deep around one word. Selecting the wrapper hands the next apply an element it can
       recognise as its own and merge into. */
    var s=document.getSelection();
    if(s){var nr=document.createRange();nr.selectNode(span);s.removeAllRanges();s.addRange(nr)}
    mergeSpans(editEl);
    return true;
  }catch(e){return false}
}
/*
 * A RANGE EDIT IS A TEXT EDIT, and that is the whole reason it can exist at all.
 *
 * A wrapped span is markup INSIDE the element, not a property ON it, so it cannot travel on
 * __bds_style: the parent replays that message against the source by setting the property on the
 * element at that path, which would style the entire sentence — the exact thing the user avoided by
 * selecting a word. So it travels as innerHTML on the channel built for that, and lands through the
 * same sanitizer (which already allows span and style), the same source rewrite and the same undo step
 * as every typed edit. Nothing new to persist, nothing new to fail.
 */
/*
 * ONE WRITE PER GESTURE, NOT ONE PER POINTER MOVE.
 *
 * The colour panel emits on every step of a drag — deliberately, because watching the headline change
 * under the pointer is the whole point of a colour picker over an artifact. The ELEMENT path has always
 * absorbed that out in the host, where a 400ms debounce turns a drag into one revision. The range path
 * had nothing: every emit wrapped the selection and posted innerHTML straight back, so a two-second
 * drag was fifty source writes, fifty commits, fifty undo steps and fifty bakes — the artifact
 * apparently reloading in a loop (Beka, 2026-09-10).
 *
 * Coalesced HERE rather than out there because this is the side that knows a range write happened at
 * all; the host's channel is "the element's contents changed", which is true of a typed commit too and
 * must stay immediate. Same 400ms as the host's own, so the two paths feel identical.
 *
 * endEdit cancels this and posts the full innerHTML itself, so nothing in flight is ever lost by
 * leaving the editor — the final commit is a superset of whatever this was about to send.
 */
var TEXT_COMMIT_MS=400;
var textTimer=0;
function scheduleTextCommit(){
  try{
    if(textTimer)clearTimeout(textTimer);
    textTimer=setTimeout(function(){textTimer=0;commitEditHtml()},TEXT_COMMIT_MS);
  }catch(e){commitEditHtml()}
}
function commitEditHtml(){
  var el=editEl;if(!el)return;
  var p=null;var h='';var t='';
  try{
    h=String(el.innerHTML||'');
    t=String(el.textContent||'').trim();
    if(t.length>80)t=t.slice(0,80);
    p=pathOf(el);
  }catch(e){p=null}
  if(!p)return;
  try{window.parent.postMessage({__bds_text:1,path:p,html:h,text:t},'*')}catch(e){}
  /* Re-baseline what Escape restores. editOrig was captured when the caret opened, and leaving it here
     would let one Escape throw away a style the parent has ALREADY written to the source and recorded
     an undo step for — a document and a history that disagree. A committed change is not cancellable;
     typing since the last commit still is. */
  editOrig=h;
}
/* The element being edited is OFF LIMITS to the inspector: inside it the pointer is a text cursor, so the
   hover outline is chrome for a mode that is no longer running, and hover is cleared rather than merely
   skipped so an outline drawn just before the edit opened does not sit there for the whole session. */
function onOver(e){
  if(!pickOn)return;
  if(inEdit(e.target)){setHover(null);return}
  /* Text mode hovers what a click would take, and nothing over the selection itself: the ring is already
     there, and a second line on the same box would only blur it. */
  if(pickMode==='text'){
    lastHit=e.target;lastDeep=!!(e.metaKey||e.ctrlKey);
    if(dragging){setHover(null);return}
    setHover(editEl&&!lastDeep?editHover(e.target):hoverFor(drillTarget(e.target,lastDeep)));return;
  }
  setHover(pickTarget(e.target));
}
/* relatedTarget null = the pointer left the document entirely, the one case mouseover cannot answer. */
function onOut(e){if(!pickOn)return;if(!e.relatedTarget){lastHit=null;setHover(null)}}
/* Capture phase AND preventDefault: while the inspector is on, a click is a selection and nothing else.
   Documents ship <a> tags and their own handlers, and a navigation inside a sandboxed opaque-origin
   iframe replaces the slide with a blank page that nothing can navigate back. */
function onClick(e){
  if(!pickOn)return;
  /* TEXT MODE: FIRST CLICK SELECTS THE ELEMENT, SECOND CLICK OPENS THE CARET (Beka, 2026-09-05).
     Which is the third answer this has had, and the last two are both in the argument. It began as
     double-click, on the theory that single clicks belong to the artifact — true of the artifact's own
     controls, false of its prose, since nobody double-clicks a headline to fix a typo. So it became one
     click straight into the caret, and that overshot: there is no way to change a headline's SIZE or
     COLOUR without first putting a cursor in its words, which is a strange thing to have to do to an
     object you only want to restyle.
     Two single clicks is the shape every canvas editor already uses, and it gives the toolbar a state
     to live in: select, restyle, and type only if that is what you came to do.
     THE SPLIT IS BY TARGET, NOT BY GESTURE: pickTarget answers for anything that OWNS TEXT, walking up
     from whatever was under the cursor, and a click it answers for is ours and is stopped here.
     BE HONEST ABOUT WHAT THAT COSTS. Owning text is a wide net — a <button> with a label and an <a>
     with words are both inside it — so while text mode is armed those do NOT run: the button's handler
     does not fire and the link does not navigate. MEASURED, not assumed (2026-09-05). For the link that
     is the behaviour we want either way, and it is old: a navigation inside a sandboxed opaque-origin
     iframe replaces the slide with a blank page nothing can navigate back from. For the button it is a
     real cost against the preview's promise that the artifact is live in there, and it is the price of
     being able to restyle a button's own label, which is as legitimate an edit as any other.
     What genuinely falls through is what owns no text at all: a chart, an image, an icon, empty ground.
     The caret lands where you aimed on that second click: the browser has already placed a collapsed
     caret at the click point by the time this fires, and startEdit reads that selection before
     flipping contentEditable rather than replacing it with select-all.
     AND THEN IT BECAME FIGMA'S (Beka, 2026-09-25): the target is no longer "the words under the
     pointer" but the next level in (drillTarget), so every box is selectable, not only the ones
     holding text, and a card is one click away instead of an Escape away. The price grew with it:
     while this mode is armed, a click on anything in the artifact selects it, a chart and a
     text-less button included. Only the slide's own ground is still left to the document. */
  if(pickMode==='text'){
    if(swallowClick){swallowClick=false;try{e.preventDefault();e.stopPropagation()}catch(err){}return}
    if(inEdit(e.target))return;
    /* A DRAG THAT STARTED IN THE CARET and was let go outside it: the browser fires the click on the common
       ancestor, which is not a click on anything. Selecting that container would close the caret and drop
       the words the user just selected to style. A plain click elsewhere has collapsed the selection on its
       mousedown, so it still takes the next target. */
    if(editEl){
      try{var ds=window.getSelection();if(ds&&ds.rangeCount&&!ds.isCollapsed&&inEdit(ds.getRangeAt(0).startContainer))return}catch(err){}
    }
    /* FROM ONE CARET STRAIGHT INTO THE NEXT (Beka, 2026-09-25, as Figma does): a click on other words while
       typing opens the caret in them, where the click landed (the browser has already put a collapsed caret
       there, and startEdit keeps it), with no stop at a selection on the way. Anything that is not words (a
       card's padding, a chart, the ground), or words the caret cannot open in (a component), takes the
       usual click. */
    var fromEdit=pressFromEdit;pressFromEdit=false;
    if(fromEdit&&!e.metaKey&&!e.ctrlKey){
      var tw=pickTarget(e.target);
      if(tw){
        startEdit({path:pathOf(tw),on:true});
        if(editEl===tw){try{e.preventDefault();e.stopPropagation()}catch(err){}return}
      }
    }
    if(clickIn(e.target,!!(e.metaKey||e.ctrlKey),null)){try{e.preventDefault();e.stopPropagation()}catch(err){}}
    return;
  }
  try{e.preventDefault();e.stopPropagation()}catch(err){}
  /* A click inside the open editor is the user aiming a CARET, not picking an element. Re-selecting here
     would fight the edit — same element, but postPicked would reset the panel mid-typing, and a click into
     a child <span> would move the selection to a node the editor does not own. The preventDefault above
     still stands (links must not navigate); caret placement comes from mousedown and is untouched. */
  if(inEdit(e.target))return;
  var t=pickTarget(e.target);
  if(!t)return;
  setSel(t);
  postPicked(t);
}
/* THE TEXT-MODE CLICK, for a real one and for one the host passes in (__bds_click): the host's handles
   cover the selected box so the whole of it can be dragged, and a press there that never moved is a click
   meant for the artifact. Answers whether the click was ours (the caller stops a real one). at is the
   point of a passed-in click: the browser put no caret down for it, so the promotion puts one there. */
function clickIn(target,deep,at){
  var tt=drillTarget(target,deep);
  if(!tt){
    /* Onto the slide's own ground. The click is the artifact's — no preventDefault — but the
       selection is over, and the parent has to hear so its ring goes and the panel shows the slide. */
    if(selEl){setSel(null);postPicked(null)}
    return false;
  }
  /* NOTHING FURTHER IN: the click is on the selection itself, and that is the promotion. The caret
     goes into the words under the pointer when they are inside the selection (a card's padding owns
     none, and stays selected). Anything else is a first click on a new target, even when an editor is
     already open elsewhere — startEdit would have committed that one, and here setSel does not, so
     the click that MOVES the selection also ends the previous edit. */
  if(selEl===tt){
    /* ONLY THE SELECTED ELEMENT'S OWN WORDS (Beka, 2026-09-25): a card never opens a caret in the text
       inside it, however the click lands. The text is selected first, and the next click on it types. */
    if(pickTarget(target)===selEl){
      if(at)caretAt(at.x,at.y);
      startEdit({path:pathOf(selEl),on:true});
    }
    return true;
  }
  if(editEl)endEdit(true);
  setSel(tt);
  postPicked(tt);
  /* The pointer has not moved, so no mouseover will come: the hover is worked out again now. */
  setHover(hoverFor(drillTarget(target,deep)));
  return true;
}
/* A PASSED-IN CLICK, at a point in slide pixels, taken exactly as a real one. */
function doClick(m){
  try{
    if(!pickOn||pickMode!=='text'||!m)return;
    var x=+m.x,y=+m.y;
    if(!(x>=0&&y>=0))return;
    var el=document.elementFromPoint(x,y);
    if(!el||inEdit(el))return;
    clickIn(el,!!m.deep,{x:x,y:y});
  }catch(e){}
}
/* The collapsed caret a real click would have left at the point, for a click that was passed in. */
function caretAt(x,y){
  try{
    var r=null;
    if(document.caretRangeFromPoint)r=document.caretRangeFromPoint(x,y);
    else if(document.caretPositionFromPoint){
      var cp=document.caretPositionFromPoint(x,y);
      if(cp){r=document.createRange();r.setStart(cp.offsetNode,cp.offset);r.collapse(true)}
    }
    var s=window.getSelection();
    if(!r||!s)return;
    s.removeAllRanges();s.addRange(r);
  }catch(e){}
}
/* DOUBLE-CLICK STARTS TYPING, and it has to be decided in here. A dblclick inside the iframe cannot be
   seen from the parent at all: pointer events do not cross a browsing-context boundary, so the overlay has
   no way to know one happened and could never send the __bds_edit that starts an edit. The bridge is the
   only place with the event, so it opens the editor itself and TELLS the parent (__bds_editing) rather than
   waiting to be asked. Single click still selects, which is what the inspector acts on. */
function onDbl(e){
  if(!pickOn)return;
  /* Already typing: let the browser have it. A double-click inside an open editor is the standard
     select-the-word gesture, and stealing it would make text impossible to replace by double-clicking. */
  if(inEdit(e.target))return;
  /* TEXT MODE HAS NO DOUBLE-CLICK (Beka, 2026-09-25). Its two clicks have already gone in two levels, from
     a card to the words in it, and a caret on top of that skipped the one state the user was after: the
     words selected. The caret is a click on the selected words themselves (clickIn). */
  if(pickMode==='text')return;
  var t=pickTarget(e.target);
  if(!t)return;
  try{e.preventDefault();e.stopPropagation()}catch(err){}
  startEdit({path:pathOf(t),on:true});
}
/* MODE, not a second switch. select is the inspector this bridge has always had: hover outlines
   anything with text, a click selects it and reports, dblclick edits, links are held. text is /deck's
   no-mode text editing: the same hover outline, the same select-then-edit order, but reached with two
   single clicks instead of a click and a double, and a click that lands anywhere the caret could not go
   is left to the document. Both are the same listeners on the same targets — only what they do with a
   plain click differs — so this is one flag rather than two code paths. */
/* KEYS FOR A SELECTED BOX. Focus stays in here after a click, so the layout editor out there never sees
   the keystroke unless it is forwarded. A document capture listener fires BEFORE editEl's own, so it
   returns the instant an editor is open: it must never eat typing. Form fields are the artifact's. */
var SEL_KEYS={ArrowUp:1,ArrowDown:1,ArrowLeft:1,ArrowRight:1,Escape:1,Delete:1,Backspace:1};
/* THE INSERT DOCK'S TOOLS (Beka, 2026-09-27): T for text, R for a square, C for a circle, selection or not.
   Focus stays in here after any click on the slide, so a letter typed then would never reach the dock.
   Plain letters only, never while a caret is open (that is typing), and only in text mode (/deck's). */
var TOOL_KEYS={t:1,r:1,c:1};
function onSelKey(e){
  try{
    if(editEl||!pickOn)return;
    var tg=e.target;var tt=tg&&tg.tagName?String(tg.tagName).toUpperCase():'';
    if(tt==='INPUT'||tt==='TEXTAREA'||tt==='SELECT'||(tg&&tg.isContentEditable))return;
    var k=String(e.key||'');
    if(pickMode==='text'&&TOOL_KEYS[k.toLowerCase()]===1&&!e.altKey&&!e.metaKey&&!e.ctrlKey&&!e.shiftKey){
      e.preventDefault();
      window.parent.postMessage({__bds_key:1,key:k.toLowerCase(),code:String(e.code||''),shift:false,alt:false,meta:false,ctrl:false,repeat:!!e.repeat},'*');
      return;
    }
    /* THE STAGE'S EDIT KEYS (2026-09-27), selection or not, text mode only: undo and redo (⌘Z, ⇧⌘Z, ⌘Y),
       Duplicate (⌘D) and the layer order (⌘] and ⌘[, with ⌥ or ⇧ for front and back). The host decides what
       each does; with nothing to act on it does nothing. The bracket is read off the key's code too, since its
       character changes with ⇧ and ⌥ (} { and quotes). ⌘C, ⌘X and ⌘V are never taken here: their keydown is
       left alone so the browser fires copy, cut and paste (onClip, onPaste). */
    if(pickMode==='text'&&(e.metaKey||e.ctrlKey)){
      var lk=k.toLowerCase();
      var br=e.code==='BracketRight'||k===']'||k==='}'||k==='\\u2018'||k==='\\u2019'?']'
        :e.code==='BracketLeft'||k==='['||k==='{'||k==='\\u201c'||k==='\\u201d'?'[':'';
      var fwd=br?br:!e.altKey&&(lk==='z'||lk==='y')?lk:!e.altKey&&!e.shiftKey&&lk==='d'?'d':'';
      if(fwd){
        e.preventDefault();
        window.parent.postMessage({__bds_key:1,key:fwd,code:String(e.code||''),shift:!!e.shiftKey,alt:!!e.altKey,meta:!!e.metaKey,ctrl:!!e.ctrlKey,repeat:!!e.repeat},'*');
        return;
      }
    }
    if(!selEl)return;
    /* ENTER AND SHIFT+ENTER are Figma's too, and they stay in here: they only move the selection, which is
       the bridge's to know. Enter goes one level in, or into the caret when there is no level left; with
       Shift it goes up (Escape does the same through the parent, which lands pending edits first — a
       new __bds_picked flushes them just the same). */
    if(pickMode==='text'&&k==='Enter'&&!e.altKey&&!e.metaKey&&!e.ctrlKey){
      /* STOPPED HERE, not only prevented. The caret this may open adds its own capture listener to the
         element, and a keydown whose target is that element (a focused button or link: Chrome focuses them
         on mousedown) would reach it too, and type this very Enter into the words it just opened. */
      e.preventDefault();e.stopPropagation();
      if(e.shiftKey)selectUp();
      else selectDown();
      return;
    }
    /* Select mode (BirtvyArtifactEditor) keeps its ⌘Z for a selection; text mode's went out above. */
    var undo=pickMode!=='text'&&(k==='z'||k==='Z')&&(e.metaKey||e.ctrlKey);
    if(!SEL_KEYS[k]&&!undo)return;
    e.preventDefault();
    window.parent.postMessage({__bds_key:1,key:k,code:String(e.code||''),shift:!!e.shiftKey,alt:!!e.altKey,meta:!!e.metaKey,ctrl:!!e.ctrlKey,repeat:!!e.repeat},'*');
  }catch(err){}
}
/* THE SECOND PRESS OF A DOUBLE-CLICK selects a word in the document before any click handler runs, and
   text mode gives double-click no meaning, so it would only leave a stray highlight on the words the two
   clicks just selected. Not inside the open editor: there it is the user's word selection. */
/* WAS A CARET OPEN when this press landed? The press itself closes it (the editor's blur commits on the way
   out), so by the time the click arrives editEl is already gone: the press remembers for it. */
var pressFromEdit=false;
function onDown(e){
  if(!pickOn||pickMode!=='text')return;
  pressFromEdit=!!editEl&&!inEdit(e.target);
  if(!(e.detail>1)||inEdit(e.target))return;
  try{e.preventDefault()}catch(err){}
}
/*
 * PRESS AND DRAG AN ELEMENT THAT IS NOT SELECTED YET (Beka, 2026-09-25): the press picks what a click
 * would pick, and the same gesture moves it, with no click first. Only the host can move an element (its
 * handles hold the snapping, the guides and the one revision a move lands as), and the host's handles
 * exist only over a selection, so a press in here is watched: past a few slide pixels it selects its target
 * and streams the drag out ({__bds_drag, phase: start / move / end / cancel}, in slide pixels), and the
 * host runs the same move its handles run. Armed by the host ({__bds_dragok:{on}}) only while hand layout
 * editing is. A press that never moves stays a click. The click a finished drag fires is swallowed.
 */
var dragOk=false;var press=null;var dragging=false;var swallowClick=false;
/* In SCREEN pixels, the host handles' own threshold: slide pixels shrink with the preview's scale, and a
   click that drifts a few screen pixels must stay a click, not a move and a revision. */
var DRAG_SLOP_SCREEN=4;
function onPress(e){
  swallowClick=false;
  if(!pickOn||pickMode!=='text'||!dragOk||e.button!==0||!(e.detail<=1)||inEdit(e.target))return;
  var t=drillTarget(e.target,!!(e.metaKey||e.ctrlKey));
  /* THE SLIDE'S OWN FRAME never moves (Beka, 2026-09-25), measured as box.frame is (frameOf). A press on it stays a
     click, which selects it. A column that does not fill the slide is not the frame, however alone it stands. */
  press=t&&handable(t)&&!frameOf(t,slideTarget())?{t:t,x:e.clientX,y:e.clientY,sx:e.screenX,sy:e.screenY,id:e.pointerId}:null;
}
function onPressMove(e){
  if(!press||e.pointerId!==press.id)return;
  if(!dragging){
    if(Math.sqrt(Math.pow(e.screenX-press.sx,2)+Math.pow(e.screenY-press.sy,2))<DRAG_SLOP_SCREEN)return;
    dragging=true;
    if(editEl)endEdit(true);
    if(selEl!==press.t){setSel(press.t);postPicked(press.t)}
    setHover(null);
    try{window.getSelection().removeAllRanges()}catch(err){}
    mark(document.documentElement,'data-bds-dragging',true);
    /* The pointer may leave the frame mid-drag; captured, the moves keep coming here. */
    try{document.documentElement.setPointerCapture(e.pointerId)}catch(err){}
    /* FLOW and TOP decide the host's mode at the press (reorder, or free on the frame's top level), and the pick
       above is not rendered out there yet, so the start says them itself. TOP is box.top's rule. */
    var pst=slideTarget();
    window.parent.postMessage({__bds_drag:1,phase:'start',path:pathOf(press.t),x:press.x,y:press.y,flow:!placedFree(press.t),top:!!pst&&press.t.parentNode===pst},'*');
  }
  window.parent.postMessage({__bds_drag:1,phase:'move',x:e.clientX,y:e.clientY,alt:!!e.altKey},'*');
}
function onPressEnd(e){
  if(!press||e.pointerId!==press.id)return;
  var was=dragging;press=null;dragging=false;
  if(!was)return;
  mark(document.documentElement,'data-bds-dragging',false);
  try{document.documentElement.releasePointerCapture(e.pointerId)}catch(err){}
  swallowClick=true;
  window.parent.postMessage({__bds_drag:1,phase:e.type==='pointercancel'?'cancel':'end',x:e.clientX,y:e.clientY,alt:!!e.altKey},'*');
}
/* THE BROWSER'S OWN DRAG of an image or a link would take the pointer first (it starts at its own small
   threshold and cancels the pointer), so an unselected picture or button could never be moved. While a
   press is being watched, it does not start. */
function onNativeDrag(e){
  if(!press&&!dragging)return;
  try{e.preventDefault()}catch(err){}
}
/* A drag whose element stops being the selection (Escape up, the parent's select) is over: say so, so the
   host drops it rather than keeping half a gesture. */
function cancelDrag(){
  if(!dragging)return;
  press=null;dragging=false;
  mark(document.documentElement,'data-bds-dragging',false);
  try{window.parent.postMessage({__bds_drag:1,phase:'cancel'},'*')}catch(err){}
}
function setDragOk(on){
  dragOk=!!on;
  if(!dragOk)cancelDrag();
}
/*
 * FILES FROM OUTSIDE (Beka, 2026-09-25): ⌘V with a picture on the clipboard and a file dragged from the
 * desktop both mean "add this picture to the slide", and focus and the pointer are often in here when they
 * happen. Only the host can upload, so the files go up as they are. Text mode only, and never while a caret
 * is open: a paste into the words is onEditPaste's (plain text), and this capture listener on the document
 * runs before that one on the element, so it must step aside first.
 * The host trusts neither message on its own: it takes a paste only right after a real keypress, and a file
 * drag only raises its overlay, which is where a real drop lands.
 */
var FILES_MAX=10;
function clipFiles(cd){
  var out=[];
  try{
    var fs=cd&&cd.files;
    for(var i=0;fs&&i<fs.length&&out.length<FILES_MAX;i++){
      if(fs[i]&&String(fs[i].type||'').indexOf('image/')===0)out.push(fs[i]);
    }
    /* Some sources carry the picture only as an item (a screenshot, an image copied in a browser). Items
       only when files had none, or a file that is both would go up twice. */
    var its=out.length?null:cd&&cd.items;
    for(var j=0;its&&j<its.length&&out.length<FILES_MAX;j++){
      var it=its[j];
      if(!it||it.kind!=='file'||String(it.type||'').indexOf('image/')!==0)continue;
      var f=it.getAsFile();
      if(f)out.push(f);
    }
  }catch(e){}
  return out;
}
/* THE STAGE'S CLIPBOARD (2026-09-27: copy, cut and paste elements, and text from outside). Clipboard data can
   only be read and written inside the event the browser fires, so that is done here and the host is told:
   - COPY / CUT with an element selected (not the slide) and no caret: the clipboard gets its words as text/plain,
     what another app pastes, and as text/html the same words in a span carrying a token. The host keeps the
     element under that token ({__bds_copy, token, path, cut}); a later paste that brings the token back is that
     element, and one that does not is something copied since, from anywhere.
   - PASTE with no caret: {__bds_paste, files, text, clip, at}. files are pictures, as before; clip is the token
     when the html carries one; text is the plain words; at is where the pointer last was on the slide.
   - beforecopy, beforecut and beforepaste are prevented under the same conditions: WebKit only enables copy and
     paste on content nobody types into once the page says it takes them.
   Never in an open caret, whose words copy and paste as words, and never in a field. */
function clipOk(e){
  if(!pickOn||pickMode!=='text'||editEl)return false;
  var tg=e&&e.target;var tt=tg&&tg.tagName?String(tg.tagName).toUpperCase():'';
  return !(tt==='INPUT'||tt==='TEXTAREA'||tt==='SELECT'||(tg&&tg.isContentEditable));
}
function copyable(){
  return !!selEl&&handable(selEl)&&!frameOf(selEl,slideTarget());
}
var CLIP_WORDS_MAX=20000;
function clipWords(el){
  var t='';
  try{t=String(el.innerText!=null?el.innerText:el.textContent||'')}catch(e){t=''}
  t=t.replace(/[ \\t\\f\\v\\r]+/g,' ').replace(/ *\\n */g,'\\n').trim();
  return t.length>CLIP_WORDS_MAX?t.slice(0,CLIP_WORDS_MAX):t;
}
function onClip(e){
  try{
    if(!clipOk(e)||!copyable())return;
    var cd=e.clipboardData;
    if(!cd)return;
    var token='c'+Date.now().toString(36)+Math.random().toString(36).slice(2,10);
    var words=clipWords(selEl);
    cd.setData('text/plain',words);
    cd.setData('text/html','<span data-deck-clip="'+token+'">'+escAttr(words)+'</span>');
    e.preventDefault();
    window.parent.postMessage({__bds_copy:1,token:token,path:pathOf(selEl),cut:e.type==='cut'},'*');
  }catch(err){}
}
function onBeforeClip(e){
  try{if(clipOk(e)&&(e.type==='beforepaste'||copyable()))e.preventDefault()}catch(err){}
}
var PASTE_TEXT_WIRE_MAX=20000;
function onPaste(e){
  if(!clipOk(e))return;
  var cd=e&&e.clipboardData;
  var fs=clipFiles(cd);
  var html='',text='';
  try{html=cd?String(cd.getData('text/html')||''):''}catch(err){html=''}
  try{text=cd?String(cd.getData('text/plain')||''):''}catch(err){text=''}
  var m=/data-deck-clip="([A-Za-z0-9_-]{1,64})"/.exec(html);
  var clip=m?m[1]:null;
  if(text.length>PASTE_TEXT_WIRE_MAX)text=text.slice(0,PASTE_TEXT_WIRE_MAX);
  if(!fs.length&&!clip&&!text.trim())return;
  try{e.preventDefault()}catch(err){}
  try{window.parent.postMessage({__bds_paste:1,files:fs,text:text,clip:clip,at:lastAt},'*')}catch(err){}
}
/* WHERE THE POINTER LAST WAS on the slide: text pasted in goes there. Asked for by the host when the paste came
   in out there: {__bds_point:{seq}} answers {__bds_pointis, seq, at}, at {x, y, t} or null. */
var lastAt=null;
function notePoint(e){
  try{if(e&&isFinite(e.clientX)&&isFinite(e.clientY))lastAt={x:e.clientX,y:e.clientY,t:Date.now()}}catch(err){}
}
function doPoint(m){
  try{window.parent.postMessage({__bds_pointis:1,seq:m&&m.seq,at:lastAt},'*')}catch(e){}
}
/* THE RIGHT-CLICK MENU (2026-09-27) is the host's to draw. A right-click here selects what a click would, and
   keeps the selection when it lands inside it, then says where: {__bds_menu, x, y, on}, on 'selection' or
   'ground'. Inside an open caret the browser's own menu stays, for its spelling. */
function onContext(e){
  try{
    if(!pickOn||pickMode!=='text')return;
    if(editEl&&inEdit(e.target))return;
    e.preventDefault();
    if(editEl)endEdit(true);
    notePoint(e);
    var t=e.target;
    var inside=!!selEl&&!!t&&t.nodeType===1&&(selEl===t||selEl.contains(t));
    if(!inside){
      var tt=drillTarget(t,!!(e.metaKey||e.ctrlKey));
      if(tt){setSel(tt);postPicked(tt)}
      else if(selEl){setSel(null);postPicked(null)}
    }
    window.parent.postMessage({__bds_menu:1,x:e.clientX,y:e.clientY,on:selEl?'selection':'ground'},'*');
  }catch(err){}
}
function dragHasFiles(dt){
  try{
    var ts=dt&&dt.types;
    if(!ts)return false;
    for(var i=0;i<ts.length;i++){if(ts[i]==='Files')return true}
  }catch(e){}
  return false;
}
/* THE DROP IS THE HOST'S. A file drag over here tells the host to raise its overlay over the frame, and the
   real drop lands on that, in the parent. Default-prevented on the way so the browser shows a copy, and on
   drop so a file let go in here never navigates the frame to itself: a sandboxed frame has no way back. A
   drag fires dragover every few ms, so it is told at most every 100ms. */
var fileDragAt=0;
function onFileDrag(e){
  try{
    if(!pickOn||pickMode!=='text')return;
    var dt=e&&e.dataTransfer;
    if(!dragHasFiles(dt))return;
    e.preventDefault();
    if(e.type==='drop')return;
    try{dt.dropEffect='copy'}catch(err){}
    var now=Date.now();
    if(e.type==='dragover'&&now-fileDragAt<100)return;
    fileDragAt=now;
    window.parent.postMessage({__bds_filedrag:1},'*');
  }catch(err){}
}
function setPick(on,mode){
  var next=mode==='text'?'text':'select';
  /* Re-arming into the OTHER mode has to take effect, so the early-out only stands when the mode is
     the same one. Nothing else in here is idempotent-unsafe: the root marks are set from next. */
  if(on===pickOn&&next===pickMode){if(on)postOverflow();return}
  pickOn=on;
  var prev=pickMode;
  pickMode=next;
  if(on){
    ensurePickStyle();
    if(prev!==next){mark(document.documentElement,'data-bds-pick',false);mark(document.documentElement,'data-bds-text',false)}
    mark(document.documentElement,next==='text'?'data-bds-text':'data-bds-pick',true);
    document.addEventListener('mouseover',onOver,true);
    document.addEventListener('mouseout',onOut,true);
    document.addEventListener('click',onClick,true);
    document.addEventListener('dblclick',onDbl,true);
    document.addEventListener('mousedown',onDown,true);
    document.addEventListener('pointerdown',onPress,true);
    document.addEventListener('pointermove',onPressMove,true);
    document.addEventListener('pointerup',onPressEnd,true);
    document.addEventListener('pointercancel',onPressEnd,true);
    document.addEventListener('dragstart',onNativeDrag,true);
    document.addEventListener('keydown',onSelKey,true);
    document.addEventListener('paste',onPaste,true);
    document.addEventListener('copy',onClip,true);
    document.addEventListener('cut',onClip,true);
    document.addEventListener('beforecopy',onBeforeClip,true);
    document.addEventListener('beforecut',onBeforeClip,true);
    document.addEventListener('beforepaste',onBeforeClip,true);
    document.addEventListener('contextmenu',onContext,true);
    document.addEventListener('pointermove',notePoint,true);
    document.addEventListener('pointerdown',notePoint,true);
    document.addEventListener('dragenter',onFileDrag,true);
    document.addEventListener('dragover',onFileDrag,true);
    document.addEventListener('drop',onFileDrag,true);
    /* One reading on entry so the parent's overflow warning is honest before the first edit, not only
       after one. */
    postOverflow();
  }else{
    try{document.removeEventListener('mouseover',onOver,true)}catch(e){}
    try{document.removeEventListener('mouseout',onOut,true)}catch(e){}
    try{document.removeEventListener('click',onClick,true)}catch(e){}
    try{document.removeEventListener('dblclick',onDbl,true)}catch(e){}
    try{document.removeEventListener('mousedown',onDown,true)}catch(e){}
    try{document.removeEventListener('pointerdown',onPress,true)}catch(e){}
    try{document.removeEventListener('pointermove',onPressMove,true)}catch(e){}
    try{document.removeEventListener('pointerup',onPressEnd,true)}catch(e){}
    try{document.removeEventListener('pointercancel',onPressEnd,true)}catch(e){}
    try{document.removeEventListener('dragstart',onNativeDrag,true)}catch(e){}
    press=null;dragging=false;mark(document.documentElement,'data-bds-dragging',false);
    /* A reorder drag's dim does not outlive the mode that started it. */
    liftOff();
    try{document.removeEventListener('keydown',onSelKey,true)}catch(e){}
    try{document.removeEventListener('paste',onPaste,true)}catch(e){}
    try{document.removeEventListener('copy',onClip,true)}catch(e){}
    try{document.removeEventListener('cut',onClip,true)}catch(e){}
    try{document.removeEventListener('beforecopy',onBeforeClip,true)}catch(e){}
    try{document.removeEventListener('beforecut',onBeforeClip,true)}catch(e){}
    try{document.removeEventListener('beforepaste',onBeforeClip,true)}catch(e){}
    try{document.removeEventListener('contextmenu',onContext,true)}catch(e){}
    try{document.removeEventListener('pointermove',notePoint,true)}catch(e){}
    try{document.removeEventListener('pointerdown',notePoint,true)}catch(e){}
    try{document.removeEventListener('dragenter',onFileDrag,true)}catch(e){}
    try{document.removeEventListener('dragover',onFileDrag,true)}catch(e){}
    try{document.removeEventListener('drop',onFileDrag,true)}catch(e){}
    setHover(null);setSel(null);
    caretSent='';
    /* pickMode already reads the off state's default, so setHover stayed quiet: the parent's line goes too. */
    if(prev==='text'){try{window.parent.postMessage({__bds_hover:1,rect:null},'*')}catch(e){}}
    mark(document.documentElement,'data-bds-pick',false);
    mark(document.documentElement,'data-bds-text',false);
  }
}
function applyStyle(m){
  try{
    /* A LIVE RANGE WINS OVER THE ELEMENT, and the parent asked for that by setting range:true — it
       learned there was one from the last __bds_picked. Checked again HERE rather than trusted, because
       the selection can have collapsed between the message being posted and this running (a click
       inside the words while a menu was open does exactly that), and styling a range that no longer
       exists would wrap nothing while the parent waited for an innerHTML that never came.
       Falls through to the element path on refusal, which is the honest answer: the user pressed a
       control and something has to change. */
    if(m&&m.range&&editEl&&liveRange()){
      var rp=m&&typeof m.prop==='string'?m.prop:'';
      if(rp&&doStyleRange(rp,m.value)){
        scheduleTextCommit();
        postOverflow();
        return;
      }
    }
    var el=nodeAt(m&&m.path);
    var prop=m&&typeof m.prop==='string'?m.prop:'';
    /* THE EMPTY PATH IS BODY, for the two hand values body takes: the slide's background on an artifact with
       no frame to hold it, and isolation when something is sent behind its text (doStack). Everything else
       addressed at [] stays refused, as nodeAt refuses it. */
    if(!el&&m&&m.priority==='important'&&(prop==='background-color'||prop==='isolation')&&m.path&&m.path.length===0)el=document.body;
    if(el&&prop){
      /* setProperty/removeProperty rather than style[prop]: the parent speaks CSS ("font-size"), and
         only the CSSOM property API takes hyphenated names. null REMOVES the inline declaration instead
         of writing an empty one, so the element drops back to whatever the document's own stylesheet
         said — that is the inspector's reset, and it is why preview never has to remember an original. */
      /* HAND EDITS (priority:'important') are layout on the BOX, so they never strip the runs inside it:
         the host skips the same pass for them over the source. */
      var imp=m.priority==='important';
      if(m.value===null||m.value===undefined)el.style.removeProperty(prop);
      else if(imp)el.style.setProperty(prop,String(m.value),'important');
      else el.style.setProperty(prop,String(m.value));
      /* AND IT OVERRIDES THE RUNS INSIDE IT. A word given its own colour sits in a span, and a span's
         inline declaration beats the block's however new the block's is — so restyling the whole
         headline left that word behind, still red, with no way to reach it: an inline run is no longer
         selectable in its own right (see inlineRun), so the block is the ONLY place its style can now
         be changed from. Styling the block therefore has to mean the block, all of it.
         The host makes the same pass over the SOURCE inside applyStyleAtPath, so this stays a preview
         of exactly what gets stored, and it stays ONE write and one undo step. An emptied span is left
         in place on BOTH sides: a path is a chain of element-child indices, and removing one element
         here and not there would renumber the live document out of step with the stored one. */
      if(!imp)clearRunProp(el,prop);
    }
  }catch(e){}
  /* QUIET = a drag frame. postOverflow lays out every element, so frames skip it; the commit answers. */
  if(m&&m.quiet)return;
  /* Always answer, even when the path went stale: the parent's warning must track the document it can
     actually see, and silence would leave a stale number on screen. */
  postOverflow();
  /* A restyle under an open caret moves the words it stands in. */
  if(editEl)postCaret();
}
/* SELECT BY PATH — the same selection a click makes, minus the click. The inspector's breadcrumb walks UP
   from a headline to the card that contains it, and that walk is the whole reason this exists: containers
   own no text of their own, so pickable() refuses them and no cursor can ever land on one. This path
   therefore deliberately does NOT go through pickable/pickTarget. Everything else matches onClick exactly
   — same data-bds-sel attribute, same __bds_picked reply with the same fields — so the panel cannot tell
   a breadcrumb selection from a click, and no consumer needs a second code path. */
function doSelect(m){
  try{
    /* {up:1} = Escape and "Select the parent": the level above, which only this side can work out (a
       same-box wrapper is not one, and the slide's frame is not one either), or nothing at the top. */
    if(m&&m.up){ensurePickStyle();selectUp();return}
    if(m&&m.down){ensurePickStyle();selectDown();return}
    /* {none:1} = a press on the stage around the slide, out in the host (2026-09-27): nothing selected, as
       Escape at the top. A caret still open closes the way a blur closes it, words kept. */
    if(m&&m.none){if(!selEl)return;if(editEl)endEdit(true);setSel(null);postPicked(null);rehover();return}
    var el=nodeAt(m&&m.path);
    if(!el)return;
    /* nodeAt walks DOWN from body, so it cannot hand back html or body today; the refusal is written
       anyway because it is a rule and not an implementation detail. A selected <body> would hand the
       user a delete button pointed at the entire slide. */
    var tn=String(el.tagName||'').toUpperCase();
    if(tn==='HTML'||tn==='BODY')return;
    /* Silence on a miss, never a failure reply: a breadcrumb built before a regeneration is stale, not
       broken, and an answer would make the panel move its selection to something that is not there. */
    /* The selected outline is a rule inside the bridge's own <style>, so the attribute paints nothing
       until that node exists. A select can legally be the FIRST inspector message an iframe ever sees —
       a panel restoring a selection before the pointer has touched the slide — so ensure it here rather
       than assuming pick mode already ran. */
    ensurePickStyle();
    setSel(el);
    postPicked(el);
    rehover();
  }catch(e){}
}
/* REMOVE BY PATH — and here is WHY IT IS DELICATE: a path is a chain of ELEMENT-CHILD INDICES, so pulling
   one node out SHIFTS the index of every later sibling and invalidates every path that points AT or AFTER
   it — the selection the panel is holding, a queued style write, a breadcrumb built two clicks ago. They
   do not fail loudly; they resolve to a DIFFERENT element and the next edit lands on innocent text.
   The rules that follow from that, and must survive any later refactor: this bridge NEVER caches a path
   across a removal (every path is resolved at the instant it is used, once, and thrown away — do not add
   a lookup map "for speed", it would silently rot), and the UI has been told to clear its selection and
   any pending write BEFORE it sends __bds_remove. The ok reply plus the fresh overflow reading are the
   signal to re-read the document, never to resume where it left off. */
function doRemove(m){
  var ok=false;var cleared=false;
  try{
    var path=m&&m.path;
    /* An empty path means "body" — the document itself, not an element in it. nodeAt already answers null
       for it; stating it here keeps the refusal a rule rather than a side effect of someone else's guard. */
    var el=path&&path.length?nodeAt(path):null;
    /* ADDED:1 = Delete on the slide, which takes out a picture the user added and nothing else (Beka,
       2026-09-25). The host has checked its source; the live node is checked too, so a path gone stale
       onto the AI's own content is refused instead of deleting it. */
    if(el&&m.added&&!el.hasAttribute('data-hand-add'))el=null;
    /* HAND:1 = Delete on any selected element (Beka, 2026-09-25), the live mirror of removeHand. Never the
       slide: not the Background's frame nor anything holding it (frameOf, the measured rule removeHand takes
       with the slide's path). Never what a hand edit cannot reach (in a deck-* host or an svg), and never what
       is not a level, a picture the user added excepted: the host cannot see what fills the frame, so this
       refusal is the one that keeps a full-bleed layer. */
    if(el&&m.hand&&(frameOf(el,slideTarget())||!handable(el)||(!levelable(el)&&!el.hasAttribute('data-hand-add'))))el=null;
    var tn=el?String(el.tagName||'').toUpperCase():'';
    /* Never the document's own structure, and never the bridge: the marked <script> IS the only channel
       back to the parent and the marked <style> is what draws the selection, so a container that would
       swallow either one is refused WHOLE rather than taking the bridge down with it. */
    if(el&&tn!=='HTML'&&tn!=='BODY'&&el.parentNode&&!el.hasAttribute('${BRIDGE_MARK}')&&!el.querySelector('[${BRIDGE_MARK}]')){
      /* Ask the containment questions BEFORE the node leaves, while the answers still describe the
         document the parent is looking at. */
      var hitSel=!!(selEl&&(selEl===el||el.contains(selEl)));
      var hitHover=!!(hoverEl&&(hoverEl===el||el.contains(hoverEl)));
      el.parentNode.removeChild(el);
      /* The selection pointed at a node that no longer exists, so the state AND its attributes go with it.
         A dangling selEl would keep a detached subtree alive and would answer the next overflow reading
         with the geometry of something nobody can see. Hover is cleared on the same reasoning. */
      if(hitSel)setSel(null);
      if(hitHover)setHover(null);
      cleared=hitSel;
      ok=true;
    }
  }catch(e){ok=false}
  try{window.parent.postMessage({__bds_removed:1,ok:ok,seq:m&&m.seq},'*')}catch(e){}
  /* THE SELECTION WENT WITH IT, so the parent hears the empty pick a click on the ground posts, and its
     hover line goes too, wherever it stood: the next pointer move draws it again over what is left. Behind
     the answer, so the answer stays first. */
  if(cleared){postPicked(null);setHover(null)}
  /* Order is part of the contract: the removed answer first, then a FRESH overflow reading. Content
     reflows the moment a node leaves — the paragraph that was cropped past 1080 may now fit, or a taller
     one may take its place — so a parent still holding the pre-removal number would be warning about a
     document that no longer exists. A refusal moved nothing, so it gets no overflow. */
  if(ok)postOverflow();
}

/* ---- layout editing: hand edits mirrored live ---- */
/* The host composes every hand edit on the SOURCE first (artifactHand.ts), then mirrors it here, because
   a self-write never re-feeds the frame. So these refuse exactly what the source refuses, or the two drift:
   structure, the bridge, anything INSIDE a deck-* host (the host itself is authored markup) or an svg. */
function handable(el){
  try{
    if(!el||el.nodeType!==1||!el.parentNode)return false;
    var tn=String(el.tagName||'').toUpperCase();
    if(tn==='HTML'||tn==='BODY'||tn==='HEAD'||tn==='SCRIPT'||tn==='STYLE')return false;
    if(el.hasAttribute('${BRIDGE_MARK}')||el.querySelector('[${BRIDGE_MARK}]'))return false;
    for(var up=el.parentElement;up;up=up.parentElement){
      var un=String(up.tagName||'').toUpperCase();
      if(un.indexOf('DECK-')===0||un==='SVG')return false;
    }
    return true;
  }catch(e){return false}
}
/* MARK = stamp data-eid / data-hand live. setAttribute directly: mark() can only write ''. A node that
   already wears a DIFFERENT eid means the path went stale, so refuse. The reply is the confirm read: the
   computed value of every prop named in hand, plus the box, after the preceding __bds_style landed. */
var EID_RE=/^e[0-9]{1,6}$/;
function doMark(m){
  var out={__bds_marked:1,seq:m&&m.seq,ok:false,path:null,rect:null,computed:{}};
  try{
    /* [] = body, the slide with no frame (see applyStyle). The source takes two hand values on body, its
       background and isolation, so a mark carrying anything else is refused here too, or the two would drift. */
    var bodyMark=!!(m&&m.path&&m.path.length===0);
    var el=bodyMark?document.body:nodeAt(m&&m.path);
    var eid=m?m.eid:undefined;var hand=m?m.hand:undefined;
    var eidOk=eid===null||(typeof eid==='string'&&EID_RE.test(eid));
    var handOk=hand===null||(typeof hand==='string'&&(!bodyMark||/^(\\s*(background-color|isolation)\\s*:[^;]*(;|$))+\\s*$/.test(hand)));
    var cur=el?el.getAttribute('data-eid'):null;
    if(el&&(bodyMark||handable(el))&&eidOk&&handOk&&!(eid!==null&&cur&&cur!==eid)){
      if(eid===null)el.removeAttribute('data-eid');else el.setAttribute('data-eid',eid);
      if(hand===null)el.removeAttribute('data-hand');else el.setAttribute('data-hand',hand);
      out.ok=true;
      out.path=pathOf(el);
      if(still)settleAnimations();
      try{
        var cs=getComputedStyle(el);
        var parts=hand===null?[]:hand.split(';');
        for(var i=0;i<parts.length;i++){
          var at=parts[i].indexOf(':');
          if(at<=0)continue;
          var pn=parts[i].slice(0,at).trim();
          if(pn.indexOf('--')!==0)pn=pn.toLowerCase();
          if(pn)out.computed[pn]=String(cs.getPropertyValue(pn)||'').trim();
        }
        var r=el.getBoundingClientRect();
        out.rect={x:r.left,y:r.top,w:r.width,h:r.height};
        out.css=boxCss(cs);
      }catch(e){}
    }
  }catch(e){}
  try{window.parent.postMessage(out,'*')}catch(e){}
  postOverflow();
}
/* MOVE = reorder among ELEMENT siblings, the same algorithm as moveHand over the source, so both land on
   the same index: take el out, insert before the toIndex-th of the rest (or at the end). Every later
   sibling's path shifts, exactly as with remove. insertBefore replays the moved subtree's entrance, so
   settle mode re-settles, and the moved element becomes the selection.
   STEP (Beka, 2026-09-25): {step:±1, flow:1} is an arrow on an element in a container. It goes one flow
   sibling back or on as the eye reads them (a reversed flex runs backwards), and toIndex is that sibling's
   raw index among parent.children, which is exactly moveHand's index into the rest in both directions.
   Refused in an append-only container (a flex order, a grid item placed on a row, areas, a column or dense
   flow; see appendOnly), the element's own placement counted: there the DOM order is not what the eye reads,
   so a step would write a reorder that shows nothing, or goes the wrong way. The drop path refuses the same. */
function doMove(m){
  var ok=false;var np=null;var el=null;var step=false;
  try{
    el=nodeAt(m&&m.path);
    var to=m?m.toIndex:undefined;
    var par=el?el.parentNode:null;
    if(m&&m.flow&&(m.step===1||m.step===-1)){
      step=true;to=undefined;
      if(el&&par&&handable(el)){
        var fk=flowKids(par,el,true);var fi=indexIn(fk,el);
        var sib=fi>=0&&!appendOnly(par,fk)?fk[fi+(flowReversed(par)?-m.step:m.step)]:null;
        if(sib)to=indexIn(par.children,sib);
      }
    }
    if(el&&par&&handable(el)&&typeof to==='number'&&to%1===0){
      var kids=par.children;var others=[];var at=-1;
      for(var i=0;i<kids.length;i++){if(kids[i]===el)at=i;else others.push(kids[i])}
      if(to>=0&&to<=others.length&&at!==to){
        par.insertBefore(el,others[to]||null);
        np=pathOf(el);
        ok=!!np;
      }
    }
  }catch(e){ok=false}
  if(ok&&still)settleAnimations();
  var reply={__bds_moved:1,seq:m&&m.seq,ok:ok,path:ok?np:null};
  if(step&&ok)reply.toIndex=to;
  try{window.parent.postMessage(reply,'*')}catch(e){}
  if(!ok)return;
  try{ensurePickStyle();setSel(el);postPicked(el)}catch(e){}
  postOverflow();
}
/*
 * INSERT = what the user adds by hand, placed on top and free to move like a Figma layer: pictures (Beka,
 * 2026-09-25), and text boxes, circles and squares from the insert dock (2026-09-27). The host has placed and
 * clamped each box in slide pixels; this builds the elements live, and answers the exact declarations it set
 * so the host writes the same ones into the source (insertHandElements).
 *   - createElement, setAttribute and textContent, never markup: nothing a message carries is parsed. A
 *     picture's src is a data: URI in here, because the sandboxed frame cannot fetch storage; the source
 *     keeps the stored URL. A text box's words are text; its type is plain inline (ADDED_TEXT_STYLE), not a
 *     hand value, and it has no height: it grows with its words, and with no width it grows as they are typed.
 *   - A shape is an empty div: its fill (the deck's accent) and a circle's 50% corners are hand values.
 *   - The parent is insertParent, the source's insert parent mirrored, NOT slideTarget: that one takes a
 *     full-bleed layer that holds elements (an svg backdrop, a photo wrapper) as the slide, and a picture put
 *     in there is not rendered, or is drawn under the content. On body the pictures go in FRONT of the
 *     bridge's own scripts, which the source does not have, or the live paths would not be the source's.
 *   - Measured, not computed, as a detach is: at left:0 / top:0 the picture sits on its containing block's
 *     padding-box corner, so its rect there is the offset to take off the box. The pictures' own finite
 *     animations are finished first, settle mode or not, so what is measured is where they come to rest.
 *   - One z-index above the highest positive one among the parent's positioned elements and flex or grid
 *     items (which stack without a position), or none at all.
 *   - NOT MARKED AND NOT PICKED. A pick made before the marks exist would strip them on the first resize, so
 *     the host composes the source, marks with __bds_mark, then selects with __bds_select.
 * All or nothing: one bad item refuses the batch, and a failure halfway takes back what went in.
 */
var INSERT_MAX=10;
var INSERT_ID_RE=/^[A-Za-z0-9_-]{1,128}$/;
function insertSrcOk(s){
  if(typeof s!=='string')return false;
  if(s.indexOf('data:image/')===0)return true;
  return s.replace(/^https?:\\/\\/[^\\/?#\\s]+/i,'').indexOf('/api/v1/storage/')===0;
}
function finiteNum(v){return typeof v==='number'&&isFinite(v)}
var ADDED_TEXT_STYLE=${JSON.stringify(ADDED_TEXT_STYLE)};
var ADDED_TEXT_MAX=${ADDED_TEXT_MAX};
var PASTED_TEXT_MAX=${PASTED_TEXT_MAX};
var ADDED_FILL=${JSON.stringify(ADDED_SHAPE_FILL)};
var ADDED_ROUND=${JSON.stringify(ADDED_ROUND)};
function insertKind(it){return it.kind==='text'?'text':it.kind==='shape'?'shape':'image'}
function insertItemOk(it){
  if(!it||typeof it!=='object')return false;
  if(it.kind!==undefined&&it.kind!=='image'&&it.kind!=='text'&&it.kind!=='shape')return false;
  var kind=insertKind(it);
  var b=it.box;
  if(!b||!finiteNum(b.x)||!finiteNum(b.y))return false;
  if(kind==='text'){
    /* Empty is allowed: the insert dock's text box opens empty, a caret and nothing else (Birtvy's text tool),
       and reaches the source only once words were typed into it. */
    if(typeof it.text!=='string')return false;
    /* Words pasted in from outside (paste:1) come with the box, lines and all, up to PASTED_TEXT_MAX. */
    if(it.paste===1){if(it.text.length>PASTED_TEXT_MAX||!it.text.trim())return false}
    else if(it.text.trim().length>ADDED_TEXT_MAX)return false;
    return b.w===undefined||b.w===null||(finiteNum(b.w)&&b.w>=1);
  }
  if(!finiteNum(b.w)||!finiteNum(b.h)||b.w<1||b.h<1)return false;
  if(kind==='shape')return it.round===undefined||typeof it.round==='boolean';
  if(typeof it.id!=='string'||!INSERT_ID_RE.test(it.id))return false;
  return insertSrcOk(it.src)&&typeof it.alt==='string';
}
/* CHANGING A PICTURE (__bds_imgsrc), the live mirror of replaceHandImage (artifactHand.ts): the <img> at path,
   already a deck picture, takes another picture's id, bytes and alt. Its box, its fit and its marks stay, so
   the new picture fills the frame the old one had. Refused, and nothing touched, for anything else. */
function doImgSrc(m){
  var ok=false;
  try{
    var el=nodeAt(m&&m.path);
    var tn=el?String(el.tagName||'').toUpperCase():'';
    if(el&&tn==='IMG'&&handable(el)&&INSERT_ID_RE.test(el.getAttribute('data-deck-image')||'')
      &&typeof m.id==='string'&&INSERT_ID_RE.test(m.id)&&insertSrcOk(m.src)&&typeof m.alt==='string'){
      el.setAttribute('data-deck-image',m.id);
      el.setAttribute('src',m.src);
      el.setAttribute('alt',m.alt.slice(0,200));
      ok=true;
    }
  }catch(e){ok=false}
  try{window.parent.postMessage({__bds_imgset:1,seq:m&&m.seq,ok:ok},'*')}catch(e){}
}
/*
 * WHERE A PICTURE ADDED BY HAND GOES: the twin of the source's insert parent (artifactHand.ts), so parentPath
 * resolves to the same element there. Body, then down into the one content child that is left once the
 * pictures already added are passed over, while that child holds elements, takes hand edits, and is neither
 * an svg nor a deck-* host (an HTML <img> in an svg never renders, and the source cannot hold one in a
 * component). No fill test and no text test: the source can measure neither, and a childless layer beside
 * the frame (grain, a glow) stops the chain at body, the safe side.
 */
function insertParent(){
  var n=document.body;
  if(!n)return null;
  for(;;){
    var ks=contentKids(n),only=null,count=0;
    for(var i=0;i<ks.length;i++){
      if(ks[i].hasAttribute('data-hand-add'))continue;
      only=ks[i];count++;
    }
    if(count!==1||only.children.length===0||!handable(only))break;
    var tn=String(only.tagName||'').toUpperCase();
    if(tn==='SVG'||tn.indexOf('DECK-')===0)break;
    n=only;
  }
  return n;
}
/* The highest positive z-index among par's descendants that stack, leaving out skip and the bridge. A static
   element stacks only as a flex or grid item: its z-index applies without a position there. */
function topZ(par,skip){
  var max=0;
  try{
    var all=par.querySelectorAll('*');
    for(var i=0;i<all.length;i++){
      var d=all[i];
      if(indexIn(skip,d)>=0||d.hasAttribute('${BRIDGE_MARK}'))continue;
      var cs=getComputedStyle(d);
      var pos=String(cs.position||'');
      if(!pos||pos==='static'){
        var pd='';
        try{pd=d.parentElement?String(getComputedStyle(d.parentElement).display||''):''}catch(e2){}
        if(pd!=='flex'&&pd!=='inline-flex'&&pd!=='grid'&&pd!=='inline-grid')continue;
      }
      var z=parseInt(cs.zIndex,10);
      if(isFinite(z)&&z>max)max=z;
    }
  }catch(e){}
  return max;
}
function doInsert(m){
  var items=m&&m.items;
  var els=[];var ok=false;var parentPath=null;var paths=null;var decls=null;
  try{
    var valid=Array.isArray(items)&&items.length>=1&&items.length<=INSERT_MAX;
    for(var v=0;valid&&v<items.length;v++){if(!insertItemOk(items[v]))valid=false}
    var body=document.body;
    var par=valid&&body?insertParent():null;
    parentPath=par?(par===body?[]:pathOf(par)):null;
    if(par&&parentPath){
      var before=null;
      if(par===body){
        var bk=body.children;
        for(var b=0;b<bk.length;b++){if(bk[b].hasAttribute('${BRIDGE_MARK}')){before=bk[b];break}}
      }
      var sizes=[];
      for(var i=0;i<items.length;i++){
        var it=items[i];var kind=insertKind(it);var el;
        var wh=kind==='text'
          ?{w:finiteNum(it.box.w)?Math.round(it.box.w)+'px':null,h:null}
          :{w:Math.round(it.box.w)+'px',h:Math.round(it.box.h)+'px'};
        if(kind==='image'){
          el=document.createElement('img');
          el.setAttribute('data-deck-image',it.id);
          el.setAttribute('src',it.src);
          el.setAttribute('alt',it.alt.slice(0,200));
        }else{
          el=document.createElement('div');
          if(kind==='text'&&it.paste===1){
            /* A line each, a <br> between, exactly as the source writes them (the host's html). */
            var ln=it.text.split('\\n');
            for(var li=0;li<ln.length;li++){
              if(li)el.appendChild(document.createElement('br'));
              if(ln[li])el.appendChild(document.createTextNode(ln[li]));
            }
          }else if(kind==='text')el.textContent=it.text.trim();
        }
        el.setAttribute('data-hand-add','');
        el.style.setProperty('position','absolute','important');
        el.style.setProperty('left','0px','important');
        el.style.setProperty('top','0px','important');
        if(wh.w)el.style.setProperty('width',wh.w,'important');
        if(wh.h)el.style.setProperty('height',wh.h,'important');
        if(kind==='image')el.style.setProperty('object-fit','cover','important');
        if(kind==='shape'){
          el.style.setProperty('background-color',ADDED_FILL,'important');
          if(it.round)el.style.setProperty('border-radius',ADDED_ROUND,'important');
        }
        if(kind==='text'){
          for(var ts=0;ts<ADDED_TEXT_STYLE.length;ts++)el.style.setProperty(ADDED_TEXT_STYLE[ts][0],ADDED_TEXT_STYLE[ts][1]);
          /* An empty box is one line tall, in here only, so the caret opened in it has a line to stand on. The
             source never gets it: the box reaches it with words, which give it its height. */
          if(!it.text.trim())el.style.setProperty('min-height','1lh');
        }
        par.insertBefore(el,before);
        els.push(el);
        sizes.push(wh);
      }
      /* Settled before the probe: an entrance the stylesheet gives every img would otherwise be measured
         mid-flight, and its offset baked into left and top. The pictures' own entrance is finished even with
         settle mode off, which is the usual case: the host arms it only while something is selected.
         getAnimations() is also what makes the engine create a CSS animation on a node this new. */
      for(var s=0;s<els.length;s++){
        try{
          var ia=els[s].getAnimations?els[s].getAnimations():[];
          for(var q=0;q<ia.length;q++){
            var tm=ia[q].effect&&ia[q].effect.getTiming?ia[q].effect.getTiming():null;
            if(tm&&tm.iterations!==Infinity){try{ia[q].finish()}catch(e2){}}
          }
        }catch(e1){}
      }
      if(still)settleAnimations();
      /* ALWAYS a z of its own (2026-09-27), one above the highest in there, 1 when nothing stacks: two added
         elements at auto would tie, and the layer order steps past a tie only by jumping behind the text. */
      var z=topZ(par,els);
      var zv=String(Math.max(z,0)+1);
      paths=[];decls=[];
      for(var j=0;j<els.length;j++){
        var r=els[j].getBoundingClientRect();
        if(!finiteNum(r.left)||!finiteNum(r.top))throw new Error('probe');
        var bx=items[j].box;var kj=insertKind(items[j]);
        var lx=Math.round(bx.x-r.left)+'px',ty=Math.round(bx.y-r.top)+'px';
        els[j].style.setProperty('left',lx,'important');
        els[j].style.setProperty('top',ty,'important');
        var ds=[{prop:'position',value:'absolute'},{prop:'left',value:lx},{prop:'top',value:ty}];
        if(sizes[j].w)ds.push({prop:'width',value:sizes[j].w});
        if(sizes[j].h)ds.push({prop:'height',value:sizes[j].h});
        if(kj==='image')ds.push({prop:'object-fit',value:'cover'});
        if(kj==='shape'){
          ds.push({prop:'background-color',value:ADDED_FILL});
          if(items[j].round)ds.push({prop:'border-radius',value:ADDED_ROUND});
        }
        els[j].style.setProperty('z-index',zv,'important');ds.push({prop:'z-index',value:zv});
        var p=pathOf(els[j]);
        if(!p)throw new Error('path');
        paths.push(p);decls.push(ds);
      }
      ok=true;
    }
  }catch(e){ok=false}
  if(!ok){
    for(var k=0;k<els.length;k++){try{if(els[k].parentNode)els[k].parentNode.removeChild(els[k])}catch(e){}}
  }
  try{window.parent.postMessage({__bds_inserted:1,seq:m&&m.seq,ok:ok,parentPath:ok?parentPath:null,paths:ok?paths:null,decls:ok?decls:null},'*')}catch(e){}
  if(ok)postOverflow();
}
/* A COPY GOES IN (__bds_graft), the live half of a paste or a duplicate (2026-09-27). The host composed the
   source first (pasteElementAt, artifactHand.ts) and sends exactly the element it put there, fresh name tags and
   all, and where: its parent and the child it went before (null: last, in front of the bridge's own scripts on
   body), both read before the insert, as this document stands now. So the two stay one tree. Scrubbed again,
   since the markup crossed a boundary: no script, no handler, no javascript: URL, no bridge mark, no caret.
   Nothing is marked or picked: the host selects once it has written. Answers the copy's path and box, and z: one
   above the highest in its parent, for a copy that has to go on top. */
var GRAFT_MAX=120000;
function scrubGraft(root){
  var ss=root.querySelectorAll('script');
  for(var i=ss.length-1;i>=0;i--){try{ss[i].parentNode.removeChild(ss[i])}catch(e){}}
  var all=[root];var inner=root.querySelectorAll('*');
  for(var j=0;j<inner.length;j++)all.push(inner[j]);
  for(var k=0;k<all.length;k++){
    var at=all[k].attributes;
    for(var a=at.length-1;a>=0;a--){
      var n=String(at[a].name||'').toLowerCase();
      var v=String(at[a].value||'');
      var url=n==='href'||n==='src'||n==='xlink:href'||n==='action'||n==='formaction'||n==='poster';
      if(n.indexOf('on')===0||n.indexOf('data-bds')===0||n==='contenteditable'||(url&&/^\\s*javascript:/i.test(v))){
        try{all[k].removeAttribute(at[a].name)}catch(e){}
      }
    }
  }
}
function doGraft(m){
  var out={__bds_grafted:1,seq:m&&m.seq,ok:false,path:null,rect:null,z:null};
  var el=null;
  try{
    var body=document.body;
    var html=m&&typeof m.html==='string'?m.html:'';
    var pp=m&&m.parentPath;
    var par=Array.isArray(pp)?(pp.length===0?body:nodeAt(pp)):null;
    var bp=m?m.beforePath:undefined;
    var before=bp===null?null:nodeAt(bp);
    var parOk=!!par&&(par===body||(handable(par)&&boxTag(par)));
    var beforeOk=bp===null||(!!before&&before.parentNode===par&&!before.hasAttribute('${BRIDGE_MARK}'));
    if(html&&html.length<=GRAFT_MAX&&parOk&&beforeOk){
      var t=document.createElement('template');
      t.innerHTML=html;
      var kids=t.content.childNodes;var node=null;var count=0;
      for(var i=0;i<kids.length;i++){
        var kn=kids[i];
        if(kn.nodeType===3&&!String(kn.textContent||'').trim())continue;
        count++;node=kn;
      }
      if(count===1&&node&&node.nodeType===1){
        el=document.importNode(node,true);
        scrubGraft(el);
        var ref=before;
        if(!ref&&par===body){
          var bk=body.children;
          for(var b=0;b<bk.length;b++){if(bk[b].hasAttribute('${BRIDGE_MARK}')){ref=bk[b];break}}
        }
        par.insertBefore(el,ref);
        /* Its own entrance finished, as an insert's is, so the box answered is where it rests. */
        try{
          var an=el.getAnimations?el.getAnimations({subtree:true}):[];
          for(var q=0;q<an.length;q++){
            var tm=an[q].effect&&an[q].effect.getTiming?an[q].effect.getTiming():null;
            if(tm&&tm.iterations!==Infinity){try{an[q].finish()}catch(e2){}}
          }
        }catch(e1){}
        if(still)settleAnimations();
        var p=pathOf(el);
        if(!p)throw new Error('path');
        var r=el.getBoundingClientRect();
        out.ok=true;out.path=p;out.rect={x:r.left,y:r.top,w:r.width,h:r.height};
        out.z=topZ(par,[el])+1;
      }
    }
  }catch(e){
    out={__bds_grafted:1,seq:m&&m.seq,ok:false,path:null,rect:null,z:null};
    if(el&&el.parentNode){try{el.parentNode.removeChild(el)}catch(e3){}}
  }
  try{window.parent.postMessage(out,'*')}catch(e){}
  if(out.ok)postOverflow();
}
/* THE LAYER ORDER (__bds_stack), for Bring forward, Send backward, Bring to front and Send to back (2026-09-27), on
   a FREE element only (absolute or fixed): one in a layout is ordered by its layout. Answers {__bds_stackis, seq,
   ok, free, front, forward, backward, back, isolate}: the z-index each would set, or null where it would change
   nothing or may not.
   The picture is the element's stacking context: what paints in it (positioned, or a flex or grid item with a
   z-index, and not inside a nested context) at its z, auto counting as 0, in paint order, z then document order;
   the parent's in-flow content is one layer between the negative ones and 0. Only what overlaps the element
   counts, so each press changes what the eye sees. Each answer moves the element alone: past the next overlapping
   layer (forward, backward) or past all of them (front, back).
   A negative answer puts it under the parent's in-flow content. That needs the parent to be its stacking context,
   or it would sink behind the parent's own background too: isolate names the parent to be given
   isolation:isolate when it is not one already, [] for body (a slide with no frame, where added elements go). */
function makesContext(el){
  try{
    if(el===document.documentElement)return true;
    var cs=getComputedStyle(el);
    var pos=String(cs.position||'static');var z=String(cs.zIndex||'auto');
    if(pos!=='static'&&z!=='auto')return true;
    if(pos==='fixed'||pos==='sticky')return true;
    if(parseFloat(cs.opacity)<1)return true;
    var set=function(v){return !!v&&v!=='none'};
    if(set(cs.transform)||set(cs.filter)||set(cs.perspective)||set(cs.clipPath))return true;
    if(set(cs.backdropFilter||cs.webkitBackdropFilter)||set(cs.maskImage||cs.webkitMaskImage))return true;
    if(String(cs.isolation||'')==='isolate')return true;
    if(cs.mixBlendMode&&cs.mixBlendMode!=='normal')return true;
    if(/paint|layout|strict|content/.test(String(cs.contain||'')))return true;
    if(/transform|opacity|filter|z-index|isolation|mask|clip-path|perspective/.test(String(cs.willChange||'')))return true;
    if(z!=='auto'&&el.parentElement&&/flex|grid/.test(String(getComputedStyle(el.parentElement).display||'')))return true;
  }catch(e){}
  return false;
}
function stackRoot(el){
  for(var up=el.parentElement;up;up=up.parentElement){if(makesContext(up))return up}
  return document.documentElement;
}
function zNum(el){var z=parseInt(getComputedStyle(el).zIndex,10);return isFinite(z)?z:0}
function stacksAtZ(el){
  var cs=getComputedStyle(el);
  if(String(cs.position||'static')!=='static')return true;
  if(String(cs.zIndex||'auto')==='auto')return false;
  var pd=el.parentElement?String(getComputedStyle(el.parentElement).display||''):'';
  return /flex|grid/.test(pd);
}
function stackRect(el){var r=el.getBoundingClientRect();return {x:r.left,y:r.top,w:r.width,h:r.height}}
function overlap(a,b){return a.w>0&&a.h>0&&b.w>0&&b.h>0&&a.x<b.x+b.w&&b.x<a.x+a.w&&a.y<b.y+b.h&&b.y<a.y+a.h}
/* a paints under b: z first (the in-flow layer at -0.5), then document order. */
function under(a,b){return a.z<b.z||(a.z===b.z&&a.i<b.i)}
/* The integer z that puts me (document index i) strictly between lo and hi, either of which may be missing: the
   lowest such z going up, the highest going down. Null when no z does it without moving anything else. */
function zBetween(lo,hi,i,up){
  var a=lo?Math.floor(lo.z):hi?Math.ceil(hi.z)-3:-1;
  var b=hi?Math.ceil(hi.z):lo?Math.floor(lo.z)+3:1;
  var best=null;
  for(var z=a-1;z<=b+1;z++){
    var me={z:z,i:i};
    if((lo&&!under(lo,me))||(hi&&!under(me,hi)))continue;
    if(best===null||(up?z<best:z>best))best=z;
  }
  return best;
}
function doStack(m){
  var out={__bds_stackis:1,seq:m&&m.seq,ok:false,free:false,front:null,forward:null,backward:null,back:null,isolate:null};
  try{
    var el=nodeAt(m&&m.path);
    if(el&&handable(el)){
      out.ok=true;
      var pos=String(getComputedStyle(el).position||'');
      if(pos==='absolute'||pos==='fixed'){
        out.free=true;
        var root=stackRoot(el);
        var mine=stackRect(el);
        var all=root.getElementsByTagName('*');
        var me=null;var layers=[];
        for(var i=0;i<all.length;i++){
          var d=all[i];
          if(d===el){me={z:zNum(el),i:i};continue}
          if(el.contains(d)||d.contains(el)||inBridge(d))continue;
          if(!stacksAtZ(d)||stackRoot(d)!==root)continue;
          if(overlap(mine,stackRect(d)))layers.push({z:zNum(d),i:i});
        }
        var par=el.parentElement;
        if(par){
          var pk=par.children;
          for(var f=0;f<pk.length;f++){
            var c=pk[f];
            if(c===el||inBridge(c))continue;
            if(String(getComputedStyle(c).position||'static')!=='static')continue;
            if(overlap(mine,stackRect(c))){layers.push({z:-0.5,i:-1});break}
          }
        }
        if(me){
          var above=[],below=[];
          for(var j=0;j<layers.length;j++)(under(me,layers[j])?above:below).push(layers[j]);
          var order=function(x,y){return under(x,y)?-1:1};
          above.sort(order);below.sort(order);
          var negOk=!!par;
          var keep=function(z){return z===null||z===me.z||(z<0&&!negOk)?null:z};
          if(above.length){
            out.forward=keep(zBetween(above[0],above[1]||null,me.i,true));
            out.front=keep(zBetween(above[above.length-1],null,me.i,true));
          }
          if(below.length){
            var n=below.length;
            out.backward=keep(zBetween(below[n-2]||null,below[n-1],me.i,false));
            out.back=keep(zBetween(null,below[0],me.i,false));
          }
          if(negOk&&!makesContext(par))out.isolate=pathOf(par);
        }
      }
    }
  }catch(e){out.ok=false}
  try{window.parent.postMessage(out,'*')}catch(e){}
}
/* SETTLE MODE (__bds_settle). While the layout editor is on, the page rests at its END state so a drag
   measures what rests, not a frame of an entrance: finish() finite animations (animation:none would drop
   the fill-both end), pause loops, kill transitions. An entrance that starts later is caught on
   animationstart. Off reverses all of it but never un-finishes. */
function onStillAnim(e){
  try{
    var t=e&&e.target;
    if(!still||!t||!t.getAnimations)return;
    var as=t.getAnimations({subtree:true});
    for(var i=0;i<as.length;i++){
      var tm=as[i].effect&&as[i].effect.getTiming?as[i].effect.getTiming():null;
      if(!tm)continue;
      if(tm.iterations===Infinity){try{as[i].pause()}catch(e2){}}
      else{try{as[i].finish()}catch(e2){}}
    }
  }catch(err){}
}
function setStill(on){
  try{
    still=!!on;
    if(still){
      ensurePickStyle();
      mark(document.documentElement,'data-bds-still',true);
      settleAnimations();
      applyFreeze();
      document.addEventListener('animationstart',onStillAnim,true);
    }else{
      try{document.removeEventListener('animationstart',onStillAnim,true)}catch(e){}
      mark(document.documentElement,'data-bds-still',false);
      applyFreeze();
    }
  }catch(e){}
}

/* ---- reorder: the drop slot, the move, the lift ---- */
/*
 * AN ELEMENT IN A CONTAINER MOVES TO A SLOT, NEVER TO A FREE OFFSET (Beka, 2026-09-25). A flex row, a grid and
 * a stack of blocks lay their children out in order, so dragging one reorders it, or moves it into another
 * container, like Figma. The host asks what a drop at a point would do (__bds_dropat, at most once a frame),
 * draws the line and the target's outline from the answer, and on release composes the source (moveHandTo) and
 * sends __bds_moveto. Elements placed free (absolute, fixed) never come here: the host drags them as before.
 */
function placedFree(el){
  try{var ps=String(getComputedStyle(el).position||'');return ps==='absolute'||ps==='fixed'}catch(e){return false}
}
function flowish(el){
  try{
    var cs=getComputedStyle(el);var ps=String(cs.position||'');
    return String(cs.display||'')!=='none'&&ps!=='absolute'&&ps!=='fixed';
  }catch(e){return false}
}
/* FLOW CHILDREN: what a container lays out in order. Never structure, the bridge, a picture added by hand, or
   what is hidden or placed free; the dragged element only when keep says to count it. */
function flowKids(c,drag,keep){
  var out=[];var ks=(c&&c.children)||[];
  for(var i=0;i<ks.length;i++){
    var k=ks[i];
    if(k===drag){if(keep)out.push(k);continue}
    if(NON_CONTENT[String(k.tagName||'').toUpperCase()]||k.hasAttribute('${BRIDGE_MARK}')||k.hasAttribute('data-hand-add'))continue;
    if(flowish(k))out.push(k);
  }
  return out;
}
/* A reversed flex shows its children against their DOM order: what the eye calls before, the DOM calls after. */
function flowReversed(c){
  try{
    var cs=getComputedStyle(c);var d=String(cs.display||'');
    return (d==='flex'||d==='inline-flex')&&String(cs.flexDirection||'').indexOf('-reverse')>0;
  }catch(e){return false}
}
/* AXIS: a = 'x' or 'y' for a flow on one line, '2d' for one that wraps; main = the axis a 2d flow fills first;
   rev = a reversed flex. A block is a column, unless everything in it flows inline (inline-block chips, floats,
   and plain inline boxes, which is what an img or an svg is by default: a logo strip is a row), and then it is
   rows. A table row is a row of cells.
   A GRID is two-dimensional unless it is a column: its children each start at or below the one before's bottom
   (measured, so centred or justified items count), or, with fewer than two to measure, it has one column track.
   A plain display:grid stack with a gap is the common case, and must slot and band along y like a flex column. */
var INLINE_BOX={'inline':1,'inline-block':1,'inline-flex':1,'inline-grid':1};
function axisOf(c,drag){
  var cs=getComputedStyle(c);var d=String(cs.display||'');
  if(d==='flex'||d==='inline-flex'){
    var fd=String(cs.flexDirection||'');var fw=String(cs.flexWrap||'');
    var main=fd.indexOf('column')===0?'y':'x';
    return {a:fw==='wrap'||fw==='wrap-reverse'?'2d':main,main:main,rev:fd.indexOf('-reverse')>0};
  }
  if(d==='grid'||d==='inline-grid'){
    var gk=flowKids(c,drag,false);var col=gk.length>1;
    for(var g=1;col&&g<gk.length;g++){if(boxR(gk[g]).t<boxR(gk[g-1]).b-1)col=false}
    if(gk.length<2){
      var gt=String(cs.gridTemplateColumns||'').replace(/\\[[^\\]]*\\]/g,'').trim();
      col=!gt||gt==='none'||gt.split(/\\s+/).length===1;
    }
    return col?{a:'y',main:'y',rev:false}:{a:'2d',main:'x',rev:false};
  }
  if(d==='table-row')return {a:'x',main:'x',rev:false};
  var ks=flowKids(c,drag,false);var rows=ks.length>0;
  for(var i=0;rows&&i<ks.length;i++){
    var kc=getComputedStyle(ks[i]);var fl=String(kc.cssFloat||kc.getPropertyValue('float')||'');
    if(!INLINE_BOX[String(kc.display||'')]&&fl!=='left'&&fl!=='right'&&fl!=='inline-start'&&fl!=='inline-end')rows=false;
  }
  return rows?{a:'2d',main:'x',rev:false}:{a:'y',main:'y',rev:false};
}
/* APPEND-ONLY: a container whose visual order is not its DOM order (a flex or grid order, an item placed on a
   grid row, areas, a column or dense grid flow). No slot in there could be drawn honestly, so a drop there goes
   to the end, and an arrow step is refused.
   A span is not a placement (a bento card on span 2 is still auto-placed in DOM order), and neither is a
   column alone: in a sparse row flow an item with a definite column and an auto row takes the cursor's next
   row that fits, in DOM order (a full-width header on 1 / -1). A line on the ROW, start or end, is what
   takes an item out of order: those are placed before everything auto. */
function placedLine(v){v=String(v||'').trim();return !!v&&v!=='auto'&&v.indexOf('span')!==0}
function appendOnly(c,kids){
  var cs=getComputedStyle(c);var d=String(cs.display||'');
  var grid=d==='grid'||d==='inline-grid';
  if(grid){
    var ta=String(cs.gridTemplateAreas||'').trim();var af=String(cs.gridAutoFlow||'');
    if((ta&&ta!=='none')||af.indexOf('column')>=0||af.indexOf('dense')>=0)return true;
  }
  for(var i=0;i<kids.length;i++){
    var kc=getComputedStyle(kids[i]);
    var o=parseFloat(kc.order);
    if(o&&isFinite(o))return true;
    if(grid&&(placedLine(kc.gridRowStart)||placedLine(kc.gridRowEnd)))return true;
  }
  return false;
}
/*
 * A CONTAINER a drop can go into: a box that lays children out and survives the source's round trip.
 *   - hand-editable, or body when body is the end (see dropSlot);
 *   - not a run, a text block, a control, a replaced element, an svg or a component (DROP_SHUT, INLINE_TAGS,
 *     deck-*), and not inside a P, an A, a BUTTON or a FORM: a box in there would not parse back where it went;
 *   - list items only into something that is not itself a list item or inside one, up to the nearest list
 *     (UL, OL, MENU, DL, counting n itself): the parser closes an open li only as far as that, so a sublist
 *     inside an li takes its own items;
 *   - block, flex, grid (inline too), flow-root or list-item, never a table part (a table part itself only
 *     moves among its own siblings: see dropSlot);
 *   - holding an element already, or flex or grid, which lay out one child as well as ten; never a childless box
 *     placed free;
 *   - not the dragged element's own same-shape sibling (same tag, same classes): a card over a card is before
 *     or after it (dropSlot keeps the walk out of its subtree too).
 */
var DROP_SHUT={P:1,H1:1,H2:1,H3:1,H4:1,H5:1,H6:1,BUTTON:1,LABEL:1,LEGEND:1,SUMMARY:1,CAPTION:1,OPTION:1,SELECT:1,TEXTAREA:1,IMG:1,VIDEO:1,AUDIO:1,CANVAS:1,IFRAME:1,OBJECT:1,EMBED:1,PICTURE:1,SVG:1,MATH:1,TEMPLATE:1,NOSCRIPT:1,HR:1,BR:1,INPUT:1};
var DROP_DISPLAY={block:1,flex:1,grid:1,'inline-flex':1,'inline-grid':1,'flow-root':1,'list-item':1};
var LIST_ITEM={LI:1,DT:1,DD:1};
var LIST_STOP={UL:1,OL:1,MENU:1,DL:1};
var TABLE_PART={TR:1,TD:1,TH:1,THEAD:1,TBODY:1,TFOOT:1,CAPTION:1,COLGROUP:1,COL:1};
function tablePart(el){return !!TABLE_PART[String(el.tagName||'').toUpperCase()]}
function boxTag(n){
  var tn=String(n.tagName||'').toUpperCase();
  return !INLINE_TAGS[tn]&&!DROP_SHUT[tn]&&tn.indexOf('DECK-')!==0;
}
function classKey(el){return String(el.getAttribute('class')||'').trim().split(/\\s+/).sort().join(' ')}
function sameShape(n,drag){
  return n!==drag&&n.parentNode===drag.parentNode&&String(n.tagName||'').toUpperCase()===String(drag.tagName||'').toUpperCase()
    &&classKey(n)===classKey(drag)&&flowish(n);
}
function dropContainer(n,drag,end){
  try{
    if(!n||n.nodeType!==1||n===drag)return false;
    var body=document.body;
    if(n===body){if(n!==end)return false}
    else if(!handable(n))return false;
    if(!boxTag(n))return false;
    var li=!!LIST_ITEM[String(drag.tagName||'').toUpperCase()];
    var tn=String(n.tagName||'').toUpperCase();
    if(li&&LIST_ITEM[tn])return false;
    /* A list ends the parser's li / dd / dt close scan: past one, an enclosing list item is safe. */
    var liOpen=li&&!LIST_STOP[tn];
    for(var up=n.parentElement;up&&up!==body;up=up.parentElement){
      var un=String(up.tagName||'').toUpperCase();
      if(un==='P'||un==='A'||un==='BUTTON'||un==='FORM'||(liOpen&&LIST_ITEM[un]))return false;
      if(LIST_STOP[un])liOpen=false;
    }
    var cs=getComputedStyle(n);var d=String(cs.display||'');
    if(!DROP_DISPLAY[d])return false;
    var kids=kidCount(n);
    if(!kids&&d!=='flex'&&d!=='grid'&&d!=='inline-flex'&&d!=='inline-grid')return false;
    var ps=String(cs.position||'');
    if(!kids&&(ps==='absolute'||ps==='fixed'))return false;
    return !sameShape(n,drag);
  }catch(e){return false}
}
/* THE HIT: the topmost element under the point that is not the dragged element or inside it, and not the
   bridge. elementsFromPoint answers through the dragged element; without it the dragged subtree is made
   transparent to the pointer for the one reading. */
function inBridge(n){
  for(var u=n;u&&u.nodeType===1;u=u.parentNode){if(u.hasAttribute('${BRIDGE_MARK}'))return true}
  return false;
}
function dropHit(x,y,drag){
  var h=null;
  if(typeof document.elementsFromPoint==='function'){
    var all=document.elementsFromPoint(x,y)||[];
    for(var i=0;i<all.length;i++){
      h=all[i];
      if(h&&h.nodeType===1&&h!==drag&&!drag.contains(h)&&!inBridge(h))return h;
    }
    return null;
  }
  ensurePickStyle();
  mark(drag,'data-bds-nohit',true);
  try{h=document.elementFromPoint(x,y)}finally{mark(drag,'data-bds-nohit',false)}
  return h&&h.nodeType===1&&h!==drag&&!drag.contains(h)&&!inBridge(h)?h:null;
}
function boxR(el){
  var q=el.getBoundingClientRect();
  return {l:q.left,t:q.top,r:q.left+q.width,b:q.top+q.height,w:q.width,h:q.height};
}
/* The content box's left edge and width: what a level line across a column spans. */
function contentBox(c){
  var q=boxR(c);var cs=getComputedStyle(c);
  var l=q.l+(parseFloat(cs.borderLeftWidth)||0)+(parseFloat(cs.paddingLeft)||0);
  var r=q.r-(parseFloat(cs.borderRightWidth)||0)-(parseFloat(cs.paddingRight)||0);
  return {l:l,w:r>l?r-l:0};
}
/* THE INSERTION LINE, 2px thick, between a (before, as the eye reads) and b (after), either of which may be
   missing. ax 'x' draws it upright in the gap, as tall as the two; 'y' draws it level, as wide as the column's
   content (wide) or else the neighbours. Two neighbours that do not share a row (or a column) have no gap
   between them, so the line goes on the edge of one, b first when prefB. */
function slotLine(ax,a,b,prefB,wide){
  var ra=a?boxR(a):null,rb=b?boxR(b):null;
  if(!ra&&!rb)return null;
  var both=!!(ra&&rb);
  if(ax==='x'){
    if(both&&ra.b>rb.t&&rb.b>ra.t){var t=Math.min(ra.t,rb.t);return {x:(ra.r+rb.l)/2-1,y:t,w:2,h:Math.max(ra.b,rb.b)-t}}
    var e=rb&&(prefB||!ra)?rb:null;
    return e?{x:e.l-1,y:e.t,w:2,h:e.h}:{x:ra.r-1,y:ra.t,w:2,h:ra.h};
  }
  var cy,l,w;
  if(both&&ra.r>rb.l&&rb.r>ra.l){cy=(ra.b+rb.t)/2;l=Math.min(ra.l,rb.l);w=Math.max(ra.r,rb.r)-l}
  else if(rb&&(prefB||!ra)){cy=rb.t;l=rb.l;w=rb.w}
  else{cy=ra.b;l=ra.l;w=ra.w}
  if(wide&&wide.w>0){l=wide.l;w=wide.w}
  return {x:l,y:cy-1,w:w,h:2};
}
/* Before (vb) or after kids[ai] as the eye sees it, turned into DOM terms, with the line on that side. */
function placeBy(c,kids,ai,vb,ax){
  var a=kids[ai];
  var before=(ax.rev?!vb:vb)?a:(kids[ai+1]||null);
  var other=(vb!==ax.rev?kids[ai-1]:kids[ai+1])||null;
  var wide=ax.a==='y'?contentBox(c):null;
  return {c:c,before:before,line:vb?slotLine(ax.main,other,a,true,wide):slotLine(ax.main,a,other,false,wide)};
}
/* SLOT(c): where among c's flow children a drop at x,y goes. On one line, in front of the first child (in the
   order the eye reads) whose middle is past the pointer, else at the end. In two dimensions, beside the nearest
   child, before it when the pointer is short of its middle on the main axis. Empty: no line, the outline says it. */
function slotIn(c,drag,x,y){
  var kids=flowKids(c,drag,false);
  if(!kids.length)return {c:c,before:null,line:null};
  var ax=axisOf(c,drag);
  var wide=ax.a==='y'?contentBox(c):null;
  if(appendOnly(c,kids)){
    var tail=kids[kids.length-1];
    return {c:c,before:null,line:ax.rev?slotLine(ax.main,null,tail,true,wide):slotLine(ax.main,tail,null,false,wide)};
  }
  if(ax.a!=='2d'){
    var vis=ax.rev?kids.slice().reverse():kids;
    var p=ax.a==='x'?x:y;var k=-1;
    for(var i=0;i<vis.length;i++){
      var q=boxR(vis[i]);
      if((ax.a==='x'?q.l+q.w/2:q.t+q.h/2)>p){k=i;break}
    }
    var vp=k<0?vis[vis.length-1]:(k>0?vis[k-1]:null);
    var vn=k<0?null:vis[k];
    /* Reversed, in front of vn on screen is after it in the DOM, which is in front of vp. */
    return {c:c,before:ax.rev?vp:vn,line:slotLine(ax.a,vp,vn,!!vn,wide)};
  }
  var best=0,bd=Infinity;
  for(var j=0;j<kids.length;j++){
    var r=boxR(kids[j]);
    var dx=x<r.l?r.l-x:(x>r.r?x-r.r:0),dy=y<r.t?r.t-y:(y>r.b?y-r.b:0);
    if(dx*dx+dy*dy<bd){bd=dx*dx+dy*dy;best=j}
  }
  var qa=boxR(kids[best]);
  return placeBy(c,kids,best,ax.main==='x'?x<qa.l+qa.w/2:y<qa.t+qa.h/2,ax);
}
/* THE EDGE BAND: a pointer near c's leading or trailing edge, along its parent's axis (the main one in two
   dimensions), means before or after c in that parent, not into c. A quarter of c's size, 12 to 60px. Not
   for a c placed free (it has no slot in its parent) nor in an append-only parent. */
function bandSlot(c,par,drag,x,y){
  var pk=flowKids(par,drag,false);var ci=indexIn(pk,c);
  if(ci<0||appendOnly(par,pk))return null;
  var ax=axisOf(par,drag);
  var q=boxR(c);
  var size=ax.main==='x'?q.w:q.h,from=ax.main==='x'?q.l:q.t,p=ax.main==='x'?x:y;
  var band=Math.min(60,Math.max(12,size*0.25));
  var lead=p-from,trail=from+size-p;
  if(lead<band&&lead<=trail)return placeBy(par,pk,ci,true,ax);
  if(trail<band)return placeBy(par,pk,ci,false,ax);
  return null;
}
/* Whether x,y is over one of c's flow children, the dragged element's own box (still in place) counted. */
function overFlowKid(c,drag,x,y){
  var ks=flowKids(c,drag,true);
  for(var i=0;i<ks.length;i++){var q=boxR(ks[i]);if(x>=q.l&&x<=q.r&&y>=q.t&&y<=q.b)return true}
  return false;
}
/*
 * WHERE A DROP AT x,y GOES (slide pixels, the frame's own unscaled 1920x1080). Up from the hit to the end, never
 * above it: the first CONTAINER takes the drop, in its slot, or, when the pointer is in its edge band and its
 * parent is a container too, beside it in the parent. No container: the end. A same-shape sibling of the dragged
 * element is walked past whole, so a card over a card is before or after it.
 * same = the dragged element's own parent; noop = same, and the same index among its flow children.
 *   - THE END IS THE SLIDE AS MEASURED (slideTarget: the frame, or body when nothing fills the slide) for
 *     everything inside it. A childless layer beside the frame (a glow, grain) must not let body take a drop in
 *     the frame's padding or edge band: after the frame, off the slide. The slide itself and what holds it never
 *     move (frameOf). An element outside the frame keeps body as its end, and is answered only where moveHandTo,
 *     given the slide's path, takes it: never into body, or anything else holding the frame, unless that is the slide.
 *   - NO EDGE BAND OVER THE ROW IT IS IN: in its own parent, over a sibling or over its own spot, the element
 *     is reordered among its siblings; it leaves the parent from the parent's padding, gaps or outside it.
 *   - A TABLE PART stays among its own siblings: anywhere else the parser takes it out of its table (P3).
 *   - THE SAME SLIDE (P4, measured; Beka, 2026-09-25): a move may never change which element slideTarget answers
 *     (slideAfter), or what may be moved or deleted would change under the user: two full-bleed layers made one,
 *     or a layer's last child taken out of it. That drop stays a reorder in the element's own parent.
 */
function dropSlot(m){
  var x=m?+m.x:NaN,y=m?+m.y:NaN;
  if(!(x>=0&&x<=1920&&y>=0&&y<=1080))return null;
  var drag=nodeAt(m.path);
  if(!drag||!handable(drag))return null;
  var body=document.body;
  var st=slideTarget();
  if(!st||frameOf(drag,st))return null;
  var end=st===body||st.contains(drag)?st:body;
  var s=null;
  if(tablePart(drag)){
    s=slotIn(drag.parentNode,drag,x,y);
  }else{
    var hit=dropHit(x,y,drag);
    for(var h=hit;h&&h.nodeType===1&&h!==document.body;h=h.parentNode){
      if(h.parentNode===drag.parentNode){if(sameShape(h,drag))hit=h.parentNode;break}
    }
    var C=null;
    if(hit&&(hit===end||end.contains(hit))){
      for(var n=hit;n&&n.nodeType===1;n=n.parentNode){
        if(dropContainer(n,drag,end)){C=n;break}
        if(n===end)break;
      }
    }
    if(C&&C!==end&&!(C===drag.parentNode&&overFlowKid(C,drag,x,y))&&dropContainer(C.parentNode,drag,end))s=bandSlot(C,C.parentNode,drag,x,y);
    if(!s)s=slotIn(C||end,drag,x,y);
  }
  if(s.c!==drag.parentNode&&slideAfter(drag,s.c)!==st)s=slotIn(drag.parentNode,drag,x,y);
  if(s.c!==st&&s.c.contains(st))return null;
  s.same=s.c===drag.parentNode;
  s.noop=false;
  if(s.same){
    var rest=flowKids(s.c,drag,false);
    s.noop=indexIn(flowKids(s.c,drag,true),drag)===(s.before?indexIn(rest,s.before):rest.length);
  }
  return s;
}
function doDropAt(m){
  var out={__bds_dropslot:1,seq:m&&m.seq,ok:false,parentPath:null,beforePath:null,line:null,target:null,same:false,noop:false};
  try{
    var s=dropSlot(m);
    var pp=s?pathOf(s.c):null;
    var bp=s&&s.before?pathOf(s.before):null;
    if(s&&pp&&(!s.before||bp)){
      var q=boxR(s.c);
      out.ok=true;out.parentPath=pp;out.beforePath=bp;out.line=s.line;out.target={x:q.l,y:q.t,w:q.w,h:q.h};
      out.same=!!s.same;out.noop=!!s.noop;
    }
  }catch(e){
    out={__bds_dropslot:1,seq:m&&m.seq,ok:false,parentPath:null,beforePath:null,line:null,target:null,same:false,noop:false};
  }
  try{window.parent.postMessage(out,'*')}catch(e){}
}
/*
 * SPACING LIKE AUTO LAYOUT (__bds_spacing, Beka, 2026-09-25). An AI-written stack usually spaces its children
 * with their own margins (a headline's margin-bottom), and a margin travels with its element: move the
 * headline below the subhead and the pair touches while the 36px trails off the end. Figma's auto layout
 * spaces the GAPS instead, so a reorder keeps them even, and that is the behaviour asked for. So before a
 * move in (or into) a one-line stack, the host asks what that would take, measured on the live page as the
 * eye sees it now: the gaps between neighbours (the median of those that show; 0 when neighbours all touch;
 * the widest margin when there are fewer than two to measure), which children carry a margin on the stack's
 * axis, and whether the stack is a plain block that has to become a flex column first. The host writes it as
 * hand edits, container and children, in the same revision as the move.
 * Nothing to do (fix:false), because the fix would change what is on screen rather than keep it:
 *   - a stack spaced by gap alone, or body (its hand edits are its background only);
 *   - a grid, a one-column one included (axisOf reads that as y, but a grid spaced by margins is rare, and its
 *     tracks are not a flex column's), a wrapping row, a table row;
 *   - a negative margin (an overlap someone meant), or an auto one on the axis: in a flex that is a spacer
 *     pushing its neighbours apart, and in a block it is 0 now but would push once the block is a flex;
 *   - a block that would not stack the same as a flex column: one holding anything that is not a block, the
 *     incoming element included (an inline-block chip would stretch), a float (a flex ignores it), bare text (an
 *     anonymous item), or a display:contents child; one set in CSS columns; a list item showing its marker (a
 *     flex has none).
 */
var BLOCK_LEVEL={'block':1,'flex':1,'grid':1,'table':1,'list-item':1,'flow-root':1};
function autoMargin(el,cs,side){
  if(String(cs.getPropertyValue('margin-'+side)||'').trim()==='auto')return true;
  /* getComputedStyle resolves an auto margin to the px it came to; the typed computed value keeps the keyword. */
  try{if(typeof el.computedStyleMap==='function'){var t=el.computedStyleMap().get('margin-'+side);if(t&&String(t)==='auto')return true}}catch(e){}
  return false;
}
function doSpacing(m){
  var out={__bds_spacingis:1,seq:m&&m.seq,fix:false,gap:0,axis:null,convert:false,paths:[]};
  try{
    var c=nodeAt(m&&m.parentPath);
    var el=m&&m.path?nodeAt(m.path):null;
    if(c&&handable(c)){
      var cs=getComputedStyle(c);var d=String(cs.display||'');
      var flex=d==='flex'||d==='inline-flex';
      var marker=String(cs.listStyleType||'none')!=='none'||String(cs.listStyleImage||'none')!=='none';
      var block=d==='block'||d==='flow-root'||(d==='list-item'&&!marker);
      var mc=String(cs.columnCount||'auto'),mw=String(cs.columnWidth||'auto');
      if(block&&((mc!=='auto'&&mc!=='1')||mw!=='auto'))block=false;
      var ax=axisOf(c,null);
      var kids=flowKids(c,null,false);
      var list=kids.slice();
      if(el&&el.parentNode!==c&&!el.contains(c)&&handable(el))list.push(el);
      var fine=(flex||block)&&ax.a!=='2d';
      for(var t=c.firstChild;fine&&block&&t;t=t.nextSibling){if(t.nodeType===3&&/\\S/.test(t.nodeValue||''))fine=false}
      for(var i=0;fine&&i<list.length;i++){
        var lc=getComputedStyle(list[i]);var ld=String(lc.display||'');
        if(ld==='contents')fine=false;
        if(block){
          var fl=String(lc.cssFloat||lc.getPropertyValue('float')||'none');
          if(!BLOCK_LEVEL[ld]||(fl!=='none'&&fl!==''))fine=false;
        }
      }
      if(fine){
        var y=ax.a==='y';
        var lead=y?'top':'left',trail=y?'bottom':'right';
        var marked=[];var widest=0;
        for(var j=0;j<list.length;j++){
          var kc=getComputedStyle(list[j]);
          if(autoMargin(list[j],kc,lead)||autoMargin(list[j],kc,trail)){fine=false;break}
          var a=parseFloat(kc.getPropertyValue('margin-'+lead))||0;var b=parseFloat(kc.getPropertyValue('margin-'+trail))||0;
          if(a<0||b<0){fine=false;break}
          if(a>0||b>0){marked.push(list[j]);if(a>widest)widest=a;if(b>widest)widest=b}
        }
        if(fine&&marked.length){
          var seen=ax.rev?kids.slice().reverse():kids;var gaps=[];
          for(var g=0;g+1<seen.length;g++){
            var p=boxR(seen[g]);var q=boxR(seen[g+1]);
            var gap=y?q.t-p.b:q.l-p.r;
            if(gap>0.5)gaps.push(gap);
          }
          gaps.sort(function(u,v){return u-v});
          var paths=[];
          for(var k=0;k<marked.length;k++){var mp=pathOf(marked[k]);if(mp)paths.push(mp)}
          out.fix=true;
          /* Nothing spaced to measure (children that touch, the margin trailing off the end: a stack a move has
             already reshuffled) gets the widest margin, which is the spacing the author meant between them. */
          out.gap=Math.round(gaps.length?gaps[Math.floor((gaps.length-1)/2)]:widest);
          out.axis=y?'y':'x';
          out.convert=block;
          out.paths=paths;
        }
      }
    }
  }catch(e){out={__bds_spacingis:1,seq:m&&m.seq,fix:false,gap:0,axis:null,convert:false,paths:[]}}
  try{window.parent.postMessage(out,'*')}catch(e){}
}
/*
 * THE DROP, live (__bds_moveto): the mirror of moveHandTo, which the host has composed on the source already.
 * Everything is resolved before anything moves: the element (hand-editable, and not the measured slide nor what
 * holds it: frameOf, the rule moveHandTo takes with the slide's path), the parent (a box that can hold it, or
 * body; never the element or inside it), before (a child of that parent, or null for the end, which on body is in
 * front of the bridge's own scripts: the source has none). Then the lift comes off, the props in clear leave the
 * inline style (the old hand translate: the element sits exactly in its slot now), and the marks go on with
 * doMark's guards. The answer is doMark's confirm read, and the element is picked after it. A refusal changes
 * nothing.
 */
function doMoveTo(m){
  var out={__bds_movedto:1,seq:m&&m.seq,ok:false,path:null,rect:null,css:null};
  var el=null;var home=null;var next=null;
  try{
    var body=document.body;
    el=nodeAt(m&&m.path);
    var pp=m&&m.parentPath;
    var par=pp&&pp.length===0?body:nodeAt(pp);
    var bp=m?m.beforePath:undefined;
    var before=bp===null?null:nodeAt(bp);
    var eid=m?m.eid:undefined;var hand=m?m.hand:undefined;
    var clear=m&&m.clear!=null?m.clear:[];
    var eidOk=eid===null||(typeof eid==='string'&&EID_RE.test(eid));
    var handOk=hand===null||typeof hand==='string';
    var clearOk=Array.isArray(clear);
    for(var i=0;clearOk&&i<clear.length;i++){if(typeof clear[i]!=='string')clearOk=false}
    var cur=el?el.getAttribute('data-eid'):null;
    var parOk=!!par&&(par===body||(handable(par)&&boxTag(par)));
    var beforeOk=bp===null||(!!before&&before!==el&&before.parentNode===par&&!before.hasAttribute('${BRIDGE_MARK}'));
    if(el&&handable(el)&&!frameOf(el,slideTarget())&&parOk&&par!==el&&!el.contains(par)&&beforeOk&&eidOk&&handOk&&clearOk&&!(eid!==null&&cur&&cur!==eid)){
      var ref=before;
      if(!ref&&par===body){
        var bk=body.children;
        for(var b=0;b<bk.length;b++){if(bk[b].hasAttribute('${BRIDGE_MARK}')){ref=bk[b];break}}
      }
      home=el.parentNode;next=el.nextSibling;
      par.insertBefore(el,ref);
      var np=pathOf(el);
      if(!np)throw new Error('path');
      liftOff();
      for(var c=0;c<clear.length;c++){try{el.style.removeProperty(clear[c])}catch(e2){}}
      if(eid===null)el.removeAttribute('data-eid');else el.setAttribute('data-eid',eid);
      if(hand===null)el.removeAttribute('data-hand');else el.setAttribute('data-hand',hand);
      if(still)settleAnimations();
      var cs=getComputedStyle(el);var r=el.getBoundingClientRect();
      out.ok=true;out.path=np;out.rect={x:r.left,y:r.top,w:r.width,h:r.height};out.css=boxCss(cs);
    }
  }catch(e){
    out={__bds_movedto:1,seq:m&&m.seq,ok:false,path:null,rect:null,css:null};
    /* A throw after the move (the path check, a failed read): put the element back where it was. */
    if(home){try{home.insertBefore(el,next&&next.parentNode===home?next:null)}catch(e3){}}
  }
  try{window.parent.postMessage(out,'*')}catch(e){}
  if(!out.ok)return;
  try{ensurePickStyle();setSel(el);postPicked(el)}catch(e){}
  postOverflow();
}
/* THE LIFT (__bds_lift): the dragged element dims where it stands while the host's ring travels as the ghost.
   One at a time, so on first takes any other off; off takes it off everything. Never in a bake (doBake). */
function liftOff(){
  try{
    var ls=document.querySelectorAll('[data-bds-lifted]');
    for(var i=0;i<ls.length;i++)ls[i].removeAttribute('data-bds-lifted');
  }catch(e){}
}
function doLift(m){
  try{
    liftOff();
    if(!m||!m.on)return;
    var el=nodeAt(m.path);
    if(!el||!handable(el))return;
    ensurePickStyle();
    el.setAttribute('data-bds-lifted','');
  }catch(e){}
}
/*
 * THE TOP LEVEL IS FREE (__bds_freeze, Beka, 2026-09-25). The slide's frame works like a Figma frame without auto
 * layout: its direct children go where they are dropped, not into a slot; containers inside it still reorder. A
 * document lays them out in flow, so the first free drag of one pins EVERY flow child of the frame where it stands,
 * absolutely, and the dragged one dx,dy on: taking that one out of the flow alone would reflow the rest under the
 * user's hand. The host has put the dragged element's translate back to where the drag began, so its rect is where
 * it sits. The host composes the source from the answer, marks, and the pick follows.
 *   - Only a flow child of the slide as measured (slideTarget; flowKids: never structure, the bridge, a picture
 *     added by hand, or what is hidden or placed free already), with dx and dy finite. Refused, and nothing
 *     touched, for anything else, and for a frame holding bare words or a display:contents child: the pinned boxes
 *     would leave those to reflow alone.
 *   - Settled and measured FIRST: every child's own finite animations finished (and the page's, in settle mode),
 *     then every rect read before anything changes, so each target is what the eye saw. Transitions are held off
 *     for the writes, as settle mode holds them, or a read would catch a box leaving its old size.
 *   - The frame becomes their containing block: position:relative when it is static, never on body.
 *   - Per child, in DOM order: the inline translate goes (the target already holds it), then position absolute at
 *     left 0 / top 0, the width it had (read back and corrected once for padding and border), its height too when
 *     the flow had stretched it, and left and top off the probe, as an insert measures: at 0 / 0 the child sits on
 *     its containing block's corner (plus its own margin), so its rect there is the offset to take off the target.
 *   - Checked after, when no child lays out another any more: a child off its target by more than a pixel (a frame
 *     that moved as its children left the flow) is corrected once, and the slide must still be the frame. Anything
 *     else, or any failure, puts back every style attribute taken, the frame's included.
 * items: the frame's own first when it took position:relative, then each child's in DOM order. Paths do not change:
 * nothing moves in the DOM. Nothing is marked and nothing is picked; an overflow reading follows.
 */
function settleOwn(el){
  try{
    var as=el.getAnimations?el.getAnimations():[];
    for(var i=0;i<as.length;i++){
      var tm=as[i].effect&&as[i].effect.getTiming?as[i].effect.getTiming():null;
      if(tm&&tm.iterations!==Infinity){try{as[i].finish()}catch(e2){}}
    }
  }catch(e){}
}
/* Whole px of prop (width or height) that give el's box the size want: set, read back, corrected once. */
function sizeTo(el,prop,want){
  var v=Math.round(want);
  el.style.setProperty(prop,v+'px','important');
  var q=boxR(el);var got=prop==='width'?q.w:q.h;
  if(!finiteNum(got))throw new Error('size');
  if(Math.abs(got-want)>0.5){
    v=Math.max(0,Math.round(v+want-got));
    el.style.setProperty(prop,v+'px','important');
  }
  return v+'px';
}
function doFreeze(m){
  var out={__bds_frozen:1,seq:m&&m.seq,ok:false,items:null};
  var snaps=[];var held=still;
  try{
    var body=document.body;
    var st=slideTarget();
    var el=m?nodeAt(m.path):null;
    var dx=m?m.dx:NaN,dy=m?m.dy:NaN;
    var par=el?el.parentNode:null;
    var kids=st&&par===st?flowKids(par,null,false):[];
    var fine=!!el&&handable(el)&&!frameOf(el,st)&&indexIn(kids,el)>=0&&finiteNum(dx)&&finiteNum(dy);
    for(var t=fine?par.firstChild:null;t;t=t.nextSibling){if(t.nodeType===3&&/\\S/.test(t.nodeValue||''))fine=false}
    for(var c=0;fine&&c<kids.length;c++){if(String(getComputedStyle(kids[c]).display||'')==='contents')fine=false}
    if(fine){
      snaps.push({n:par,s:par.getAttribute('style')});
      for(var i=0;i<kids.length;i++)snaps.push({n:kids[i],s:kids[i].getAttribute('style')});
      for(var a=0;a<kids.length;a++)settleOwn(kids[a]);
      if(still)settleAnimations();
      var aim=[];
      for(var j=0;j<kids.length;j++){
        var r=boxR(kids[j]);
        if(!finiteNum(r.l)||!finiteNum(r.t)||!finiteNum(r.w)||!finiteNum(r.h))throw new Error('rect');
        var mine=kids[j]===el;
        aim.push({x:r.l+(mine?dx:0),y:r.t+(mine?dy:0),w:r.w,h:r.h});
      }
      if(!held){ensurePickStyle();mark(document.documentElement,'data-bds-still',true)}
      var items=[];
      if(par!==body&&String(getComputedStyle(par).position||'static')==='static'){
        var pp=pathOf(par);
        if(!pp)throw new Error('path');
        par.style.setProperty('position','relative','important');
        items.push({path:pp,decls:[{prop:'position',value:'relative'}]});
      }
      var put=[];
      for(var k=0;k<kids.length;k++){
        var kd=kids[k],to=aim[k];
        var kp=pathOf(kd);
        if(!kp)throw new Error('path');
        kd.style.removeProperty('translate');
        kd.style.setProperty('position','absolute','important');
        kd.style.setProperty('left','0px','important');
        kd.style.setProperty('top','0px','important');
        var w=sizeTo(kd,'width',to.w);
        var h=Math.abs(boxR(kd).h-to.h)>1?sizeTo(kd,'height',to.h):null;
        var o=boxR(kd);
        if(!finiteNum(o.l)||!finiteNum(o.t))throw new Error('probe');
        var lx=Math.round(to.x-o.l),ty=Math.round(to.y-o.t);
        kd.style.setProperty('left',lx+'px','important');
        kd.style.setProperty('top',ty+'px','important');
        put.push({path:kp,x:lx,y:ty,w:w,h:h});
      }
      for(var v=0;v<kids.length;v++){
        var g=boxR(kids[v]);
        var gx=aim[v].x-g.l,gy=aim[v].y-g.t;
        if(!finiteNum(gx)||!finiteNum(gy))throw new Error('check');
        if(Math.abs(gx)>1){put[v].x+=Math.round(gx);kids[v].style.setProperty('left',put[v].x+'px','important')}
        if(Math.abs(gy)>1){put[v].y+=Math.round(gy);kids[v].style.setProperty('top',put[v].y+'px','important')}
      }
      if(slideTarget()!==par)throw new Error('slide');
      for(var u=0;u<put.length;u++){
        var ds=[{prop:'position',value:'absolute'},{prop:'left',value:put[u].x+'px'},{prop:'top',value:put[u].y+'px'},{prop:'width',value:put[u].w}];
        if(put[u].h)ds.push({prop:'height',value:put[u].h});
        items.push({path:put[u].path,decls:ds});
      }
      out.ok=true;out.items=items;
    }
  }catch(e){
    for(var z=0;z<snaps.length;z++){
      try{if(snaps[z].s===null)snaps[z].n.removeAttribute('style');else snaps[z].n.setAttribute('style',snaps[z].s)}catch(e2){}
    }
    out={__bds_frozen:1,seq:m&&m.seq,ok:false,items:null};
  }
  if(!held)mark(document.documentElement,'data-bds-still',false);
  try{window.parent.postMessage(out,'*')}catch(e){}
  if(out.ok)postOverflow();
}
/* RESIZE, LIVE: one frame of a drag on the handles (Beka, 2026-09-27: "if I resize from top or left, the inner
   element moves independently, and when I stop resizing, it goes"). The parent has the pointer, but only the
   document knows where a size puts a box: a layout places its children (block flow keeps the left edge, a centring
   flex moves both), and an absolute box anchored right, bottom or centred moves its left or top edge as it grows.
   So the parent says the size, and for a box placed freely where it must start, and this sets them and answers with
   the box as it now is: the handles draw that box, and the release lands exactly what was set.
   - w / h: the border box in slide px, whole px corrected once for padding and border (sizeTo).
   - x / y: where the border box's left / top must be, for a free box (absolutely placed, or the top level once
     pinned). The translate on that axis is probed at 0 and set to whatever puts the box there, so right, bottom,
     centring and a percentage translate all come out the same; the other axis keeps its own. Absent in a layout,
     which places the box itself (Beka, 2026-09-27: inside a container "the layout decides").
   Answers {__bds_resized, seq, ok, rect, width, height, translate}: the box after, and what was set (translate
   only when asked to place). Quiet: no overflow reading per frame; the landing's mark takes one. */
function doResize(m){
  var out={__bds_resized:1,seq:m&&m.seq,ok:false,rect:null,width:null,height:null,translate:null};
  try{
    var el=nodeAt(m&&m.path);
    if(el&&handable(el)){
      if(finiteNum(m.w))out.width=sizeTo(el,'width',Math.max(1,m.w));
      if(finiteNum(m.h))out.height=sizeTo(el,'height',Math.max(1,m.h));
      var px=finiteNum(m.x),py=finiteNum(m.y);
      if(px||py){
        var tv=String(getComputedStyle(el).getPropertyValue('translate')||'').trim();
        var tk=!tv||tv==='none'?[]:tv.split(/\\s+/);
        var ox=tk[0]||'0px',oy=tk[1]||'0px';
        el.style.setProperty('translate',(px?'0px':ox)+' '+(py?'0px':oy),'important');
        var s=boxR(el);
        if(!finiteNum(s.l)||!finiteNum(s.t))throw new Error('probe');
        out.translate=(px?Math.round(m.x-s.l)+'px':ox)+' '+(py?Math.round(m.y-s.t)+'px':oy);
        el.style.setProperty('translate',out.translate,'important');
      }
      var q=boxR(el);
      if(!finiteNum(q.l)||!finiteNum(q.t)||!finiteNum(q.w)||!finiteNum(q.h))throw new Error('rect');
      out.rect={x:q.l,y:q.t,w:q.w,h:q.h};
      out.ok=true;
    }
  }catch(e){out.ok=false;out.rect=null}
  try{window.parent.postMessage(out,'*')}catch(e){}
}

/* ---- inline text edit ---- */
/* TYPING INTO THE REAL DOCUMENT. {__bds_edit:{path,on:true}} makes exactly one element contentEditable,
   so the words reflow under the author's own stylesheet as they are typed — the only way an edit can be
   honest on a hard 1920x1080 box, where a floating input would tell the user their headline fits right up
   until it is committed and cropped.
   Every default the engine brings with contenteditable is then taken back (Enter, paste, ⌘B/I/U below),
   because what leaves this element is not a value in a form — it is innerHTML the parent sanitizes and
   writes into a DOCUMENT it stores, re-renders and bakes. Anything a browser invents on the user's behalf
   would become markup the author never wrote and every later render has to keep honouring.
   Cold like the rest of the inspector: no listener exists until the first on:true. */
var editEl=null;var editOrig=null;var editFill=null;
/* A NEW TEXT BOX'S FIRST CARET ({__bds_edit:{path,on:true,draft:1}}, the text tool's). Escape keeps its
   words, as every caret's Escape now does (Birtvy's text tool finished the same way). Lives and dies with
   the caret it was opened with. */
var editDraft=false;
function inEdit(n){
  try{return !!(editEl&&n&&(n===editEl||editEl.contains(n)))}catch(e){return false}
}
/* THE CARET THE CLICK ALREADY PUT DOWN. A double-click to open the editor has selected the word under the
   cursor and a plain click has left a collapsed caret at the character the user aimed at — both are the
   user's own aim, both live inside the element, and select-all here would throw them away so the first
   keystroke wiped a line the user only meant to correct. Only a selection that is somewhere ELSE (a panel
   opening the editor with no click at all, a leftover selection in another element) gets replaced by
   select-the-contents, so typing still has something to land on. Read BEFORE contentEditable flips: the
   attribute change is the browser's cue to normalize the selection. */
function editRange(el){
  try{
    var s=window.getSelection();
    if(s&&s.rangeCount>0){
      var r=s.getRangeAt(0);
      var c=r?r.commonAncestorContainer:null;
      if(c&&(c===el||el.contains(c)))return r;
    }
  }catch(e){}
  return null;
}
function putCaret(el,keep){
  try{
    var s=window.getSelection();
    if(!s)return;
    if(keep){s.removeAllRanges();s.addRange(keep);return}
    var r=document.createRange();
    r.selectNodeContents(el);
    s.removeAllRanges();s.addRange(r);
  }catch(e){}
}
/* Replace the selection with one node and leave the caret after it. Shared by paste and Enter because both
   need the same three steps in the same order — insert before deleteContents drops the new content INTO
   the text it was meant to replace. The inEdit guard is not paranoia: a stale range can still point at the
   document the user clicked before the editor opened, and inserting there would write into an element
   nobody is editing and that no __bds_text message will ever describe. */
function insertAtCaret(node){
  try{
    var s=window.getSelection();
    if(!s||!s.rangeCount)return false;
    var r=s.getRangeAt(0);
    if(!inEdit(r.commonAncestorContainer))return false;
    r.deleteContents();
    r.insertNode(node);
    r.setStartAfter(node);
    r.collapse(true);
    s.removeAllRanges();s.addRange(r);
    return true;
  }catch(e){return false}
}
/* "Nothing renders after this node" — the test the filler break and its cleanup both hang on, and NOT the
   same question as nextSibling===null. insertNode SPLITS the text node it lands in, so a break inserted at
   the end of a line is always followed by the empty remainder of that split: a bare null-sibling check
   answers "not at the end" for the exact case that needs a filler most, and Enter at the end of a paragraph
   then does nothing visible at all (measured: the element stayed one line high and the next characters
   typed landed BEFORE the break). Empty and whitespace-only text is skipped because at the end of a block
   that is precisely what it renders as — nothing. */
function trailing(n){
  try{
    var s=n?n.nextSibling:null;
    while(s){
      if(s.nodeType!==3)return false;
      if(String(s.nodeValue||'').trim()!=='')return false;
      s=s.nextSibling;
    }
    return true;
  }catch(e){return false}
}
/* ENTER = one <br>, placed by hand. Left to the engine, Enter in a contenteditable wraps lines in <div> or
   <p> (Chrome and Firefox disagree on which) and the author's element comes back structurally different
   from the one they wrote — a paragraph split into blocks the stylesheet has no rules for.
   The second <br> is not a typo. A line break that ends a block renders NOTHING on its own: the line it
   opens has no content to give it height, so the caret would look like it never moved. Engines solve this
   with a filler break of their own; doing it by hand keeps the filler OURS, which is the only reason the
   commit can drop it again instead of persisting a blank line the user never typed. */
function insertBreak(){
  var br=document.createElement('br');
  if(!insertAtCaret(br))return;
  try{
    /* Only when this break ends its line, and only while we do not already have a filler standing —
       repeated Enters land BEFORE the existing one and need no second scaffold. A filler the user
       backspaced away (parentNode gone) is replaced, not counted. The filler goes immediately after the
       break, ahead of that empty split remainder, so the caret — parked after the break and NOT moved by an
       insertion at its own offset — ends up between the two, which is the new line. */
    if(trailing(br)&&(!editFill||!editFill.parentNode)&&br.parentNode){
      var f=document.createElement('br');
      br.parentNode.insertBefore(f,br.nextSibling);
      editFill=f;
    }
  }catch(e){}
}
/* PASTE IS PLAIN TEXT, always. The clipboard almost always carries a text/html flavour beside the text,
   and the browser would prefer it: a stranger's DOM tree with its own inline styles, classes, fonts and
   markup arrives inside a document we store and re-render. Take the text/plain flavour and drop it in as a
   single text node — characters, nothing else, styled by the element it lands in. */
function onEditPaste(e){
  try{e.preventDefault();e.stopPropagation()}catch(err){}
  var t='';
  try{var cd=e.clipboardData;t=cd?String(cd.getData('text/plain')||''):''}catch(err){t=''}
  if(!t)return;
  try{insertAtCaret(document.createTextNode(t))}catch(err){}
}
function onEditKey(e){
  try{
    var k=String(e.key||'');
    if(k==='Escape'||k==='Esc'){
      /* DONE TYPING, words kept (Beka, 2026-09-27: "if I type something in the text element and click esc it
         gets deleted"). Escape closes the caret as a click away does, Figma's way, and the element stays
         selected; Undo takes the words back. It used to put the element back as it was, which threw away
         what was just typed. The keystroke stops here: anything else that also treats Escape as its own
         close (the document's own scripts, the editor chrome outside the frame) would be answering a key
         that was never aimed at it. */
      try{e.preventDefault();e.stopPropagation()}catch(err){}
      endEdit(true);
      return;
    }
    if(k==='Enter'){
      try{e.preventDefault();e.stopPropagation()}catch(err){}
      insertBreak();
      return;
    }
    /* ⌘/Ctrl+B and ⌘/Ctrl+I ARE ALLOWED, and that is a reversal worth stating. They were refused here
       on the grounds that <b>/<i> "belong to no part of the artifact's style system" — while
       artifactEdit.ts's INLINE_ALLOWED_TAGS has listed b, i, strong and em the whole time, with
       its own comment saying they are there "because a contenteditable emits them the moment someone
       presses the bold shortcut they have pressed all their life". Two files, opposite answers. The text
       toolbar settles it: emphasis is a sanctioned edit and the sanitizer stores the tags this produces.
       The shortcut and the toolbar's B are NOT the same edit and are not meant to be — the button sets
       font-weight on the whole element, this wraps the words you highlighted in <b>. That is the split a
       document editor has always had, and it is why the shortcut is worth keeping once the button exists:
       it is the only way to emphasise a phrase.
       ⌘U stays refused: underline is not on the toolbar and, in a document, reads as a link.
       alt is excluded because ⌥⌘ combinations are the OS's, not the editor's. */
    if((e.metaKey||e.ctrlKey)&&!e.altKey){
      var c=k.toLowerCase();
      if(c==='u'){try{e.preventDefault()}catch(err){}}
    }
  }catch(err){}
}
/* Blur is a COMMIT, not a cancel: clicking away from text you just rewrote means you meant it, and losing
   the change there would be the cruelest possible reading of the gesture. Same path as the parent's
   on:false, deliberately — one commit routine, so the two can never drift. */
function onEditBlur(){endEdit(true)}
/* THE RING FOLLOWS THE TEXT AS IT IS TYPED, and before this it did not.
   The parent draws its outline OUTSIDE the iframe and can only know this element's box from a message.
   Geometry was posted after a style change and after a commit, which covers everything except the one
   thing that moves a text box most: typing into it. A headline that grew from two lines to three kept
   its ring at two until the user escaped and clicked back in (Beka, 2026-09-10).
   Coalesced to one animation frame. Input fires per keystroke and postOverflow measures every element
   in the document, so an uncoalesced version would force a full layout on each key. One frame is also
   the earliest the new geometry EXISTS: reading a rect inside the input handler reads the box from
   before the line the keystroke just added.
   Guarded on editEl inside the callback rather than outside, because the edit can be committed between
   the frame being asked for and the frame running. */
/* THE TOOLBAR FOLLOWS THE SELECTION. Without this the bar would keep showing the values it read when
   the caret opened, so selecting a word inside a styled sentence would offer you the sentence's size to
   change the word with. Coalesced for the same reason input is: dragging a selection fires this on
   every mousemove, and each reply is a re-render out in the parent. */
var selRaf=0;
function onSelChange(){
  if(!editEl||selRaf)return;
  try{
    selRaf=requestAnimationFrame(function(){
      selRaf=0;
      if(editEl){postPicked(editEl);postCaret()}
    });
  }catch(e){selRaf=0}
}
var editRaf=0;
function onEditInput(){
  if(editRaf)return;
  try{
    editRaf=requestAnimationFrame(function(){
      editRaf=0;
      if(editEl){postOverflow();postCaret()}
    });
  }catch(e){editRaf=0}
}
/* THE CARET IS THE PARENT'S TO DRAW, in text mode, for the reason the hover line is: anything 1px in here
   shrinks with the frame. Where it stands, in slide px like every rect this bridge reports, and the colour
   of the words it stands in; null while there is nothing to draw (no caret open, or words selected, which
   the engine's own highlight shows). Posted on change only. Select mode keeps the engine's caret. */
var caretSent='';
function postCaret(){
  try{
    if(pickMode!=='text')return;
    var c=editEl?caretRect():null;
    var key=c?[Math.round(c.x*10),Math.round(c.y*10),Math.round(c.h*10),c.color].join(','):'';
    if(key===caretSent)return;
    caretSent=key;
    window.parent.postMessage({__bds_caret:1,rect:c?{x:c.x,y:c.y,w:0,h:c.h}:null,color:c?c.color:null},'*');
  }catch(e){}
}
/* Where a collapsed caret stands. A range has a box of its own only in text, so a caret in an empty
   element, beside a <br> or at an element's edge is read off what it stands next to, and an element with
   nothing in it off its own first line, where the engine puts it. Not 'caretAt': that name places a caret
   at a point (a click passed in), and a second function of the same name here silently replaced it. */
function caretRect(){
  try{
    var s=window.getSelection();
    if(!s||!s.rangeCount)return null;
    var r=s.getRangeAt(0);
    if(!r.collapsed||!inEdit(r.startContainer))return null;
    var n=r.startContainer,o=r.startOffset;
    var host=n.nodeType===1?n:n.parentElement;
    if(!host||!inEdit(host))host=editEl;
    var q=edgeRect(rectsOf(r),false);
    if(q)q={left:q.left,top:q.top,height:q.height};
    else if(n.nodeType===3)q=textEdge(n,o);
    else{
      var kid=n.childNodes[o]||null,prev=o>0?n.childNodes[o-1]:null;
      q=nodeEdge(kid,true);
      /* Just past a line break with nothing after it: the start of the line that break opened. */
      if(!q&&prev&&prev.nodeName==='BR'){
        var b=nodeEdge(prev,true),f=firstLine(host);
        if(b&&f)q={left:f.left,top:b.top+b.height,height:b.height};
      }
      if(!q)q=nodeEdge(prev,false);
    }
    if(!q)q=firstLine(host);
    if(!q||!(q.height>0))return null;
    return {x:q.left,y:q.top,h:q.height,color:inkOf(host)};
  }catch(e){return null}
}
function rectsOf(x){try{return x.getClientRects()}catch(e){return null}}
/* The first (or last) box with any height: a collapsed or line-end range can report empty ones. */
function edgeRect(rs,first){
  if(!rs)return null;
  for(var i=0;i<rs.length;i++){var q=rs[first?i:rs.length-1-i];if(q&&q.height>0)return q}
  return null;
}
/* A caret inside text, read off the character before it (its right edge) or, at the start, after it. */
function textEdge(t,o){
  try{
    var len=String(t.nodeValue||'').length;
    if(!len)return null;
    var g=document.createRange();
    if(o>0){
      g.setStart(t,Math.min(o,len)-1);g.setEnd(t,Math.min(o,len));
      var a=edgeRect(rectsOf(g),false);
      return a?{left:a.right,top:a.top,height:a.height}:null;
    }
    g.setStart(t,0);g.setEnd(t,1);
    var z=edgeRect(rectsOf(g),true);
    return z?{left:z.left,top:z.top,height:z.height}:null;
  }catch(e){return null}
}
/* A caret against the node beside it: before it, its first box's left edge; after it, its last box's right. */
function nodeEdge(nd,before){
  try{
    if(!nd)return null;
    if(nd.nodeType===3){
      var len=String(nd.nodeValue||'').length;
      return len?textEdge(nd,before?0:len):null;
    }
    if(nd.nodeType!==1)return null;
    var g=document.createRange();
    g.selectNode(nd);
    var q=edgeRect(rectsOf(g),before);
    return q?{left:before?q.left:q.right,top:q.top,height:q.height}:null;
  }catch(e){return null}
}
function firstLine(el){
  try{
    if(!el)return null;
    var b=el.getBoundingClientRect(),cs=getComputedStyle(el);
    var px=function(v){var x=parseFloat(v);return isFinite(x)?x:0};
    var lh=px(cs.lineHeight);
    if(!(lh>0))lh=(px(cs.fontSize)||16)*1.2;
    var al=String(cs.textAlign||'');
    var x=b.left+px(cs.borderLeftWidth)+px(cs.paddingLeft);
    if(al==='center')x=b.left+b.width/2;
    else if(al==='right'||al==='end')x=b.right-px(cs.borderRightWidth)-px(cs.paddingRight);
    return {left:x,top:b.top+px(cs.borderTopWidth)+px(cs.paddingTop),height:lh};
  }catch(e){return null}
}
/* The caret is the colour of its words, as the engine's is, unless they are see-through (a gradient
   clipped to the text): then null, and the parent uses its own blue. */
function inkOf(el){
  try{
    var c=String(getComputedStyle(el).color||'');
    return clearColor(c)?null:c;
  }catch(e){return null}
}
function startEdit(m){
  try{
    var el=nodeAt(m&&m.path);
    if(!el)return;
    var tn=String(el.tagName||'').toUpperCase();
    /* Refusals, all SILENT — no message, no reply. A stale path (the panel is holding one from before a
       regeneration) is not an error, and an answer would only make the panel act on an editor that never
       opened. nodeAt walks DOWN from body so html/body/head cannot come back today; the refusal is written
       anyway because it is a rule, not an implementation detail. The bridge check is doRemove's rule in
       another form: an editable box drawn around the marked <script> would let one select-all sweep the
       only channel back to the parent into the html this edit posts up. */
    if(tn==='HTML'||tn==='BODY'||tn==='HEAD')return;
    /* Sealed components edit through their ATTRIBUTES, never a caret: a contenteditable commit posts
       innerHTML, and for a runtime-expanded host that would bake the expansion into the source. */
    if(tn.indexOf('DECK-')===0)return;
    for(var up=el.parentElement;up;up=up.parentElement){if(String(up.tagName||'').toUpperCase().indexOf('DECK-')===0)return}
    if(el.hasAttribute('${BRIDGE_MARK}')||el.querySelector('[${BRIDGE_MARK}]'))return;
    /* Re-entering the element already open would re-place the caret mid-sentence. A DIFFERENT element
       commits the one in flight first: one caret, one open editor, and the parent hears about the first
       edit before it hears about the second. */
    if(editEl===el)return;
    if(editEl)endEdit(true);
    var keep=editRange(el);
    /* The innerHTML remembered here is the whole of Escape: what the element looked like before a single
       keystroke landed. Stored as a string, not a node list, so nothing the user does inside can reach it. */
    editEl=el;editOrig=el.innerHTML;editFill=null;editDraft=!!(m&&m.draft);
    /* THE SELECTION FOLLOWS THE CARET, always. In text mode the click that opens an editor has
       normally selected this element already, so this is a no-op — but a double-click, a breadcrumb,
       or a __bds_edit from the parent can all open one on an element that was never selected, and
       then the next click on it would read as a FIRST click and merely select what is already being
       typed in. One rule instead: whatever is being edited is what is selected. */
    setSel(el);
    el.addEventListener('keydown',onEditKey,true);
    el.addEventListener('paste',onEditPaste,true);
    el.addEventListener('blur',onEditBlur,true);
    /* Not capture-phase like the other three: they intercept keys and pastes before the editor sees
       them, and this one only wants to know that the editor already handled something. */
    el.addEventListener('input',onEditInput,false);
    /* On the DOCUMENT, not the element: selectionchange has no target to speak of and does not fire on
       an element at all. Removed symmetrically in endEdit, so a document with no editor open is not
       still answering caret moves. */
    document.addEventListener('selectionchange',onSelChange,false);
    el.contentEditable='true';
    /* TAGS, NOT INLINE STYLE. execCommand defaults to styleWithCSS on in some engines, which turns a
       bold into <span style="font-weight:bold"> — legal through the sanitizer (it allows style) but
       the wrong shape: the allowlist names b and i for this, a tag survives a re-theme that rewrites
       token values, and a span carrying one declaration is indistinguishable from the inspector's own
       writes. Set once per edit; it is a document-level flag and cheap. */
    try{document.execCommand('styleWithCSS',false,false)}catch(e){}
    /* Tell the parent an editor is OPEN. It cannot infer this: a double-click starts editing in here, and
       while the caret is live the overlay must stop treating Delete and Backspace as "remove the selected
       element" or the first correction the user types would delete the thing they are editing. */
    try{window.parent.postMessage({__bds_editing:1,path:pathOf(el),on:true},'*')}catch(e){}
    /* preventScroll because the slide is a FIXED 1920x1080 box: focus() is allowed to scroll an
       overflow:hidden document into view, and a slide scrolled by even a few pixels offsets every rect the
       bridge reports and every pixel the next bake captures, with no scrollbar to show it happened. */
    try{el.focus({preventScroll:true})}catch(e){try{el.focus()}catch(e2){}}
    putCaret(el,keep);
    /* The toolbar opens with this editor and has to open on the RIGHT VALUES — the family, weight, size,
       colour and alignment the element actually renders at. That is exactly what __bds_picked carries, so
       the edit reports it rather than growing a second payload with the same seven fields in it. */
    postPicked(el);
    postCaret();
    /* The hover outline was drawn for a pointer that is now a text cursor. onOver will not redraw it
       inside the editor, so clear the one already on screen. */
    setHover(null);
  }catch(e){}
}
function endEdit(commit){
  var el=editEl;
  if(!el)return;
  var orig=editOrig;var fill=editFill;
  /* Clear the state FIRST, before touching the element: dropping contenteditable blurs it, and that blur
     re-enters this very function through onEditBlur. With the state already gone the re-entry returns
     immediately instead of committing the same edit twice — and two __bds_text messages would be two
     revisions recorded for one edit. This also restores the inspector before anything below can throw. */
  editEl=null;editOrig=null;editFill=null;editDraft=false;
  /* A coalesced range write still waiting to go. This function posts the element's whole innerHTML
     below, which contains everything that write was about to send, so the pending one is cancelled
     rather than allowed to fire afterwards against an element that is no longer being edited. */
  try{if(textTimer){clearTimeout(textTimer);textTimer=0}}catch(e){textTimer=0}
  try{el.removeEventListener('keydown',onEditKey,true)}catch(e){}
  try{el.removeEventListener('paste',onEditPaste,true)}catch(e){}
  try{el.removeEventListener('blur',onEditBlur,true)}catch(e){}
  try{el.removeEventListener('input',onEditInput,false)}catch(e){}
  try{document.removeEventListener('selectionchange',onSelChange,false)}catch(e){}
  try{if(selRaf){cancelAnimationFrame(selRaf);selRaf=0}}catch(e){selRaf=0}
  /* A frame already asked for would fire after the element stopped being edited. Harmless — the
     callback checks editEl — but cancelling keeps the next edit's first input from being swallowed
     by a pending id that is never cleared. */
  try{if(editRaf){cancelAnimationFrame(editRaf);editRaf=0}}catch(e){editRaf=0}
  /* Symmetric with the open notice, and sent for a CANCEL too: the overlay has to get its keys back
     whether the edit was kept or thrown away. */
  try{window.parent.postMessage({__bds_editing:1,path:null,on:false},'*')}catch(e){}
  postCaret();
  if(!commit){
    /* CANCEL: restore the remembered innerHTML and post NOTHING. An abandoned edit is not a revision, and
       a parent told about it would record an undo step for a change the user already took back. */
    try{el.innerHTML=orig}catch(e){}
    try{el.removeAttribute('contenteditable')}catch(e){}
    return;
  }
  /* Drop OUR filler break before reading anything: it existed so the caret had a line to stand on, not
     because the author typed an empty line. Only while it is still trailing — once the user has typed or
     broken past it, it is content and stays. Same trailing() test that placed it, for the same reason:
     split remainders sit between it and the end. */
  try{if(fill&&fill.parentNode&&el.contains(fill)&&trailing(fill))fill.parentNode.removeChild(fill)}catch(e){}
  try{el.removeAttribute('contenteditable')}catch(e){}
  var h='';var t='';var p=null;
  try{
    /* innerHTML, not textContent: the author's own inline markup — a <br> they typed, a <span class> the
       generator wrote — is part of the element and the parent replaces the element's CONTENTS with this. */
    h=String(el.innerHTML||'');
    /* text is a LABEL for the activity log, same 80-char cap as __bds_picked, never the payload. */
    t=String(el.textContent||'').trim();
    if(t.length>80)t=t.slice(0,80);
    /* Resolved at the instant it is used and thrown away, like every other path in this bridge. */
    p=pathOf(el);
  }catch(e){p=null}
  /* No path, no message: the parent replays this against the SOURCE HTML, and an edit it cannot address is
     one it must not try to write anywhere. */
  if(p){try{window.parent.postMessage({__bds_text:1,path:p,html:h,text:t},'*')}catch(e){}}
  /* Then the overflow reading, unconditionally. Text length is the one thing that moves the fold — a line
     that grew by a sentence pushes the last paragraph past 1080, where it is cropped and gone rather than
     scrolled — so the parent's warning has to be recomputed off the document as it now stands. A commit
     the parent could not address still changed the layout, which is why this is outside the guard above. */
  postOverflow();
}

/* The logo pass, applied IN PLACE. The parent proves (logoFillPlan) that a docHtml write was the logo
   rewrite and nothing else, keeps this frame mounted instead of remounting it, and posts the fills here.
   Semantics mirror applyLogoResolutions exactly: a verified url fills src and drops the data-logo marker
   (which is what makes a later fill idempotent), an unverified label removes the element. After this runs,
   the live DOM matches the persisted html, so the next bake captures the marks. */
function fillLogos(fills){
  for(var fi=0;fi<fills.length;fi++){
    var label=fills[fi][0],url=fills[fi][1];
    var imgs=document.querySelectorAll('img[data-logo]');
    for(var fj=0;fj<imgs.length;fj++){
      if(imgs[fj].getAttribute('data-logo')!==label)continue;
      if(url){imgs[fj].setAttribute('src',url);imgs[fj].removeAttribute('data-logo')}
      else if(imgs[fj].parentNode)imgs[fj].parentNode.removeChild(imgs[fj]);
    }
  }
}
/* WHAT IS UNDER THIS POINT, without arming anything. Annotate mode lives OUTSIDE this iframe: it draws
   its own crosshair layer over the frame and the pointer never crosses the boundary, so it cannot ask the
   DOM what it is aiming at. It sends the point instead — in the document's own coordinates, which is the
   only space involved here, because the iframe's viewport is a hard 1920x1080 that never scrolls.
   The reply carries a DESCRIPTOR CHAIN rather than a finished name. Naming is a page of heuristics
   (h1 "Explaining…", button [Close], graphic in chart) that belongs in TypeScript where it is
   readable and testable; the bridge's job is to report facts about three generations of one element and
   let the parent do the writing. Three, because the deepest naming rule the parent has — an icon inside a
   button — reaches exactly that far. */
function descOf(el){
  try{
    var t=String(el.textContent||'').trim();
    if(t.length>80)t=t.slice(0,80);
    var g=el.getAttribute?function(n){return el.getAttribute(n)||''}:function(){return ''};
    return{
      tag:String(el.tagName||'').toLowerCase(),
      text:t,
      id:String(el.id||''),
      /* SVG elements hand back an SVGAnimatedString here, which is not a string and would serialize as
         an empty object across postMessage. */
      cls:(el.className&&typeof el.className==='string')?el.className:'',
      data:(el.dataset&&el.dataset.element)||'',
      aria:g('aria-label'),role:g('role'),href:g('href'),alt:g('alt'),
      /* The hand-edit name tag, so a pin can say which marked element it means. */
      eid:(el.getAttribute&&el.getAttribute('data-eid'))||null
    };
  }catch(e){return null}
}
/* THE SLIDE STOPS THE WALK. The nearest hand-marked box above a pin is the box it is about, but not the
   slide (slideTarget) or anything that wraps it: a pin on a headline under a slide with a hand background
   is not a pin on the background, and targeting it would let the AI change that background by accident.
   A pin on the slide's own ground still names it. The host makes the same cut for a selection
   (useInlineTextEdit.selectedEid). */
function slideLevel(n,slide){
  return n===document.body||!!(slide&&(n===slide||(n.contains&&n.contains(slide))));
}
function nearestEid(el,slide){
  for(var n=el;n&&n.nodeType===1;n=n.parentNode){
    if(n!==el&&slideLevel(n,slide))return null;
    var e=n.getAttribute?n.getAttribute('data-eid'):null;
    if(e)return e;
    if(n===document.body)return null;
  }
  return null;
}
function doHit(m){
  var out={__bds_hitres:1,seq:m&&m.seq,path:null,rect:null,chain:[],eid:null};
  try{
    var el=document.elementFromPoint(m.x,m.y);
    /* Never name the tool. The bridge's own style node is not painted and cannot be hit, but the walk
       costs nothing and the alternative — an annotation pointing at style — is unrecoverable noise. */
    while(el&&el.nodeType===1&&(PICK_SKIP[String(el.tagName||'').toUpperCase()]||el.hasAttribute('${BRIDGE_MARK}')))el=el.parentNode;
    if(el&&el.nodeType===1&&el!==document.body&&el!==document.documentElement){
      out.path=pathOf(el);
      var r=el.getBoundingClientRect();
      out.rect={x:r.left,y:r.top,w:r.width,h:r.height};
      var n=el,d=0;var slide=slideTarget();
      while(n&&n.nodeType===1&&n!==document.body&&d<3){
        var dn=descOf(n);
        /* The slide's own tag names the slide only when the pin is ON it (see nearestEid). */
        if(n!==el&&slideLevel(n,slide))dn.eid=null;
        out.chain.push(dn);n=n.parentNode;d++;
      }
      /* The nearest hand-marked box at or above the point: a pin on a word inside a moved card is a pin
         on that card. */
      out.eid=nearestEid(el,slide);
    }
  }catch(e){}
  /* Always answers, including the empty answer. The parent holds a pending request keyed by seq and a
     silent miss would strand it — over a point that hit nothing, the honest reply is "nothing here". */
  try{window.parent.postMessage(out,'*')}catch(e){}
}
/* RE-ANNOUNCE WHAT IS ALREADY TRUE, on request and with no side effects.
   Every other message here is a push: the bridge tells the parent when something HAPPENS. That leaves
   one hole, and it is the hole a user sees — a parent that reloads, remounts, or loses its state while
   an editor is open has no way to find out, because the event that would have told it already fired.
   The ring then stays gone until the user clicks somewhere else and back, which reads as the selection
   working sometimes and not others.
   So the parent can ask. Deliberately NOT doSelect: that marks the element with the inspector's own
   2px outline, and in text mode the parent is already drawing a ring around it — two outlines on one
   headline. This only re-posts what is true, and posts the editing notice too so a parent that lost
   BOTH halves gets both back from one question. */
/* WHAT TYPE THIS DOCUMENT CAN ACTUALLY SET, answered by the document itself.
   The toolbar out there was offering six weights and every family the brand kit knew, on every artifact,
   whatever the artifact had loaded. On a document that pulls Inter at 300 and 400 that is four rows that
   do nothing: the engine has no face to use, so it picks the nearest one and the menu has told the user
   something false. Same for families — a name the document never loaded falls back silently.
   THE DOCUMENT IS THE ONLY PLACE THAT KNOWS. document.fonts holds a FontFace for every @font-face rule in
   reach, cross-origin stylesheets included (measured, 2026-09-06: a Google Fonts <link> for Inter at
   300;400 yields exactly those two weights), and that set already includes the brand kit's faces because
   brandFontStyle injects them into this same head. One read, one source of truth, and it matches what will
   render because it IS what will render.
   Families the document USES but has no face for — system-ui, Georgia, a local name — come back with an
   empty weight list, which means UNKNOWN rather than none. There is no way to enumerate a local font's
   weights from script, and a menu that hid every weight on a system font would be a worse lie than the one
   this replaces. The parent shows the full ladder for those. */
function fontWeightsInto(bag,spec){
  /* A face declares either one weight or a variable range ("100 900"). Expand a range to the hundreds
     inside it: the parent's ladder is in hundreds, so anything finer would be filtered away anyway. */
  /* DOUBLE-BACKSLASH, and it is not a typo. This whole script is a TEMPLATE LITERAL, so it loses one
     level of escaping on the way out: a single-backslash s written here reaches the engine as the plain
     letter s, and this split silently ran on the letter rather than on whitespace — a variable face
     declaring "400 900" came back as 400 alone. Measured, 2026-09-06. Every other regex in this string
     already doubles for the same reason. */
  var parts=String(spec||'400').trim().split(/\\s+/);
  var lo=parseInt(parts[0],10);var hi=parseInt(parts.length>1?parts[1]:parts[0],10);
  if(!isFinite(lo))return;
  if(!isFinite(hi)||hi<lo)hi=lo;
  if(hi===lo){bag[lo]=1;return}
  for(var w=100;w<=900;w+=100){if(w>=lo&&w<=hi)bag[w]=1}
}
function leadFamilyOf(stack){
  var first=String(stack||'').split(',')[0];
  /* Same escaping trap, and worse here: a single-backslash s reaches the engine as a bare s, so the
     pattern becomes "zero or more letter-s, then an optional quote" and it STRIPS A LEADING S.
     "sans-serif" became "ans-serif" and "system-ui" became "ystem-ui". */
  return first.replace(/^\\s*["']?/,'').replace(/["']?\\s*$/,'').trim();
}
/* LOAD A FACE THE DOCUMENT DOES NOT HAVE YET.
   The toolbar out there offers the whole font catalogue, and a family this document never loaded would
   otherwise fall back in silence — the exact failure the family list was narrowed to the document's own
   fonts to avoid. The parent cannot fix it from its side: a stylesheet added to the host head is not a
   face this origin can use. So it sends the href and the document adds the link itself.
   IDEMPOTENT on the href, because the parent sends one on every pick and most picks are re-picks of
   something already loaded. Not bridge-marked: this link IS the document's now, it goes into the source
   on the same write, and the bake already carries link hrefs forward as data-bds-fonts. Marking it
   would strip the very thing that makes the choice survive.
   The face arrives asynchronously; nothing here waits for it. The parent's own __bds_fonts reply will
   pick the new family up once document.fonts settles, which is what re-fills the weight menu. */
function doFontLink(m){
  try{
    var href=m&&typeof m.href==='string'?m.href:'';
    if(!href)return;
    var links=document.getElementsByTagName('link');
    for(var i=0;i<links.length;i++){if(links[i].getAttribute('href')===href)return}
    var l=document.createElement('link');
    l.rel='stylesheet';
    l.href=href;
    /* RE-ANSWER ON THE LINK'S OWN LOAD, not on document.fonts.ready alone.
       Measured 2026-09-10: ready resolved BEFORE the stylesheet arrived, so the reply went out naming
       only the fonts the document already had and the new family was missing from its own inventory.
       ready is about the faces the document KNOWS of; a sheet still in flight has declared none yet.
       So: wait for the sheet, then wait for the faces it declared, then answer. Both are needed —
       onload says the @font-face rules exist, ready says the files behind them are usable.
       The parent is not blocked on any of this. It has the catalogue's own weights for the font it just
       picked; this is the document confirming them. */
    try{l.onload=function(){try{document.fonts.ready.then(function(){doFonts()})}catch(e){doFonts()}}}catch(e){}
    (document.head||document.documentElement).appendChild(l);
  }catch(e){}
}
function doFonts(){
  try{
    var byName={};
    var order=[];
    var add=function(name){
      if(!name)return null;
      if(!byName[name]){byName[name]={name:name,weights:{},used:false};order.push(name)}
      return byName[name];
    };
    /* 1. THE FACES. document.fonts is set-like; forEach rather than spread because this string is
       delivered to whatever engine the artifact opens in and has never assumed iteration syntax. */
    try{
      document.fonts.forEach(function(ff){
        var e=add(leadFamilyOf(ff.family));
        if(e)fontWeightsInto(e.weights,ff.weight);
      });
    }catch(e){}
    /* 2. THE FAMILIES IN USE, which is a different question and the one that decides the ORDER. A
       document can load a face it never uses, and the family the user is standing on has to be in the
       list or the chip shows a value its own menu does not contain. Capped: this walks the same node
       set postOverflow does, and a slide is a few hundred elements, but the cap makes it a bounded
       cost on a document that is not. */
    try{
      var els=document.body?document.body.querySelectorAll('*'):[];
      var cap=els.length>2000?2000:els.length;
      for(var i=0;i<cap;i++){
        if(!ownsText(els[i]))continue;
        var e2=add(leadFamilyOf(getComputedStyle(els[i]).fontFamily));
        if(e2)e2.used=true;
      }
    }catch(e){}
    var used=[];var rest=[];
    for(var j=0;j<order.length;j++){
      var it=byName[order[j]];
      var ws=[];
      for(var k in it.weights)if(it.weights.hasOwnProperty(k))ws.push(parseInt(k,10));
      ws.sort(function(a,b){return a-b});
      var row={name:it.name,weights:ws};
      (it.used?used:rest).push(row);
    }
    var byNameAsc=function(a,b){return a.name<b.name?-1:a.name>b.name?1:0};
    used.sort(byNameAsc);rest.sort(byNameAsc);
    window.parent.postMessage({__bds_fontres:1,families:used.concat(rest)},'*');
  }catch(e){
    /* Answer even on failure. The parent falls back to its own list on an empty reply, and silence
       would leave it waiting for a message that is never coming. */
    try{window.parent.postMessage({__bds_fontres:1,families:[]},'*')}catch(e2){}
  }
}
/* ASKED ONCE, ANSWERED TWICE. A stylesheet is still in flight when the parent arms — the Google Fonts
   <link> is a second request behind the document — so the first answer can be empty or short. Rather
   than have the parent poll, send what is true now and send it again when the set settles. */
function doFontsWhenReady(){
  doFonts();
  try{document.fonts.ready.then(function(){doFonts()})}catch(e){}
}
function doRepick(){
  try{
    if(editEl){
      try{window.parent.postMessage({__bds_editing:1,path:pathOf(editEl),on:true},'*')}catch(e){}
      postPicked(editEl);
      caretSent='';postCaret();
      return;
    }
    if(selEl)postPicked(selEl);
  }catch(e){}
}
window.addEventListener('message',function(e){
  var d=e.data;
  if(!d)return;
  if(typeof d.__bfz==='boolean'){frozen=d.__bfz;applyFreeze();return}
  if(d.__bds_logos){fillLogos(d.__bds_logos);return}
  if(d.__bds_bake){doBake(d.__bds_bake.seq);return}
  if(d.__bds_pick){setPick(!!d.__bds_pick.on,d.__bds_pick.mode);return}
  if(d.__bds_hit){doHit(d.__bds_hit);return}
  if(d.__bds_fontlink){doFontLink(d.__bds_fontlink);return}
  if(d.__bds_fonts){doFontsWhenReady();return}
  if(d.__bds_repick){doRepick();return}
  if(d.__bds_style){applyStyle(d.__bds_style);return}
  if(d.__bds_select){doSelect(d.__bds_select);return}
  if(d.__bds_slide){doSlide();return}
  if(d.__bds_click){doClick(d.__bds_click);return}
  if(d.__bds_dragok){setDragOk(d.__bds_dragok.on);return}
  if(d.__bds_remove){doRemove(d.__bds_remove);return}
  if(d.__bds_mark){doMark(d.__bds_mark);return}
  if(d.__bds_move){doMove(d.__bds_move);return}
  if(d.__bds_dropat){doDropAt(d.__bds_dropat);return}
  if(d.__bds_moveto){doMoveTo(d.__bds_moveto);return}
  if(d.__bds_spacing){doSpacing(d.__bds_spacing);return}
  if(d.__bds_lift){doLift(d.__bds_lift);return}
  if(d.__bds_freeze){doFreeze(d.__bds_freeze);return}
  if(d.__bds_resize){doResize(d.__bds_resize);return}
  if(d.__bds_insert){doInsert(d.__bds_insert);return}
  if(d.__bds_graft){doGraft(d.__bds_graft);return}
  if(d.__bds_stack){doStack(d.__bds_stack);return}
  if(d.__bds_point){doPoint(d.__bds_point);return}
  if(d.__bds_imgsrc){doImgSrc(d.__bds_imgsrc);return}
  if(d.__bds_settle){setStill(!!d.__bds_settle.on);return}
  /* on:false is a COMMIT, the same one a blur performs — leaving the editor is never a way to discard.
     Only Escape discards, and it never reaches the parent to be echoed back. */
  if(d.__bds_edit){if(d.__bds_edit.on)startEdit(d.__bds_edit);else endEdit(true);return}
});
})()<` +
  `/script>`

/**
 * The active brand kit's real `@font-face` files, as a style block for the document HEAD.
 *
 * Rides the same injection as the runtime and for the same reason: every surface that gets the bridge
 * needs it, and none of them can be handed the kit. An artifact is a COMPLETE self-contained document on
 * an OPAQUE origin, so a parent-side `loadFont` cannot reach into it and a same-origin proxy URL would
 * 401 — the document has to carry its own faces, pointed at the brand's own CORS-enabled CDN. Before
 * this, an artifact generated from a kit imported off a website rendered every heading in a fallback,
 * because nothing in the artifact path had ever loaded a kit's font files. See `brandFonts.ts`.
 *
 * Head, not body: a face declared after the text it styles causes a visible reflow on every load.
 * Empty string when no kit is active, so the no-kit document is byte-identical to what it was.
 */
function brandFontStyle(): string {
  const css = activeBrandFaceCssForDoc()
  // Nothing published yet: ask for the kit ONCE, in the background. Rendering an artifact is the earliest
  // honest signal that a document is about to need brand faces, and it is a signal every surface sends —
  // the canvas cards included, which no other seam here can reach (BirtvyCanvas and BirtvySlideCard are
  // held by other sessions tonight). This render still goes out unstyled; the fetch publishes, listeners
  // fire, and the surfaces that subscribe reload with the real faces. Fire-and-forget by construction:
  // a failed lookup costs the brand font and never the document.
  if (!css) {
    primeBrandFonts()
    return ''
  }
  return `<style data-bds-brand-fonts="1">${css}</style>`
}

/**
 * A DECLARED-BUT-UNFILLED LOGO IS INVISIBLE, not a broken-image glyph.
 *
 * The contract has the model declare intent only — `<img data-logo="stradtimes.com" alt="Stradtimes">`
 * with NO src — and `artifactLogos` fills src afterwards, 1-3 network-seconds later. In that window the
 * browser does what it always does with a srcless img: draws the broken-image icon and the alt text. Guri
 * saw exactly that mid-generation, a torn glyph reading "stradtimes.com" sitting in the composition.
 *
 * THE SELECTOR IS SELF-CLEARING, which is why this needs no script and no cleanup. The fill does two
 * things — sets src AND removes the `data-logo` marker — so `img[data-logo]` means precisely "declared,
 * not yet resolved". The moment it resolves the rule stops matching. A declaration that never verifies is
 * removed from the document entirely, so nothing is left hidden forever.
 *
 * `visibility` rather than `display`, so the element keeps the box its own style attribute gives it and
 * the logo appears in place instead of shoving the layout sideways when it lands.
 *
 * THE FRAME IS BODY, NOT HTML, the second rule (Beka, 2026-09-26: a generated deck's artifacts all sat
 * shifted right and down in the preview while their thumbnails were right). The contract sizes html and
 * body both to the frame, and a model that puts the frame's padding in that one `html, body {}` rule pads
 * twice: html's padding pushes body, itself a full 1920px, 80px right and 60px down and out past the clip.
 * The bake never showed it, because a bake is body alone (its root div carries body's box, and nothing
 * matches html in a shadow root), so the thumbnails and resting cards disagreed with the live frame. Every
 * bridged document now treats html the way the bake does: no margin, padding or border of its own.
 * `!important` and last in the head, so it wins over the document's own rule whichever way it is written.
 */
const HYGIENE_STYLE =
  '<style data-bds-hygiene="1">img[data-logo]{visibility:hidden}' +
  'html{margin:0!important;padding:0!important;border:0!important}</style>'

/** Inject the bridge before </body> (or append — the parser hoists trailing content into body), and the
 *  brand faces into </head> (or prepend — the parser hoists a leading <style> into head). */
export function withDocBridge(html: string): string {
  // IDEMPOTENT, and that is what lets the surfaces stop coordinating. `BirtvyOpenPresent` now bridges
  // whatever it is handed, so every present mount gets the brand faces; a caller that had already
  // bridged (the kit page's tiles, the deck's preview) would otherwise inject the runtime twice, which
  // means two message listeners answering one bake request. The hygiene style is injected on every
  // path, so its presence is the honest stamp.
  if (html.includes('data-bds-hygiene="1"')) return html
  // The runtime rides the same injection: every surface that gets the bridge (live cards, the bake
  // warmer, the artifact editor, present mode) gets deck behaviors and components with no new call
  // sites. Runtime first, so its expansions exist before any bake request can arrive.
  const inject = DECK_RUNTIME_SNIPPET + DOC_BRIDGE_SNIPPET
  const i = html.lastIndexOf('</body>')
  const withBridge = i >= 0 ? html.slice(0, i) + inject + html.slice(i) : html + inject

  // ALWAYS injected now, where this used to bail when no kit was active: the logo rule is not about
  // branding and a document with no brand kit still declares logos.
  const head = HYGIENE_STYLE + brandFontStyle()
  const h = withBridge.indexOf('</head>')
  return h >= 0 ? withBridge.slice(0, h) + head + withBridge.slice(h) : head + withBridge
}

export interface DocBakeReply {
  __bds_baked: 1
  seq: number
  ok: boolean
  html: string | null
  fontHrefs: string[]
  err: string | null
}

export function isDocBakeReply(data: unknown): data is DocBakeReply {
  return typeof data === 'object' && data !== null && (data as { __bds_baked?: unknown }).__bds_baked === 1
}

/**
 * PINNED CONTRACT: posted the moment whenRendered proves the document is rendering, BEFORE the
 * quiescence gate arms — "alive and gating, answer coming". The queue re-arms its no-answer timeout
 * on this instead of culling the iframe; an iframe that posts nothing is still culled as throttled.
 */
export interface DocBakingAck {
  __bds_baking: 1
  seq: number
}

export function isDocBakingAck(data: unknown): data is DocBakingAck {
  return typeof data === 'object' && data !== null && (data as { __bds_baking?: unknown }).__bds_baking === 1
}

/**
 * The picked element's COMPUTED text properties — what the inspector opens on. Reporting only: changes go
 * back down as `__bds_style`, which takes any CSS property, so this shape grows only when a control needs
 * a starting value it cannot otherwise know. Computed rather than inline because the honest starting value
 * is what the element RENDERS as, whatever mix of stylesheet, cascade and an earlier preview produced it.
 * Every field is defensive on the iframe side: a value that will not parse degrades to the default noted
 * below and never costs the selection.
 */
export interface DocPickedStyles {
  /** Computed size in px. Always the same number as the reply's top-level `fontSize` — one read feeds both. */
  fontSize: number
  /** Numeric weight; the computed keywords are normalized (`bold` → 700, `normal` → 400). Unreadable → 400. */
  fontWeight: number
  /**
   * UNITLESS ratio: computed line-height px ÷ computed font-size px, rounded to 2dp. A ratio and not px
   * because it is the only form that survives a font-size change — a panel holding 64px would keep the
   * leading pinned while the text grew.
   *
   * NULL when the computed value is `normal`. That is not a missing reading, it is an honest one: `normal`
   * is the font's OWN metric, roughly 1.1–1.2 and different per family, so no ratio describes it. The panel
   * must render an indeterminate state for null. Inventing 1.2 would look harmless right up to the moment
   * the user changed something else and the panel wrote its guess into the document, silently re-leading
   * text nobody touched.
   */
  lineHeight: number | null
  /** Tracking in px. Computed `normal` reports 0, which is exactly what `normal` is. */
  letterSpacing: number
  /** As computed: `left` | `right` | `center` | `justify` | `start` | `end`. Unreadable → `''`. */
  textAlign: string
  /** As computed — the browser's own `rgb()` / `rgba()` string, never re-encoded (hex cannot carry alpha). */
  color: string
  /** 0..1. Unreadable → 1, so the panel agrees with what the user is looking at. */
  opacity: number
}

/**
 * PINNED CONTRACT: posted when the user clicks a pickable element while pick mode is on. `path` is the
 * element-child index chain from document.body (THE PATH CONTRACT above) — the same path goes back down
 * as `__bds_style` for preview, and the parent replays it against the source HTML with DOMParser to make
 * the edit real. `text` is the element's trimmed textContent capped at 80 chars (a label, not content),
 * `fontSize` is the COMPUTED size in px as a number so a stepper has a real starting value instead of
 * guessing at the stylesheet, and `rect` is in the document's own 1920x1080 space, so the parent scales
 * it by the card's scale to draw the selection outline on top.
 *
 * `styles` is the same idea widened to the rest of the text properties (see DocPickedStyles), so an
 * inspector can offer weight, leading, tracking, alignment, colour and opacity controls that open on the
 * element's real values instead of on defaults it made up. `fontSize` stays at the top level unchanged for
 * the consumers that already read it, and is taken from the same computed read as `styles.fontSize`, so
 * the two can never disagree. The one field that can come back null is `styles.lineHeight`: computed
 * `normal` has no ratio to report — it is the font's own metric, not a multiplier — so the bridge says so
 * instead of inventing 1.2, and the panel is expected to show an indeterminate leading control rather than
 * a number it would later write back into the document as if the user had chosen it.
 */
export interface DocPickedReply {
  __bds_picked: 1
  path: number[]
  tag: string
  text: string
  fontSize: number
  rect: { x: number; y: number; w: number; h: number }
  styles: DocPickedStyles
  /** True when `styles` describes a selected run of words inside the open editor, not the element. */
  range?: boolean
  /** Per style key, true when the selected run disagrees with itself (the control shows indeterminate). */
  mixed?: Partial<Record<string, boolean>>
  /** Box facts for the layout editor. Optional because older bridges in flight do not send it. */
  box?: DocPickedBox
  /** Text mode: the level around the selection, drawn dashed; null at the top. */
  parent?: { x: number; y: number; w: number; h: number } | null
}

/**
 * The picked element as a BOX: what a move or resize acts on. `rect` on the reply includes `translate`,
 * so the host subtracts it to find the layout slot. `index` is the element's position among its element
 * siblings (the last step of `path`); `siblings` counts the parent's element children, bridge nodes
 * excluded, so it matches the source.
 */
export interface DocPickedBox {
  /** Computed `display`. */
  display: string
  /** Computed `position`. */
  position: string
  /** Computed `translate`, `'none'` when unset or unsupported. */
  translate: string
  /** The element's `data-eid`, or null when it has never been hand-edited. */
  eid: string | null
  /** The element's `data-hand` (`prop:value;…`), or null. */
  hand: string | null
  /** The parent's computed `display` (flex/grid decides whether a move is a reorder). */
  parentDisplay: string
  /** The parent's computed `position`: anything but `static` already anchors a detached element. */
  parentPosition?: string
  /**
   * True when a Detach may anchor to the parent by making it `position:relative`: the parent is not
   * structure or the whole slide, and nothing else inside it is anchored above it today.
   */
  parentAnchor?: boolean
  /** The computed subset the properties panel shows ({@link BOX_CSS_PROPS}). Optional: older bridges omit it. */
  css?: Partial<Record<BoxCssProp, string>>
  /** Element children the source has, bridge nodes excluded. */
  kids?: number
  /** False inside a `deck-*` component or an svg, where hand edits never land. */
  handable?: boolean
  /** True when the Text controls apply: the element owns words, or it is a `deck-*` host with words. */
  text?: boolean
  /**
   * True when the element carries `data-hand-add`: a picture the user added by hand, whose placement is the
   * picture itself (Reset keeps it). Optional: older bridges omit it.
   */
  added?: boolean
  /**
   * True when the element is the slide as the bridge measures it (the frame `__bds_slide` answers: the element
   * that fills the slide and holds everything, or body) or holds it (Beka, 2026-09-25). It is the slide: a press
   * on it is a click only, and a drop, a move or a hand delete refuses it. A lone column that does not fill the
   * slide is not. Pass `__bds_slide`'s path to `moveHandTo` / `removeHand` so the source takes the same rule.
   * Optional: older bridges omit it.
   */
  frame?: boolean
  /**
   * True when the element's parent is that slide: the top level, which works like a Figma frame without auto
   * layout, its children placed freely (see `__bds_freeze`). Containers inside it still reorder. Optional: older
   * bridges omit it.
   */
  top?: boolean
  index: number
  siblings: number
}

export function isDocPickedReply(data: unknown): data is DocPickedReply {
  return typeof data === 'object' && data !== null && (data as { __bds_picked?: unknown }).__bds_picked === 1
}

/**
 * PINNED CONTRACT: posted once on entering pick mode and after every `__bds_style` apply. `bottom` is the
 * furthest element bottom in the document; anything past DOC_CANVAS_H is cropped by the fixed slide box,
 * so the UI can warn the moment a size change starts costing content rather than letting the user find
 * out at export time.
 */
export interface DocOverflowReply {
  __bds_overflow: 1
  bottom: number
  /** The selected element's rect AFTER the change, or null when nothing is selected. The parent draws its
   *  outline outside the iframe, so this is what keeps that outline on the text while the text resizes. */
  rect: { x: number; y: number; w: number; h: number } | null
  /** Text mode: the level around the selection after the change, as on `__bds_picked`. */
  parent?: { x: number; y: number; w: number; h: number } | null
}

export function isDocOverflowReply(data: unknown): data is DocOverflowReply {
  return typeof data === 'object' && data !== null && (data as { __bds_overflow?: unknown }).__bds_overflow === 1
}

/**
 * PINNED CONTRACT: the answer to `{__bds_remove:{path}}`. `ok:true` means the node is gone from the live
 * document and a `__bds_overflow` message follows immediately with the reflowed reading — content moves the
 * moment a node leaves, so the previous overflow number describes a document that no longer exists.
 * `ok:false` means NOTHING changed and no overflow follows: the path did not resolve, or it addressed
 * `html`, `body`, or the bridge's own nodes, all of which are refused (the bridge cannot delete the channel
 * it answers over).
 *
 * WHY THE CALLER HAS WORK TO DO: a path is a chain of ELEMENT-CHILD indices, so removing a node shifts the
 * index of every later sibling and invalidates every path pointing at or after it — a stale one does not
 * fail, it resolves to a DIFFERENT element and the next edit lands on innocent text. The UI must therefore
 * clear its selection and any pending style write BEFORE issuing the remove, and treat this reply as
 * "re-read the document", never as "resume where you were". Nothing on either side may cache paths across
 * a removal.
 *
 * With `added:1` on the request only an element carrying `data-hand-add` is taken; anything else answers
 * `ok:false`. With `hand:1` (Delete on any selection) it also answers `ok:false` for the Background's slide
 * (`__bds_slide`'s frame) and anything holding it (`box.frame`), anything inside a `deck-*` host or an svg,
 * and anything that is not a selectable level (a full-bleed layer, an inline run), a picture the user added
 * excepted. When the selection was inside what left, the empty `__bds_picked` (path null) and, if a hover
 * line stood, a `__bds_hover` with a null rect follow this answer, before the overflow reading.
 */
export interface DocRemovedReply {
  __bds_removed: 1
  ok: boolean
  /** The request's `seq`, echoed; absent when it carried none. */
  seq?: number
}

export function isDocRemovedReply(data: unknown): data is DocRemovedReply {
  return typeof data === 'object' && data !== null && (data as { __bds_removed?: unknown }).__bds_removed === 1
}

/**
 * PINNED CONTRACT: the answer to an inline text edit, posted ONLY on commit — `{__bds_edit:{path,on:false}}`
 * or a blur. Escape cancels and posts nothing at all, so the arrival of this message is itself the signal
 * that something actually changed and is worth a revision; there is no "cancelled" variant to filter out.
 *
 * `html` is the element's innerHTML AFTER the edit, and that is the whole reason this is not a plain-text
 * reply: the author's own inline markup — a `<br>` they typed, a `<span class="…">` the generator wrote —
 * lives inside the element and has to survive an edit. The parent sanitizes this and writes it back as the
 * element's CONTENTS in the source string, addressed by `path` (THE PATH CONTRACT above, resolved fresh at
 * commit time and never cached across one). `text` is the trimmed textContent capped at 80 chars — a label
 * for the activity log, the same shape `__bds_picked.text` uses, never the payload.
 *
 * A `__bds_overflow` message follows immediately and always: text length is the one edit that moves the
 * fold, and a line that grew by a sentence pushes the last paragraph past DOC_CANVAS_H where it is cropped,
 * not scrolled. It follows even when the bridge could not resolve a path to report, because the document
 * reflowed either way.
 *
 * WHAT THE CALLER MUST NOT DO: an edit rewrites the element's children, so every path pointing INSIDE the
 * edited element is stale the moment this arrives — the same hazard as `__bds_removed`, for the same
 * reason. Paths to the element itself and to everything outside it are untouched.
 */
/**
 * PINNED CONTRACT: posted when an inline text editor OPENS or CLOSES, including when it closes on a
 * cancel. The parent cannot infer either edge — a double-click inside the iframe starts an edit and
 * pointer events do not cross a browsing-context boundary — and it has to know, because while a caret is
 * live the overlay must stop reading Delete and Backspace as "remove the selected element". `path` names
 * the element on open and is null on close.
 */
export interface DocEditingReply {
  __bds_editing: 1
  path: number[] | null
  on: boolean
}

export function isDocEditingReply(data: unknown): data is DocEditingReply {
  return typeof data === 'object' && data !== null && (data as { __bds_editing?: unknown }).__bds_editing === 1
}

/**
 * PINNED CONTRACT, text mode only: where the open caret stands, for the parent to draw (Beka, 2026-09-27: "the
 * blinking writing indicator isn't visible"). The engine's caret is 1px inside a frame scaled to fit, so at Fit
 * it paints under half a screen pixel; text mode hides it (`caret-color: transparent`) and posts this instead.
 * `rect` is the caret's line in slide px (`w` is 0); `color` is its words' colour, null for see-through text.
 * Both null while there is nothing to draw: no caret open, or words selected (the engine's highlight shows
 * those). Posted on change only, and again on `__bds_repick`.
 */
export interface DocCaretReply {
  __bds_caret: 1
  rect: { x: number; y: number; w: number; h: number } | null
  color: string | null
}

export function isDocCaretReply(data: unknown): data is DocCaretReply {
  return typeof data === 'object' && data !== null && (data as { __bds_caret?: unknown }).__bds_caret === 1
}

export interface DocTextReply {
  __bds_text: 1
  path: number[]
  html: string
  text: string
}

export function isDocTextReply(data: unknown): data is DocTextReply {
  return typeof data === 'object' && data !== null && (data as { __bds_text?: unknown }).__bds_text === 1
}

/**
 * PINNED CONTRACT: the answer to `{__bds_mark:{path, eid, hand, seq}}`, which stamps `data-eid` /
 * `data-hand` on the live element (null removes either). `ok:false` means nothing was stamped: a stale
 * path, structure, the bridge, a node inside a `deck-*` host or an svg, a malformed eid, or a node that
 * already carries a DIFFERENT eid; `path` and `rect` are then null and `computed` is empty.
 *
 * `computed` is the confirm read: for every prop named in `hand`, the element's computed value AFTER the
 * preceding `__bds_style` writes landed. A `__bds_overflow` reading always follows.
 */
export interface DocMarkedReply {
  __bds_marked: 1
  seq: number
  ok: boolean
  path: number[] | null
  rect: { x: number; y: number; w: number; h: number } | null
  computed: Record<string, string>
  /** The box's computed subset after the mark ({@link BOX_CSS_PROPS}), so the panel never shows stale values. */
  css?: Partial<Record<BoxCssProp, string>>
}

export function isDocMarkedReply(data: unknown): data is DocMarkedReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_marked?: unknown; ok?: unknown; computed?: unknown }
  return d.__bds_marked === 1 && typeof d.ok === 'boolean' && typeof d.computed === 'object' && d.computed !== null
}

/**
 * PINNED CONTRACT: the answer to `{__bds_move:{path, toIndex, seq}}`, the live mirror of `moveHand`
 * (same algorithm, so both land on the same index). `path` is the moved element's NEW path, null when
 * refused. On `ok:true` a `__bds_picked` for the moved element (now the selection) and a
 * `__bds_overflow` follow. Every path at or after the old and new slots is stale, as with a remove.
 *
 * `{__bds_move:{path, step, flow:1, seq}}` (step `1` or `-1`) is the arrow on an element in a container: one
 * flow sibling on or back as the eye reads them (flow children as `__bds_dropat` counts them, the element
 * included; a `row-reverse` / `column-reverse` flex counts backwards). `toIndex` is that sibling's raw index in
 * `parent.children`, which is `moveHand`'s `toIndex` for the same move in both directions; it is present only
 * on an `ok:true` answer to the step form. No sibling that way (the element is first or last) is `ok:false`, and
 * so is any step in an append-only container (the element's own `order` or grid row placement counted; see
 * `DocDropSlotReply.beforePath`), where the DOM order is not the order on screen.
 */
export interface DocMovedReply {
  __bds_moved: 1
  seq: number
  ok: boolean
  path: number[] | null
  /** Step form only, on `ok:true`: the index the move used, for `moveHand(html, path, toIndex)`. */
  toIndex?: number
}

export function isDocMovedReply(data: unknown): data is DocMovedReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_moved?: unknown; ok?: unknown }
  return d.__bds_moved === 1 && typeof d.ok === 'boolean'
}

/**
 * PINNED CONTRACT: the answer to `{__bds_dropat:{seq, path, x, y}}`, where a reorder drag of the element at
 * `path` would land if let go at slide point `x`,`y` (the frame's own 1920x1080, unscaled). Paths are in the
 * CURRENT document, before any move.
 *
 * - `parentPath`: the container it goes into (`[]` = body, only when body is the slide: nothing fills it). Never
 *   the slide's frame's ancestors, and a drop in the frame never leaves it. A table part (`tr`, `td`, `tbody`, …)
 *   only ever gets its own parent. A move that would change which element is the slide (P4, measured: two
 *   full-bleed layers made one, a layer's last child taken out) is answered as a reorder in the element's own
 *   parent instead. Compose it with `moveHandTo(html, path, parentPath, beforePath, slidePath)`.
 * - `beforePath`: the flow child it goes in front of, or null for the end (always null in an append-only
 *   container: a flex or grid `order`, an item placed on a grid row (start or end; a `span` or a column alone
 *   is not a placement), grid areas, a `column` / `dense` grid flow).
 * - `line`: the 2px insertion line in slide pixels, upright in a row, level in a column (a one-column grid is a
 *   column); null in an empty container, where `target` alone says it.
 * - `target`: the container's rect. `same`: it is the element's own parent. `noop`: same, and the element's
 *   index among its flow children would not change, so a drop there is nothing.
 *
 * `ok:false` (every other field null or false): a point off the slide, a path that resolves to nothing, to an
 * element a hand edit cannot reach, or to the slide or anything holding it (`box.frame`), an element outside the
 * frame with nowhere but above it to go, or any failure. Never throws.
 */
export interface DocDropSlotReply {
  __bds_dropslot: 1
  seq: number
  ok: boolean
  parentPath: number[] | null
  beforePath: number[] | null
  line: { x: number; y: number; w: number; h: number } | null
  target: { x: number; y: number; w: number; h: number } | null
  same: boolean
  noop: boolean
}

export function isDocDropSlotReply(data: unknown): data is DocDropSlotReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_dropslot?: unknown; ok?: unknown }
  return d.__bds_dropslot === 1 && typeof d.ok === 'boolean'
}

/**
 * PINNED CONTRACT: the answer to `{__bds_spacing:{seq, parentPath, path}}` (see doSpacing): what making the stack
 * at `parentPath` space its gaps would take, measured live. `fix:false` means nothing to do (and then `gap` is 0,
 * `axis` null, `convert` false, `paths` empty); otherwise `gap` in px (the median of the gaps that show between
 * neighbours; 0 when they all touch; the widest margin with fewer than two to measure), the stack's `axis`,
 * whether it must `convert` from a block to a flex column, and the `paths` of the children (and the element
 * coming in, at `path`, when it lives elsewhere) whose margins on that axis must go. Paths are the current
 * document's, before the move. Never a grid, body (`parentPath` `[]`), a negative or auto margin on the axis, or
 * a block that would not stack the same as a flex column.
 */
export interface DocSpacingReply {
  __bds_spacingis: 1
  seq: number
  fix: boolean
  gap: number
  axis: 'x' | 'y' | null
  convert: boolean
  paths: number[][]
}

export function isDocSpacingReply(data: unknown): data is DocSpacingReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_spacingis?: unknown; fix?: unknown }
  return d.__bds_spacingis === 1 && typeof d.fix === 'boolean'
}

/**
 * PINNED CONTRACT: the answer to `{__bds_moveto:{seq, path, parentPath, beforePath, eid, hand, clear}}`, the
 * live mirror of `moveHandTo`. The element at `path` goes into the element at `parentPath` (`[]` = body), in
 * front of the one at `beforePath`, or last when that is null (on body: in front of the bridge's own scripts).
 * Then its lift comes off, every prop named in `clear` (strings) leaves its inline style, and `data-eid` /
 * `data-hand` are set or removed as `__bds_mark` does, with the same guards. `clear` may be omitted.
 *
 * `path` is the element's NEW path; `rect` and `css` are the confirm read `__bds_marked` gives. On `ok:true` a
 * `__bds_picked` for the element (now the selection) and a `__bds_overflow` follow. `ok:false` changed nothing
 * (the other fields are null): a path that resolves to nothing, the slide or anything holding it (`box.frame`), an
 * element out of a hand edit's reach, a parent that is the element or inside it or cannot hold a box, a `before`
 * that is not the parent's child, a malformed eid, hand or clear, or an element already wearing a different eid.
 */
export interface DocMovedToReply {
  __bds_movedto: 1
  seq: number
  ok: boolean
  path: number[] | null
  rect: { x: number; y: number; w: number; h: number } | null
  css: Partial<Record<BoxCssProp, string>> | null
}

export function isDocMovedToReply(data: unknown): data is DocMovedToReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_movedto?: unknown; ok?: unknown }
  return d.__bds_movedto === 1 && typeof d.ok === 'boolean'
}

/**
 * PINNED CONTRACT: the answer to `{__bds_freeze:{seq, path, dx, dy}}` (Beka, 2026-09-25: the slide's top level is
 * a Figma frame without auto layout). The element at `path` is a flow child of the slide (`box.top`, `flow`), and
 * the host has put its translate back where the drag began. On `ok:true` every flow child of the slide (never a
 * picture added by hand, a script, or what is placed free or hidden already) is absolutely placed where it stood,
 * and the one at `path` `dx`,`dy` (slide px) on. `items` says exactly what was set, `!important`, string values
 * as set, lengths whole px:
 * - first, only when the slide was `position: static` and is not body: `{path: slide, decls: [position: relative]}`;
 * - then one per child, in DOM order: `position: absolute`, `left`, `top`, `width` and, only when the flow had
 *   stretched it, `height`. left/top are measured against the containing block, so they are what the source holds.
 * Each child's inline `translate` was removed (its offset is in left/top now), so the host clears `translate` from
 * every child's hand too. Paths are unchanged: nothing moved in the DOM. Nothing is marked or picked (the host
 * composes the source, marks, and the pick follows); a `__bds_overflow` reading follows.
 *
 * `ok:false` (`items` null) touched nothing: a path to nothing, an element that is not a flow child of the slide,
 * `dx`/`dy` that are not finite numbers, a slide holding bare words or a `display: contents` child, a slide that
 * would stop being the slide once its children leave the flow, or any failure (every style attribute is restored).
 */
export interface DocFrozenReply {
  __bds_frozen: 1
  seq: number
  ok: boolean
  items: { path: number[]; decls: { prop: string; value: string }[] }[] | null
}

export function isDocFrozenReply(data: unknown): data is DocFrozenReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_frozen?: unknown; ok?: unknown }
  return d.__bds_frozen === 1 && typeof d.ok === 'boolean'
}

/**
 * PINNED CONTRACT: the answer to `{__bds_resize:{seq, path, w?, h?, x?, y?}}`, one frame of a resize on the handles
 * (Beka, 2026-09-27: resizing from the top or the left drifted, then jumped on release). `w`/`h` are the border box
 * in slide px, set whole px and corrected once for padding and border; `x`/`y`, sent for a free box only (absolute,
 * or the top level once pinned), are where its border box's left/top must be: the `translate` on that axis is
 * probed at 0 and set to what puts it there, whatever anchors the box, and the other axis keeps its own. A box in a
 * layout gets no `x`/`y`: the layout places it. On `ok:true`, `rect` is the border box as it now is (what the
 * handles draw), and `width`/`height`/`translate` are exactly what was set `!important` (null when not asked), which
 * is what the host lands. Quiet: no `__bds_overflow` per frame. `ok:false` (`rect` null): a path to nothing, a
 * node no hand may touch, or a failure part way, after which the host lands the last frame that answered.
 */
export interface DocResizedReply {
  __bds_resized: 1
  seq: number
  ok: boolean
  rect: { x: number; y: number; w: number; h: number } | null
  width: string | null
  height: string | null
  translate: string | null
}

export function isDocResizedReply(data: unknown): data is DocResizedReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_resized?: unknown; ok?: unknown }
  return d.__bds_resized === 1 && typeof d.ok === 'boolean'
}

/**
 * One picture in `{__bds_insert:{seq, items}}` (1 to 10 of them). `id` is the image pool id
 * (`[A-Za-z0-9_-]{1,128}`), written as `data-deck-image`; `src` a `data:image/…` URI (what the sandboxed
 * frame can show) or a `/api/v1/storage/…` URL, with or without an http(s) origin; `alt` is cut to 200
 * characters. `box` is where it lands, in slide pixels, already placed and clamped by the host; `w` and
 * `h` are at least 1. One invalid item refuses the whole message.
 */
export interface DocInsertImageItem {
  kind?: 'image'
  id: string
  src: string
  alt: string
  box: { x: number; y: number; w: number; h: number }
}

/**
 * A text box from the insert dock: `text` (trimmed, at most {@link ADDED_TEXT_MAX} characters) set as text in a
 * `<div>` in the deck's body type. Empty is the dock's own case: a caret and nothing else, one line tall in the
 * frame (`min-height: 1lh`, never in `decls`) until words are typed. `box.w` is its width when one was drawn;
 * with none it grows as it is typed. It never has a height.
 */
export interface DocInsertTextItem {
  kind: 'text'
  text: string
  box: { x: number; y: number; w?: number }
}

/** A circle (`round`) or a square from the insert dock: an empty `<div>` in the deck's accent. */
export interface DocInsertShapeItem {
  kind: 'shape'
  round?: boolean
  box: { x: number; y: number; w: number; h: number }
}

export type DocInsertItem = DocInsertImageItem | DocInsertTextItem | DocInsertShapeItem

/**
 * PINNED CONTRACT: the answer to `{__bds_insert:{seq, items}}`. On `ok:true` every element is in the live
 * document, in item order, as the last children of the slide at `parentPath` (`[]` = body, where they sit
 * in front of the bridge's scripts), and `paths[i]` is item i's path. `decls[i]` is exactly what was set on
 * it as a hand value, `!important`, in this order: position, left, top, then width and height when it has
 * them (a text box has no height, and no width unless one was drawn), then a picture's object-fit or a
 * shape's background-color and a circle's border-radius, then z-index only when one was needed; lengths are
 * whole px (`'480px'`). left/top are measured against the containing block, so they are what the source must
 * hold. A text box's type is not in `decls`: it is plain inline, not a hand value. A `__bds_overflow` reading
 * follows.
 *
 * Nothing is marked and nothing is picked: the host writes the source, stamps each with `__bds_mark`, then
 * selects with `__bds_select`. `ok:false` means nothing is left behind and the three fields are null.
 */
export interface DocInsertedReply {
  __bds_inserted: 1
  seq: number
  ok: boolean
  parentPath: number[] | null
  paths: number[][] | null
  decls: { prop: string; value: string }[][] | null
}

/**
 * PINNED CONTRACT: the answer to `{__bds_imgsrc:{path, id, src, alt, seq}}`, a picture's pool id, bytes and alt
 * swapped in place. `ok:false` means nothing changed: not a deck picture, or an id or src it would not take.
 */
export interface DocImgSetReply {
  __bds_imgset: 1
  seq: number
  ok: boolean
}

export function isDocInsertedReply(data: unknown): data is DocInsertedReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_inserted?: unknown; ok?: unknown }
  return d.__bds_inserted === 1 && typeof d.ok === 'boolean'
}

/**
 * PINNED CONTRACT: a paste carrying image files (at most 10), posted from inside the frame while text mode
 * is armed and no caret is open; the bridge has already called preventDefault on it. Any script in the
 * artifact can post this shape too, so the host takes it only right after a real keypress
 * (`navigator.userActivation.isActive`), and only as `File` objects within its own caps.
 */
export interface DocPasteMessage {
  __bds_paste: 1
  /** Picture files, as before. */
  files: File[]
  /** The plain words on the clipboard, capped for the wire; '' when there are none. */
  text: string
  /** The token of an element copied in Deck, when the clipboard's html still carries one. */
  clip: string | null
  /** Where the pointer last was on the slide, in slide px, and when. */
  at: { x: number; y: number; t: number } | null
}

export function isDocPasteMessage(data: unknown): data is DocPasteMessage {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_paste?: unknown; files?: unknown; text?: unknown; clip?: unknown }
  return (
    d.__bds_paste === 1 &&
    Array.isArray(d.files) &&
    d.files.every((f) => typeof File !== 'undefined' && f instanceof File) &&
    (d.text === undefined || typeof d.text === 'string') &&
    (d.clip === undefined || d.clip === null || typeof d.clip === 'string')
  )
}

/**
 * PINNED CONTRACT (2026-09-27): ⌘C or ⌘X with an element selected and no caret. The clipboard already holds the
 * element's words and a span carrying `token`; the host keeps the element under it, and a cut removes it.
 */
export interface DocCopyMessage {
  __bds_copy: 1
  token: string
  path: number[]
  cut: boolean
}

export function isDocCopyMessage(data: unknown): data is DocCopyMessage {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_copy?: unknown; token?: unknown; path?: unknown; cut?: unknown }
  return (
    d.__bds_copy === 1 &&
    typeof d.token === 'string' &&
    /^[A-Za-z0-9_-]{1,64}$/.test(d.token) &&
    Array.isArray(d.path) &&
    d.path.every((n) => Number.isInteger(n) && (n as number) >= 0) &&
    typeof d.cut === 'boolean'
  )
}

/** PINNED CONTRACT (2026-09-27): a right-click on the slide, at slide px; `on` says whether something is selected. */
export interface DocMenuMessage {
  __bds_menu: 1
  x: number
  y: number
  on: 'selection' | 'ground'
}

export function isDocMenuMessage(data: unknown): data is DocMenuMessage {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_menu?: unknown; x?: unknown; y?: unknown; on?: unknown }
  return (
    d.__bds_menu === 1 &&
    typeof d.x === 'number' &&
    Number.isFinite(d.x) &&
    typeof d.y === 'number' &&
    Number.isFinite(d.y) &&
    (d.on === 'selection' || d.on === 'ground')
  )
}

/** PINNED CONTRACT (2026-09-27): the answer to `__bds_graft`. `z` is one above the highest in the copy's parent. */
export interface DocGraftedReply {
  __bds_grafted: 1
  seq: number
  ok: boolean
  path: number[] | null
  rect: { x: number; y: number; w: number; h: number } | null
  z: number | null
}

export function isDocGraftedReply(data: unknown): data is DocGraftedReply {
  return typeof data === 'object' && data !== null && (data as { __bds_grafted?: unknown }).__bds_grafted === 1
}

/**
 * PINNED CONTRACT (2026-09-27): the answer to `__bds_stack`. Each of the four is the z-index that would do it, or
 * null; `isolate` is the parent to give `isolation: isolate` with a negative one.
 */
export interface DocStackReply {
  __bds_stackis: 1
  seq: number
  ok: boolean
  free: boolean
  front: number | null
  forward: number | null
  backward: number | null
  back: number | null
  isolate: number[] | null
}

export function isDocStackReply(data: unknown): data is DocStackReply {
  return typeof data === 'object' && data !== null && (data as { __bds_stackis?: unknown }).__bds_stackis === 1
}

/** PINNED CONTRACT (2026-09-27): the answer to `__bds_point`, where the pointer last was on the slide. */
export interface DocPointReply {
  __bds_pointis: 1
  seq: number
  at: { x: number; y: number; t: number } | null
}

export function isDocPointReply(data: unknown): data is DocPointReply {
  return typeof data === 'object' && data !== null && (data as { __bds_pointis?: unknown }).__bds_pointis === 1
}

/**
 * PINNED CONTRACT: a drag carrying files is over the frame (dragenter, then dragover at most every
 * 100ms), while text mode is armed. It only asks the host to raise its drop overlay over the frame; the
 * drop itself lands on that overlay, never in here.
 */
export interface DocFileDragMessage {
  __bds_filedrag: 1
}

export function isDocFileDragMessage(data: unknown): data is DocFileDragMessage {
  return typeof data === 'object' && data !== null && (data as { __bds_filedrag?: unknown }).__bds_filedrag === 1
}

/**
 * PINNED CONTRACT: a keystroke forwarded out of the frame while pick is armed, an element is selected and
 * NO text editor is open (typing is never forwarded). Arrows, Escape, Delete, Backspace and ⌘/Ctrl+Z
 * only; the bridge has already called preventDefault on it. In text mode the insert dock's tools come too,
 * selection or not: a plain `t`, `r` or `c` (lowercased, no modifier), for the host to arm Text, Square or
 * Circle.
 */
export interface DocKeyReply {
  __bds_key: 1
  key: string
  code: string
  shift: boolean
  alt: boolean
  meta: boolean
  ctrl: boolean
  repeat: boolean
}

export function isDocKeyReply(data: unknown): data is DocKeyReply {
  if (typeof data !== 'object' || data === null) return false
  const d = data as { __bds_key?: unknown; key?: unknown }
  return d.__bds_key === 1 && typeof d.key === 'string'
}

/**
 * PINNED CONTRACT: text mode's hover, drawn by the parent. `rect` is what a click at the pointer would
 * select, in slide pixels, or null: over the selection itself, inside the open caret, off the document, and
 * once when pick mode turns off.
 */
export interface DocHoverReply {
  __bds_hover: 1
  rect: { x: number; y: number; w: number; h: number } | null
}

export function isDocHoverReply(data: unknown): data is DocHoverReply {
  return typeof data === 'object' && data !== null && (data as { __bds_hover?: unknown }).__bds_hover === 1
}

/**
 * PINNED CONTRACT: the answer to `{__bds_slide:1}`, the slide for the panel's Background. `path` is the
 * frame (the element filling the whole slide and holding everything), `[]` for body when there is no frame,
 * or null when the document has no body. `bg` is the colour the reader sees there: the first painted
 * `background-color` from the target up.
 */
export interface DocSlideReply {
  __bds_slidebox: 1
  path: number[] | null
  eid?: string | null
  hand?: string | null
  bg?: string
}

export function isDocSlideReply(data: unknown): data is DocSlideReply {
  return typeof data === 'object' && data !== null && (data as { __bds_slidebox?: unknown }).__bds_slidebox === 1
}

/**
 * PINNED CONTRACT: a press-and-drag on an element that was not selected, streamed out while
 * `{__bds_dragok:{on:true}}` holds. `start` comes once the press has travelled a few slide pixels, right
 * after the `__bds_picked` that selects its target, and carries that target's `path`, the press point and
 * `flow` and `top`; `move` and `end` carry the pointer, and ⌥ for a detach; `cancel` drops it. Slide pixels
 * throughout. A press on the slide or anything holding it (`box.frame`) never starts one: it stays a click.
 */
export interface DocDragReply {
  __bds_drag: 1
  phase: 'start' | 'move' | 'end' | 'cancel'
  path?: number[]
  x?: number
  y?: number
  alt?: boolean
  /** `start` only: the element's computed position is neither absolute nor fixed, so the drag is a reorder. */
  flow?: boolean
  /**
   * `start` only: the element's parent is the slide (`box.top`), whose children are placed freely: a flow one is
   * frozen (`__bds_freeze`) on the drop instead of reordered.
   */
  top?: boolean
}

export function isDocDragReply(data: unknown): data is DocDragReply {
  return typeof data === 'object' && data !== null && (data as { __bds_drag?: unknown }).__bds_drag === 1
}

/** The slide's fixed height in document pixels. Content below this line is cropped, never scrolled. */
export const DOC_CANVAS_H = 1080
